import React from "react";
import autoBind from "react-autobind";
import { Button, ButtonToolbar } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import request from "superagent";
import { getImagesEdit } from "../../ai/aiAssistant";
import env from "../../constants/env";
import { refreshCredentials } from "../../utils/securityUtil";
import { getJwtToken } from "../../actions/user";
import { getLs, setLs } from "../../utils/utils";
import { isFutureEnabled } from "../../utils/featureFlags";
import ColorPicker from "./colorPicker";
import PopoutWindowPortal from "./popoutWindowPortal";

const MIN_FREE_CROP_SCALE = 0.35;
const MAX_CROP_ZOOM = 4;
const DEFAULT_CROP_ZOOM = 1;
const FREE_CROP_PRESET = "free";
const MAX_BLUR_PX = 24;
const DEFAULT_BLUR_FILL_PX = 20;
const DEFAULT_BLUR_FILL_SCALE = 1.12;
const DEFAULT_BLUR_FILL_BRIGHTNESS = 0.82;
const MIN_MARKUP_ITEM_SIZE = 8;
const MARKUP_COPY_PASTE_OFFSET_PX = 24;
const MAX_PADDING_SLIDER_PX = 240;
const MAX_PADDING_INPUT_PX = 4096;
const MAX_FINAL_OUTPUT_DIMENSION_PX = 8192;
const IMAGE_OUTPUT_FORMAT_OPTIONS = [
	{ value: "png", label: "PNG" },
	{ value: "jpg", label: "JPG" }
];
const MARKUP_ZOOM_OPTIONS = [25, 33, 50, 66, 75, 90, 100, 110, 125, 150, 200];
const MARKUP_BORDER_WIDTH_OPTIONS = Array.from({ length: 17 }, (_, index) => index);
const MARKUP_BORDER_RADIUS_OPTIONS = Array.from({ length: 65 }, (_, index) => index);
const MARKUP_BLUR_INTENSITY_OPTIONS = [4, 6, 8, 10, 12, 16, 20, 24];
const MARKUP_SPOTLIGHT_BLUR_OPTIONS = [0, 4, 6, 8, 10, 12, 16, 20, 24, 32, 40];
const MAX_MARKUP_SPOTLIGHT_BLUR_PX = 40;
const MARKUP_SPOTLIGHT_DEFAULTS = {
	backgroundColor: "rgba(0,0,0,1)",
	opacity: 0.55,
	blur: 0
};
const MARKUP_SHAPE_OPTIONS = [
	{ value: "rectangle", label: "Rectangle" },
	{ value: "arrow", label: "Arrow" },
	{ value: "ellipse", label: "Ellipse" },
	{ value: "diamond", label: "Diamond" },
	{ value: "line", label: "Line" }
];
const MARKUP_DRAWING_SHAPES = MARKUP_SHAPE_OPTIONS.map((option) => option.value);
const MARKUP_TEXT_FONT_FAMILY_OPTIONS = [
	"system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif",
	"\"SF Pro Text\", \"SF Pro Display\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif",
	"\"Helvetica Neue\", Helvetica, Arial, sans-serif",
	"Arial, Helvetica, sans-serif",
	"Helvetica, Arial, sans-serif",
	"Segoe UI, Arial, sans-serif",
	"Roboto, \"Helvetica Neue\", Arial, sans-serif",
	"Inter, \"Helvetica Neue\", Arial, sans-serif",
	"\"Avenir Next\", Avenir, \"Helvetica Neue\", Arial, sans-serif",
	"\"Proxima Nova\", \"Avenir Next\", \"Helvetica Neue\", Arial, sans-serif",
	"\"DIN Next\", \"Franklin Gothic Medium\", Arial Narrow, Arial, sans-serif",
	"\"Open Sans\", Arial, sans-serif",
	"\"Lato\", \"Helvetica Neue\", Arial, sans-serif",
	"\"Source Sans 3\", \"Segoe UI\", Arial, sans-serif",
	"\"Noto Sans\", Arial, sans-serif",
	"Tahoma, Geneva, sans-serif",
	"Verdana, Geneva, sans-serif",
	"Trebuchet MS, Helvetica, sans-serif",
	"Calibri, \"Segoe UI\", Arial, sans-serif",
	"Gill Sans, Gill Sans MT, Calibri, sans-serif",
	"Century Gothic, Futura, sans-serif",
	"Montserrat, \"Helvetica Neue\", Arial, sans-serif",
	"\"Arial Narrow\", Arial, sans-serif",
	"Times New Roman, Times, serif",
	"Georgia, serif",
	"Cambria, Georgia, serif",
	"Garamond, Baskerville, serif",
	"Baskerville, \"Times New Roman\", serif",
	"Palatino Linotype, Book Antiqua, Palatino, serif",
	"Courier New, Courier, monospace",
	"Menlo, Monaco, Consolas, \"Courier New\", monospace",
	"Consolas, \"Lucida Console\", Monaco, monospace",
	"Lucida Console, Monaco, monospace",
	"Impact, Haettenschweiler, Arial Narrow Bold, sans-serif"
];
const MARKUP_TEXT_FONT_SIZE_OPTIONS = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72, 96];
const MARKUP_TEXT_FONT_WEIGHT_OPTIONS = [
	{ value: "300", label: "Light" },
	{ value: "400", label: "Normal" },
	{ value: "500", label: "Medium" },
	{ value: "600", label: "Semibold" },
	{ value: "700", label: "Bold" },
	{ value: "800", label: "Heavy" }
];
const MARKUP_TEXT_FONT_STYLE_OPTIONS = [
	{ value: "normal", label: "Normal" },
	{ value: "italic", label: "Italic" },
	{ value: "oblique", label: "Oblique" }
];
const MARKUP_TEXT_ALIGN_OPTIONS = [
	{ value: "left", label: "Left" },
	{ value: "center", label: "Center" },
	{ value: "right", label: "Right" }
];
const MARKUP_TEXT_DEFAULT_VALUE = "Replacement Text";
const MARKUP_TEXT_DEFAULT_FONT_FAMILY = "system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif";
const MAX_MARKUP_TEXT_FONT_SIZE = 256;
const DEFAULT_IMAGE_PATCH_BG_COLOR = "rgba(255,255,255,1)";
const DEFAULT_IMAGE_PATCH_BG_TOLERANCE = 36;
const DEFAULT_IMAGE_PATCH_BG_FEATHER = 18;
const MAX_IMAGE_PATCH_BG_TOLERANCE = 255;
const MAX_IMAGE_PATCH_BG_FEATHER = 255;
const MARKUP_COLOR_PICK_TARGETS = {
	border: "border",
	fill: "fill",
	text: "text",
	spotlightBackground: "spotlightBackground"
};
const MARKUP_LAYOUT_MODE_KEY = "imageEditorMarkupLayoutMode";
const MARKUP_LEFT_PANEL_COLLAPSED_KEY = "imageEditorMarkupLeftPanelCollapsed";
const MARKUP_RIGHT_PANEL_COLLAPSED_KEY = "imageEditorMarkupRightPanelCollapsed";
const MARKUP_LAYOUT_MODE_SPLIT = "split";
const MARKUP_LAYOUT_MODE_STACKED_RIGHT_SPLIT = "stackedRightSplit";
const MARKUP_LAYOUT_MODE_STACKED_RIGHT_APPEND = "stackedRightAppend";
const AI_IMAGE_SIZES = [256, 512, 1024];
const CROP_PRESET_OPTIONS = [
	{ value: "16:9", label: "16:9", aspectRatio: 16 / 9 },
	{ value: "4:3", label: "4:3", aspectRatio: 4 / 3 },
	{ value: "1:1", label: "1:1", aspectRatio: 1 },
	{ value: "3:4", label: "3:4", aspectRatio: 3 / 4 },
	{ value: "9:16", label: "9:16", aspectRatio: 9 / 16 },
	{ value: FREE_CROP_PRESET, label: "Free", aspectRatio: null }
];
const FINAL_FRAME_ASPECT_RATIO_OPTIONS = [
	{ value: "custom", label: "Custom Size", aspectRatio: null },
	...CROP_PRESET_OPTIONS.filter((option) => option.aspectRatio)
];

function clampValue(value, min, max) {
	return Math.min(Math.max(value, min), max);
}

function normalizeImageOutputFormat(format = "", fallback = "png") {
	const normalized = `${format || ""}`.trim().toLowerCase();
	if (normalized === "jpg" || normalized === "jpeg") {
		return "jpg";
	}
	if (normalized === "png") {
		return "png";
	}
	return normalizeImageOutputFormat(fallback, "png");
}

function getImageOutputExtension(format = "png") {
	return normalizeImageOutputFormat(format) === "jpg" ? "jpg" : "png";
}

function getMimeTypeForImageOutputFormat(format = "png") {
	return normalizeImageOutputFormat(format) === "jpg" ? "image/jpeg" : "image/png";
}

function getImageOutputFormatFromFileName(fileName = "", fallback = "png") {
	const trimmed = `${fileName || ""}`.trim().toLowerCase();
	if (!trimmed) {
		return normalizeImageOutputFormat(fallback, "png");
	}

	if (/\.(jpe?g)$/i.test(trimmed)) {
		return "jpg";
	}
	if (/\.png$/i.test(trimmed)) {
		return "png";
	}

	return normalizeImageOutputFormat(fallback, "png");
}

function normalizeImageFileName(fileName = "", format = "png", fallbackBaseName = "image-edited") {
	const trimmed = `${fileName || ""}`.trim();
	const extension = getImageOutputExtension(format);
	if (!trimmed) {
		return `${fallbackBaseName}.${extension}`;
	}

	const withoutExtension = trimmed.replace(/\.[^/.]+$/, "");
	return `${withoutExtension || fallbackBaseName}.${extension}`;
}

function getFileNameFromValue(value = "") {
	const trimmed = `${value || ""}`.trim();
	if (!trimmed) {
		return "";
	}

	const withoutQuery = trimmed.split("?")[0].split("#")[0];
	return withoutQuery.split("/").pop() || withoutQuery;
}

function getFileNameStemFromValue(value = "") {
	const rawValue = `${value || ""}`.trim();
	if (!rawValue) {
		return "";
	}

	const withoutQuery = rawValue.split("?")[0].split("#")[0];
	const lastSegment = withoutQuery.split("/").pop() || withoutQuery;
	let decodedSegment = lastSegment;
	try {
		decodedSegment = decodeURIComponent(lastSegment);
	} catch (err) {
		decodedSegment = lastSegment;
	}

	return decodedSegment.replace(/\.[^/.]+$/, "");
}

function getCoverDrawRect(sourceWidth, sourceHeight, targetWidth, targetHeight, scale = 1) {
	const safeSourceWidth = Math.max(Number(sourceWidth) || 1, 1);
	const safeSourceHeight = Math.max(Number(sourceHeight) || 1, 1);
	const safeTargetWidth = Math.max(Number(targetWidth) || 1, 1);
	const safeTargetHeight = Math.max(Number(targetHeight) || 1, 1);
	const sourceAspect = safeSourceWidth / safeSourceHeight;
	const targetAspect = safeTargetWidth / safeTargetHeight;
	let width = safeTargetWidth;
	let height = safeTargetHeight;

	if (sourceAspect > targetAspect) {
		height = safeTargetHeight;
		width = height * sourceAspect;
	} else {
		width = safeTargetWidth;
		height = width / sourceAspect;
	}

	const safeScale = clampValue(Number(scale) || 1, 1, 2);
	width *= safeScale;
	height *= safeScale;

	return {
		x: (safeTargetWidth - width) / 2,
		y: (safeTargetHeight - height) / 2,
		width,
		height
	};
}

function getAspectRatioForValue(value = "") {
	const normalizedValue = `${value || ""}`.trim();
	const option = FINAL_FRAME_ASPECT_RATIO_OPTIONS.find((candidate) => candidate.value === normalizedValue);
	return option && option.aspectRatio ? option.aspectRatio : null;
}

function getAspectRatioFrameDimensions(contentWidth, contentHeight, aspectRatio) {
	const safeContentWidth = Math.max(parseInt(contentWidth, 10) || 1, 1);
	const safeContentHeight = Math.max(parseInt(contentHeight, 10) || 1, 1);
	const safeAspectRatio = Number(aspectRatio);
	if (!Number.isFinite(safeAspectRatio) || safeAspectRatio <= 0) {
		return {
			width: safeContentWidth,
			height: safeContentHeight
		};
	}

	const contentAspectRatio = safeContentWidth / safeContentHeight;
	if (contentAspectRatio > safeAspectRatio) {
		return {
			width: safeContentWidth,
			height: Math.max(Math.ceil(safeContentWidth / safeAspectRatio), safeContentHeight)
		};
	}

	return {
		width: Math.max(Math.ceil(safeContentHeight * safeAspectRatio), safeContentWidth),
		height: safeContentHeight
	};
}

function isCanvasSafeUrl(url = "") {
	const value = `${url || ""}`.trim();
	if (!value) {
		return false;
	}
	if (/^(blob:|data:)/i.test(value)) {
		return true;
	}
	try {
		return new URL(value, window.location.href).origin === window.location.origin;
	} catch (err) {
		return false;
	}
}

function normalizeBase64Value(base64Value = "") {
	return `${base64Value || ""}`.replace(/\s+/g, "").trim();
}

function extractDriveContentPayload(response = null) {
	let payload = response && response.body ? response.body : null;

	if (typeof payload === "string") {
		try {
			payload = JSON.parse(payload);
		} catch (err) {
			payload = {
				body: payload
			};
		}
	}

	if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
		payload = {};
	}

	if ((!payload.base64 && !payload.body && !payload.dataBase64 && !payload.data) && typeof (response && response.text) === "string" && response.text.trim()) {
		try {
			const parsedTextPayload = JSON.parse(response.text);
			if (parsedTextPayload && typeof parsedTextPayload === "object" && !Array.isArray(parsedTextPayload)) {
				payload = {
					...parsedTextPayload,
					...payload
				};
			} else if (!payload.body) {
				payload.body = response.text;
			}
		} catch (err) {
			if (!payload.body) {
				payload.body = response.text;
			}
		}
	}

	const rawBodyValue = payload?.body || payload?.data || "";
	const rawBase64Value = payload?.base64 || payload?.dataBase64 || payload?.base64Value || "";
	let contentType = payload?.contentType || payload?.content_type || (payload.headers && (payload.headers["content-type"] || payload.headers["Content-Type"])) || "";
	let base64Value = normalizeBase64Value(rawBase64Value);

	if (!base64Value && typeof rawBodyValue === "string") {
		const trimmedBody = rawBodyValue.trim();
		const dataUrlMatch = trimmedBody.match(/^data:([^;,]+)?;base64,(.+)$/i);
		if (dataUrlMatch) {
			contentType = contentType || dataUrlMatch[1] || "";
			base64Value = normalizeBase64Value(dataUrlMatch[2] || "");
		} else {
			base64Value = normalizeBase64Value(trimmedBody);
		}
	}

	return {
		base64Value,
		contentType
	};
}

function getBestAiSquareSize(maxDimension = 0) {
	const target = Math.max(Math.round(maxDimension || 0), 1);
	const found = AI_IMAGE_SIZES.find((size) => target <= size);
	return found || AI_IMAGE_SIZES[AI_IMAGE_SIZES.length - 1] || 1024;
}

function createMarkupGuid() {
	return `markup-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const MARKUP_COMPONENT_OPTIONS = [
	{ value: "highlight", label: "Highlight", idBase: "highlight", supportedShapes: MARKUP_DRAWING_SHAPES },
	{ value: "hotspot", label: "Hotspot", idBase: "hotspot", supportedShapes: MARKUP_DRAWING_SHAPES },
	{ value: "spotlight", label: "Spotlight", idBase: "spotlight", supportedShapes: MARKUP_DRAWING_SHAPES },
	{ value: "redactBlur", label: "Blur Redact", idBase: "blur", supportedShapes: ["rectangle"] },
	{ value: "redactBlack", label: "Blackout", idBase: "blackout", supportedShapes: ["rectangle"] },
	{ value: "imagePatch", label: "Image Patch", idBase: "patch", supportedShapes: ["rectangle"] },
	{ value: "text", label: "Text", idBase: "text", supportedShapes: ["rectangle"] }
];

function getMarkupComponentMeta(componentType = "highlight") {
	return MARKUP_COMPONENT_OPTIONS.find((option) => option.value === componentType) || MARKUP_COMPONENT_OPTIONS[0];
}

function isBlurMarkupComponent(componentType = "highlight") {
	return componentType === "redactBlur";
}

function isBlackRedactMarkupComponent(componentType = "highlight") {
	return componentType === "redactBlack";
}

function isTextMarkupComponent(componentType = "highlight") {
	return componentType === "text";
}

function isImagePatchMarkupComponent(componentType = "highlight") {
	return componentType === "imagePatch";
}

function isSpotlightMarkupComponent(componentType = "highlight") {
	return componentType === "spotlight";
}

function getMarkupComponentSupportedShapes(componentType = "highlight") {
	const meta = getMarkupComponentMeta(componentType);
	return Array.isArray(meta.supportedShapes) && meta.supportedShapes.length
		? meta.supportedShapes
		: ["rectangle"];
}

function normalizeMarkupShapeForComponent(componentType = "highlight", requestedShape = "rectangle") {
	const normalizedShape = `${requestedShape || "rectangle"}`.trim().toLowerCase();
	const supportedShapes = getMarkupComponentSupportedShapes(componentType);
	return supportedShapes.includes(normalizedShape) ? normalizedShape : (supportedShapes[0] || "rectangle");
}

function getMarkupShapeOptions(componentType = "highlight") {
	const supportedShapes = getMarkupComponentSupportedShapes(componentType);
	return MARKUP_SHAPE_OPTIONS.filter((option) => supportedShapes.includes(option.value));
}

function getMarkupItemTypeLabel(componentType = "highlight") {
	return getMarkupComponentMeta(componentType).label;
}

function markupShapeSupportsBorderRadius(shape = "rectangle") {
	return shape === "rectangle";
}

function markupShapeSupportsFill(shape = "rectangle") {
	return shape !== "line";
}

function getMarkupShapeLabel(shape = "rectangle") {
	const match = MARKUP_SHAPE_OPTIONS.find((option) => option.value === `${shape || "rectangle"}`.trim().toLowerCase());
	return match ? match.label : "Rectangle";
}

function normalizeMarkupSpotlightOpacity(value = MARKUP_SPOTLIGHT_DEFAULTS.opacity) {
	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) {
		return MARKUP_SPOTLIGHT_DEFAULTS.opacity;
	}

	return clampValue(numericValue > 1 ? numericValue / 100 : numericValue, 0, 1);
}

function normalizeMarkupSpotlightBlur(value = MARKUP_SPOTLIGHT_DEFAULTS.blur) {
	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) {
		return MARKUP_SPOTLIGHT_DEFAULTS.blur;
	}

	return clampValue(numericValue, 0, MAX_MARKUP_SPOTLIGHT_BLUR_PX);
}

function getMarkupSpotlightSettings(markupItem = {}) {
	const style = markupItem.style || {};
	const spotlight = markupItem.spotlight && typeof markupItem.spotlight === "object" ? markupItem.spotlight : {};
	const backgroundColor = `${style.spotlightBackgroundColor || spotlight.backgroundColor || MARKUP_SPOTLIGHT_DEFAULTS.backgroundColor}`.trim() || MARKUP_SPOTLIGHT_DEFAULTS.backgroundColor;
	const opacityValue = style.spotlightOpacity !== undefined ? style.spotlightOpacity : spotlight.opacity;
	const blurValue = style.spotlightBlurPx !== undefined ? style.spotlightBlurPx : spotlight.blur;

	return {
		backgroundColor,
		opacity: normalizeMarkupSpotlightOpacity(opacityValue !== undefined ? opacityValue : MARKUP_SPOTLIGHT_DEFAULTS.opacity),
		blur: normalizeMarkupSpotlightBlur(blurValue !== undefined ? blurValue : MARKUP_SPOTLIGHT_DEFAULTS.blur)
	};
}

function getMarkupSpotlightCutoutStrokeWidth(markupItem = {}) {
	const shape = `${markupItem.shape || "rectangle"}`.trim().toLowerCase();
	const authoredBorderWidth = Number(markupItem.style && markupItem.style.borderWidth) || 0;
	return shape === "line" ? Math.max(authoredBorderWidth, 18) : Math.max(authoredBorderWidth, 1);
}

function getMarkupSpotlightHitTargetStyle(markupItem = {}) {
	const shape = `${markupItem.shape || "rectangle"}`.trim().toLowerCase();
	const strokeWidth = getMarkupSpotlightCutoutStrokeWidth(markupItem);

	if (shape === "line") {
		return {
			fill: "none",
			stroke: "rgba(0,0,0,0.001)",
			strokeWidth
		};
	}

	return {
		fill: "rgba(0,0,0,0.001)",
		stroke: "transparent",
		strokeWidth
	};
}

function getMarkupSpotlightLayerSettings(items = []) {
	if (!Array.isArray(items) || !items.length) {
		return MARKUP_SPOTLIGHT_DEFAULTS;
	}

	return items.reduce((result, item) => {
		const setting = getMarkupSpotlightSettings(item);
		const itemOpacity = item && item.opacity !== undefined ? normalizeMarkupSpotlightOpacity(item.opacity) : 1;
		return {
			backgroundColor: setting.backgroundColor || result.backgroundColor,
			opacity: Math.max(result.opacity, normalizeMarkupSpotlightOpacity(setting.opacity) * itemOpacity),
			blur: Math.max(result.blur, normalizeMarkupSpotlightBlur(setting.blur))
		};
	}, {
		...getMarkupSpotlightSettings(items[0]),
		opacity: 0
	});
}

function sanitizeMarkupSvgId(value = "") {
	return `${value || "item"}`.replace(/[^A-Za-z0-9_-]/g, "-");
}

function normalizeMarkupLayoutMode(layoutMode = "") {
	if (layoutMode === MARKUP_LAYOUT_MODE_STACKED_RIGHT_APPEND) {
		return MARKUP_LAYOUT_MODE_STACKED_RIGHT_APPEND;
	}
	if (layoutMode === MARKUP_LAYOUT_MODE_STACKED_RIGHT_SPLIT || layoutMode === "stackedRight") {
		return MARKUP_LAYOUT_MODE_STACKED_RIGHT_SPLIT;
	}
	return MARKUP_LAYOUT_MODE_SPLIT;
}

function readStoredBoolean(key, fallback = false) {
	const value = `${getLs(key) || ""}`.trim().toLowerCase();
	if (value === "true") {
		return true;
	}
	if (value === "false") {
		return false;
	}
	return fallback;
}

function getMarkupItemDescriptor(markupItem = {}) {
	const shape = markupItem.shape || "rectangle";
	const type = markupItem.type || "highlight";
	const shapeLabel = getMarkupShapeLabel(shape).toLowerCase();
	if (isSpotlightMarkupComponent(type)) {
		return `Spotlight ${shapeLabel}`;
	}
	if (shape === "arrow") {
		return "Arrow";
	}
	if (isBlurMarkupComponent(type)) {
		return "Blur redact";
	}
	if (isBlackRedactMarkupComponent(type)) {
		return "Blackout";
	}
	if (isTextMarkupComponent(type)) {
		return "Text box";
	}
	if (isImagePatchMarkupComponent(type)) {
		if (markupItem && markupItem.sourceDataUrl && markupItem.sourceRect) {
			return "Image patch";
		}
		return markupItem && markupItem.sourceDataUrl ? "Pasted image" : "Copied image area";
	}
	return `${getMarkupItemTypeLabel(type)} ${shapeLabel}`;
}

function getMarkupDefaults(componentType = "highlight") {
	if (componentType === "text") {
		return {
			shape: "rectangle",
			borderColor: "rgba(17,24,39,0)",
			borderWidth: 0,
			borderRadius: 6,
			fillColor: "rgba(255,255,255,0)",
			fillEnabled: false,
			blurPx: 0,
			textValue: MARKUP_TEXT_DEFAULT_VALUE,
			textColor: "rgba(17,24,39,1)",
			fontFamily: MARKUP_TEXT_DEFAULT_FONT_FAMILY,
			fontSize: 28,
			fontWeight: "400",
			fontStyle: "normal",
			textAlign: "left"
		};
	}

	if (componentType === "hotspot") {
		return {
			shape: "rectangle",
			borderColor: "rgba(255,140,0,1)",
			borderWidth: 5,
			borderRadius: 10,
			fillColor: "rgba(255,140,0,0.2)",
			fillEnabled: false,
			blurPx: 12
		};
	}

	if (componentType === "spotlight") {
		return {
			shape: "rectangle",
			borderColor: "rgba(255,255,255,0.95)",
			borderWidth: 0,
			borderRadius: 0,
			fillColor: "transparent",
			fillEnabled: false,
			blurPx: 0,
			spotlightBackgroundColor: MARKUP_SPOTLIGHT_DEFAULTS.backgroundColor,
			spotlightOpacity: MARKUP_SPOTLIGHT_DEFAULTS.opacity,
			spotlightBlurPx: MARKUP_SPOTLIGHT_DEFAULTS.blur
		};
	}

	if (componentType === "redactBlur") {
		return {
			shape: "rectangle",
			borderColor: "rgba(17,24,39,0.95)",
			borderWidth: 0,
			borderRadius: 10,
			fillColor: "transparent",
			fillEnabled: false,
			blurPx: 12
		};
	}

	if (componentType === "redactBlack") {
		return {
			shape: "rectangle",
			borderColor: "rgba(0,0,0,1)",
			borderWidth: 0,
			borderRadius: 10,
			fillColor: "rgba(0,0,0,1)",
			fillEnabled: true,
			blurPx: 12
		};
	}

	if (componentType === "imagePatch") {
		return {
			shape: "rectangle",
			borderColor: "rgba(17,24,39,0)",
			borderWidth: 0,
			borderRadius: 0,
			fillColor: "transparent",
			fillEnabled: false,
			blurPx: 0
		};
	}

	return {
		shape: "rectangle",
		borderColor: "rgba(0,0,255,1)",
		borderWidth: 5,
		borderRadius: 0,
		fillColor: "rgba(0,0,255,0.2)",
		fillEnabled: false,
		blurPx: 12
	};
}

function colorResultToRgba(colorResult, fallback = "rgba(0,0,0,1)") {
	if (typeof colorResult === "string" && colorResult.trim()) {
		return colorResult;
	}

	const rgb = colorResult && colorResult.rgb ? colorResult.rgb : null;
	if (!rgb) {
		return fallback;
	}

	const alpha = rgb.a !== undefined ? rgb.a : 1;
	return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

function isTransparentMarkupColor(colorValue = "") {
	const value = `${colorValue || ""}`.trim().toLowerCase();
	return !value || value === "transparent" || value === "rgba(0,0,0,0)" || value === "rgba(0, 0, 0, 0)";
}

function normalizeMarkupImagePatchBgTolerance(value = DEFAULT_IMAGE_PATCH_BG_TOLERANCE) {
	return clampValue(Math.round(Number(value) || 0), 0, MAX_IMAGE_PATCH_BG_TOLERANCE);
}

function normalizeMarkupImagePatchBgFeather(value = DEFAULT_IMAGE_PATCH_BG_FEATHER) {
	return clampValue(Math.round(Number(value) || 0), 0, MAX_IMAGE_PATCH_BG_FEATHER);
}

function parseMarkupColorChannels(colorValue = "", fallback = [255, 255, 255, 255]) {
	const trimmedValue = `${colorValue || ""}`.trim();
	if (!trimmedValue) {
		return [...fallback];
	}

	const hexMatch = trimmedValue.match(/^#([0-9a-f]{3,8})$/i);
	if (hexMatch) {
		const hexValue = hexMatch[1];
		if (hexValue.length === 3 || hexValue.length === 4) {
			const expandedHex = hexValue.split("").map((value) => `${value}${value}`).join("");
			return parseMarkupColorChannels(`#${expandedHex}`, fallback);
		}
		if (hexValue.length === 6 || hexValue.length === 8) {
			return [
				parseInt(hexValue.slice(0, 2), 16),
				parseInt(hexValue.slice(2, 4), 16),
				parseInt(hexValue.slice(4, 6), 16),
				hexValue.length === 8 ? parseInt(hexValue.slice(6, 8), 16) : 255
			];
		}
	}

	const rgbMatch = trimmedValue.match(/^rgba?\((.+)\)$/i);
	if (rgbMatch) {
		const rawChannels = rgbMatch[1]
			.split(",")
			.map((value) => `${value || ""}`.trim())
			.filter((value) => value.length > 0);
		if (rawChannels.length >= 3) {
			const red = clampValue(Math.round(Number(rawChannels[0]) || 0), 0, 255);
			const green = clampValue(Math.round(Number(rawChannels[1]) || 0), 0, 255);
			const blue = clampValue(Math.round(Number(rawChannels[2]) || 0), 0, 255);
			const alpha = rawChannels.length >= 4
				? clampValue(Math.round((Number(rawChannels[3]) || 0) * 255), 0, 255)
				: 255;
			return [red, green, blue, alpha];
		}
	}

	return [...fallback];
}

function normalizeMarkupTextAlign(textAlign = "left") {
	switch (`${textAlign || ""}`.trim().toLowerCase()) {
	case "center":
		return "center";
	case "right":
		return "right";
	default:
		return "left";
	}
}

function normalizeMarkupFontStyle(fontStyle = "normal") {
	switch (`${fontStyle || ""}`.trim().toLowerCase()) {
	case "italic":
		return "italic";
	case "oblique":
		return "oblique";
	default:
		return "normal";
	}
}

function normalizeMarkupFontWeight(fontWeight = "400") {
	const value = `${fontWeight || ""}`.trim();
	if (["300", "400", "500", "600", "700", "800", "900", "normal", "bold"].includes(value)) {
		return value;
	}
	return "400";
}

function normalizeMarkupFontSize(fontSize = 28, fallback = 28) {
	return clampValue(parseInt(fontSize, 10) || fallback, 1, MAX_MARKUP_TEXT_FONT_SIZE);
}

function getMarkupTextValue(markupItem = {}) {
	const value = `${markupItem.text || ""}`;
	return value.length ? value : MARKUP_TEXT_DEFAULT_VALUE;
}

function getMarkupTextPreview(markupItem = {}, maxLength = 32) {
	const collapsed = getMarkupTextValue(markupItem).replace(/\s+/g, " ").trim();
	if (!collapsed) {
		return MARKUP_TEXT_DEFAULT_VALUE;
	}
	if (collapsed.length <= maxLength) {
		return collapsed;
	}
	return `${collapsed.slice(0, Math.max(maxLength - 1, 1))}…`;
}

function buildUniqueStringList(values = []) {
	const seen = new Set();
	const result = [];
	values.forEach((value) => {
		const normalized = `${value || ""}`.trim();
		if (!normalized || seen.has(normalized)) {
			return;
		}
		seen.add(normalized);
		result.push(normalized);
	});
	return result;
}

function getSuggestedMarkupFontFamilyName(rawFamilyName = "", fallbackName = "") {
	const trimmedFamilyName = `${rawFamilyName || ""}`.trim();
	if (trimmedFamilyName && trimmedFamilyName !== MARKUP_TEXT_DEFAULT_FONT_FAMILY) {
		return trimmedFamilyName;
	}

	const normalizedFallbackName = `${fallbackName || ""}`.trim();
	if (normalizedFallbackName) {
		try {
			const parsedUrl = new URL(normalizedFallbackName, typeof window !== "undefined" ? window.location.href : "https://example.com");
			const familyParam = parsedUrl.searchParams.get("family");
			if (familyParam) {
				const firstFamilyParam = familyParam.split(":")[0].split("|")[0];
				const decodedFamilyName = decodeURIComponent(firstFamilyParam).replace(/\+/g, " ").trim();
				if (decodedFamilyName) {
					return decodedFamilyName;
				}
			}
		} catch (err) {
			// Fall back to filename parsing below when the input is not a URL.
		}
	}

	const fallbackStem = getFileNameStemFromValue(fallbackName).replace(/[-_]+/g, " ").trim();
	if (fallbackStem) {
		return fallbackStem;
	}

	return trimmedFamilyName || "Custom Font";
}

function normalizeMarkupRotation(rotationValue = 0) {
	const numericRotation = Number(rotationValue);
	if (!Number.isFinite(numericRotation)) {
		return 0;
	}

	let normalizedRotation = numericRotation % 360;
	if (normalizedRotation > 180) {
		normalizedRotation -= 360;
	} else if (normalizedRotation < -180) {
		normalizedRotation += 360;
	}

	return normalizedRotation;
}

function rotatePointAroundCenter(pointX, pointY, centerX, centerY, rotationDegrees = 0) {
	const radians = (Number(rotationDegrees || 0) * Math.PI) / 180;
	const cos = Math.cos(radians);
	const sin = Math.sin(radians);
	const translatedX = pointX - centerX;
	const translatedY = pointY - centerY;

	return {
		x: centerX + (translatedX * cos) - (translatedY * sin),
		y: centerY + (translatedX * sin) + (translatedY * cos)
	};
}

export default class ImageEditorModal extends React.PureComponent {
	constructor(props) {
		super(props);
		autoBind(this);
		this.imageRef = React.createRef();
		this.canvasRef = React.createRef();
		this.cropViewportRef = React.createRef();
		this.markupSvgRef = React.createRef();
		this.dragState = null;
		this.markupPressAndHoldTimer = null;
		this.markupPressAndHoldCount = 0;
		this.markupKeyboardAdjustState = null;
		this.markupPointerDrag = null;
		this.markupPointerListenerWindow = null;
		this.markupAuxWindow = null;
		this.markupRotateModifierActive = false;
		this.suppressNextMarkupCanvasClick = false;
		this.openNonce = 0;
		this.previewObjectUrl = null;
		this.textMeasureCanvas = null;
		this.markupSourceCanvas = null;
		this.markupFontStylesheetRegistry = new Map();
		this.markupLoadedFontObjectUrls = [];
		this.state = this.getInitialState(props);
	}

	componentDidMount() {
		window.addEventListener("resize", this.handleWindowResize);
		window.addEventListener("blur", this.handleWindowBlur);
		window.addEventListener("keydown", this.handleWindowKeyDown);
		window.addEventListener("keyup", this.handleWindowKeyUp);
		window.addEventListener("paste", this.handleWindowPaste);
		this.syncMarkupAuxWindowListeners();
		if (this.props.open) {
			this.resetForOpen(this.props);
		}
	}

	componentDidUpdate(prevProps) {
		if (this.props.open && !prevProps.open) {
			this.resetForOpen(this.props);
			return;
		}

		if (!this.props.open && prevProps.open) {
			this.openNonce += 1;
			this.revokePreviewObjectUrl();
			this.stopMarkupKeyboardAdjustments({ resetRotateModifier: true });
		}

		if (
			this.props.open &&
			(prevProps.imageUrl !== this.props.imageUrl || prevProps.defaultImageName !== this.props.defaultImageName)
		) {
			this.resetForOpen(this.props);
			return;
		}

		if (this.props.open) {
			this.syncCropViewportSize();
		}

		this.syncMarkupAuxWindowListeners();
	}

	componentWillUnmount() {
		this.openNonce += 1;
		window.removeEventListener("resize", this.handleWindowResize);
		window.removeEventListener("blur", this.handleWindowBlur);
		window.removeEventListener("keydown", this.handleWindowKeyDown);
		window.removeEventListener("keyup", this.handleWindowKeyUp);
		window.removeEventListener("paste", this.handleWindowPaste);
		this.detachMarkupAuxWindowListeners();
		this.detachCropDragListeners();
		this.stopMarkupPressAndHold();
		this.stopMarkupKeyboardAdjustments({ resetRotateModifier: true });
		this.detachMarkupPointerListeners();
		this.revokePreviewObjectUrl();
		this.markupSourceCanvas = null;
		this.markupFontStylesheetRegistry.forEach((entry) => {
			if (entry && entry.link && entry.link.parentNode) {
				entry.link.parentNode.removeChild(entry.link);
			}
		});
		this.markupFontStylesheetRegistry.clear();
		this.markupLoadedFontObjectUrls.forEach((objectUrl) => {
			if (objectUrl && typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
				URL.revokeObjectURL(objectUrl);
			}
		});
		this.markupLoadedFontObjectUrls = [];
	}

	getMarkupOwnerWindow() {
		const markupNode = (this.markupSvgRef && this.markupSvgRef.current) || null;
		const ownerWindow = markupNode && markupNode.ownerDocument && markupNode.ownerDocument.defaultView
			? markupNode.ownerDocument.defaultView
			: null;
		return ownerWindow || (typeof window !== "undefined" ? window : null);
	}

	syncMarkupAuxWindowListeners() {
		if (typeof window === "undefined") {
			return;
		}

		const targetWindow = (this.props.open && this.state.showMarkupModal)
			? this.getMarkupOwnerWindow()
			: null;
		if (!targetWindow || targetWindow === window) {
			this.detachMarkupAuxWindowListeners();
			return;
		}

		if (this.markupAuxWindow === targetWindow) {
			return;
		}

		this.detachMarkupAuxWindowListeners();
		targetWindow.addEventListener("blur", this.handleWindowBlur);
		targetWindow.addEventListener("keydown", this.handleWindowKeyDown);
		targetWindow.addEventListener("keyup", this.handleWindowKeyUp);
		targetWindow.addEventListener("paste", this.handleWindowPaste);
		this.markupAuxWindow = targetWindow;
	}

	detachMarkupAuxWindowListeners() {
		if (!this.markupAuxWindow) {
			return;
		}

		try {
			this.markupAuxWindow.removeEventListener("blur", this.handleWindowBlur);
			this.markupAuxWindow.removeEventListener("keydown", this.handleWindowKeyDown);
			this.markupAuxWindow.removeEventListener("keyup", this.handleWindowKeyUp);
			this.markupAuxWindow.removeEventListener("paste", this.handleWindowPaste);
		} catch (err) {
			// Ignore listener cleanup failures for already-closed windows.
		}

		this.markupAuxWindow = null;
	}

	getInitialState(props = this.props) {
		const defaultOutputFormat = this.getDefaultOutputFormat(props);
		const defaultImageName = this.getDefaultImageName(props, defaultOutputFormat);
		const textDefaults = getMarkupDefaults("text");
		return {
			imageName: defaultImageName,
			customImageName: defaultImageName,
			outputFormat: defaultOutputFormat,
			customOutputFormat: defaultOutputFormat,
			autoLinkImage: props.defaultAutoLink !== false,
			overwriteImage: false,
			previewImageUrl: "",
			previewImageLoading: !!props.imageUrl,
			previewExportSafe: false,
			originalPreviewImageUrl: "",
			sourceReady: false,
			sourceImageWidth: 0,
			sourceImageHeight: 0,
			cropViewportWidth: 0,
			cropViewportHeight: 0,
			cropPreset: FREE_CROP_PRESET,
			cropZoom: DEFAULT_CROP_ZOOM,
			cropOffsetX: 0,
			cropOffsetY: 0,
			freeCropWidthPct: 1,
			freeCropHeightPct: 1,
			isDraggingCrop: false,
			saving: false,
			error: "",
			aiPrompt: "",
			aiEditing: false,
			aiError: "",
			hasAiEdits: false,
			blurPx: 0,
			blurFillBackground: false,
			blurFillPx: DEFAULT_BLUR_FILL_PX,
			paddingPx: 0,
			paddingMode: "uniform",
			paddingTopPx: 0,
			paddingRightPx: 0,
			paddingBottomPx: 0,
			paddingLeftPx: 0,
			padToTargetSize: false,
			padAspectRatio: "custom",
			padTargetWidth: "",
			padTargetHeight: "",
			showMarkupModal: false,
			markupSourceUrl: "",
			markupSourceImageSize: null,
			markupItems: [],
			selectedMarkupGuid: "",
			markupComponentType: "highlight",
			markupShape: "rectangle",
			markupBorderColor: getMarkupDefaults("highlight").borderColor,
			markupBorderWidth: getMarkupDefaults("highlight").borderWidth,
			markupBorderRadius: getMarkupDefaults("highlight").borderRadius,
			markupFillEnabled: false,
			markupFillColor: getMarkupDefaults("highlight").fillColor,
			markupBlurPx: getMarkupDefaults("highlight").blurPx,
			markupSpotlightBackgroundColor: MARKUP_SPOTLIGHT_DEFAULTS.backgroundColor,
			markupSpotlightOpacity: MARKUP_SPOTLIGHT_DEFAULTS.opacity,
			markupSpotlightBlurPx: MARKUP_SPOTLIGHT_DEFAULTS.blur,
			markupTextValue: textDefaults.textValue,
			markupTextColor: textDefaults.textColor,
			markupFontFamily: textDefaults.fontFamily,
				markupFontSize: textDefaults.fontSize,
				markupFontWeight: textDefaults.fontWeight,
				markupFontStyle: textDefaults.fontStyle,
				markupTextAlign: textDefaults.textAlign,
				markupCustomFontFamilies: [],
				markupFontSourceUrl: "",
				markupFontLoading: false,
			markupFontLoadMessage: "",
			markupFontLoadError: "",
			markupColorPickTarget: "",
			markupImagePatchBgColor: DEFAULT_IMAGE_PATCH_BG_COLOR,
			markupImagePatchBgTolerance: DEFAULT_IMAGE_PATCH_BG_TOLERANCE,
			markupImagePatchBgFeather: DEFAULT_IMAGE_PATCH_BG_FEATHER,
			markupImagePatchBgPickMode: false,
			markupImagePatchProcessing: false,
			markupImageCopyMode: false,
			markupClipboardItem: null,
			markupRotation: 0,
			markupLayoutMode: normalizeMarkupLayoutMode(getLs(MARKUP_LAYOUT_MODE_KEY) || MARKUP_LAYOUT_MODE_SPLIT),
			markupLeftPanelCollapsed: readStoredBoolean(MARKUP_LEFT_PANEL_COLLAPSED_KEY, false),
			markupRightPanelCollapsed: readStoredBoolean(MARKUP_RIGHT_PANEL_COLLAPSED_KEY, false),
			markupPreviewScale: 100,
			markupShowItemIds: true,
			markupCanvasPopoutOpen: false,
			markupMouseX: 0,
			markupMouseY: 0,
			markupPointerInCanvas: false,
			markupMouseDown: false,
			markupStartX: 0,
			markupStartY: 0,
			markupCurrentX: 0,
			markupCurrentY: 0,
			resizeEnabled: false,
			resizeWidth: "",
			resizeHeight: "",
			resizeLockAspect: true
		};
	}

	getDefaultOutputFormat(props = this.props) {
		return getImageOutputFormatFromFileName(
			props.defaultImageName ||
			props.originalImageName ||
			props.imageFileName ||
			props.imageUrl ||
			props.defaultBaseName ||
			"",
			"png"
		);
	}

	getOverwriteOutputFormat(props = this.props) {
		return getImageOutputFormatFromFileName(
			getFileNameFromValue(props.overwriteTargetPath) ||
			props.originalImageName ||
			props.imageFileName ||
			props.imageUrl ||
			props.defaultBaseName ||
			"",
			this.getDefaultOutputFormat(props)
		);
	}

	getDefaultImageName(props = this.props, outputFormat = this.getDefaultOutputFormat(props)) {
		if (props.defaultImageName) {
			return normalizeImageFileName(props.defaultImageName, outputFormat);
		}

		const fileStem =
			getFileNameStemFromValue(props.imageFileName) ||
			getFileNameStemFromValue(props.imageUrl) ||
			getFileNameStemFromValue(props.defaultBaseName) ||
			"image";

		const normalizedStem = /-edited$/i.test(fileStem) ? fileStem : `${fileStem}-edited`;
		return normalizeImageFileName(normalizedStem, outputFormat);
	}

	getOverwriteImageName(props = this.props, outputFormat = this.getOverwriteOutputFormat(props)) {
		const overwritePathName = getFileNameFromValue(props.overwriteTargetPath);
		if (overwritePathName) {
			return overwritePathName;
		}

		const originalImageName = `${props.originalImageName || ""}`.trim();
		if (originalImageName) {
			return originalImageName;
		}

		const fileName =
			getFileNameStemFromValue(props.imageFileName) ||
			getFileNameStemFromValue(props.imageUrl) ||
			getFileNameStemFromValue(props.defaultBaseName) ||
			"image";

		return normalizeImageFileName(fileName, outputFormat, "image");
	}

	resetForOpen(props = this.props) {
		this.openNonce += 1;
		this.revokePreviewObjectUrl();
		this.detachCropDragListeners();
		this.stopMarkupKeyboardAdjustments({ resetRotateModifier: true });
		this.setState(this.getInitialState(props), () => {
			this.prepareImageSource(props);
		});
	}

	revokePreviewObjectUrl() {
		if (this.previewObjectUrl && typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
			URL.revokeObjectURL(this.previewObjectUrl);
		}
		this.previewObjectUrl = null;
	}

	base64ToBlob(base64Value = "", contentType = "application/octet-stream") {
		const binaryString = window.atob(normalizeBase64Value(base64Value));
		const binaryLength = binaryString.length;
		const bytes = new Uint8Array(binaryLength);
		for (let i = 0; i < binaryLength; i += 1) {
			bytes[i] = binaryString.charCodeAt(i);
		}
		return new Blob([bytes], { type: contentType || "application/octet-stream" });
	}

	readBlobAsDataUrl(blob) {
		return new Promise((resolve, reject) => {
			if (!blob) {
				reject(new Error("The pasted image data was empty."));
				return;
			}

			const reader = new window.FileReader();
			reader.onload = () => {
				const result = typeof reader.result === "string" ? reader.result : "";
				if (!result) {
					reject(new Error("The pasted image could not be read."));
					return;
				}
				resolve(result);
			};
			reader.onerror = () => {
				reject(new Error("The pasted image could not be read."));
			};
			reader.readAsDataURL(blob);
		});
	}

	loadImageElement(sourceUrl = "", errorMessage = "The image could not be loaded.") {
		return new Promise((resolve, reject) => {
			if (!sourceUrl) {
				reject(new Error(errorMessage));
				return;
			}

			const nextImage = new window.Image();
			nextImage.onload = () => resolve(nextImage);
			nextImage.onerror = () => reject(new Error(errorMessage));
			nextImage.src = sourceUrl;
		});
	}

	async tryCreateDriveBlobPreviewUrl(objectGuid) {
		if (!objectGuid || !env?.apiGateway?.endpoints?.drive?.getObjectContent) {
			return "";
		}

		await refreshCredentials();
		const response = await request
			.get(`${env.apiGateway.baseUrl}${env.apiGateway.endpoints.drive.getObjectContent(objectGuid)}`)
			.set({ Authorization: getJwtToken(), "Content-Type": "application/json" });
		const { base64Value, contentType } = extractDriveContentPayload(response);
		if (!base64Value) {
			throw new Error("Image content response did not include base64 data.");
		}

		const blob = this.base64ToBlob(base64Value, contentType || "application/octet-stream");
		if (!blob.size) {
			throw new Error("Image content response was empty.");
		}

		if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
			return "";
		}

		return URL.createObjectURL(blob);
	}

	async tryCreateBlobPreviewUrl(imageUrl) {
		if (!imageUrl) {
			return "";
		}

		if (/^(blob:|data:)/i.test(imageUrl)) {
			return imageUrl;
		}

		const response = await fetch(imageUrl, {
			credentials: "include"
		});
		if (!response.ok) {
			throw new Error(`Unable to load image for editing (${response.status}).`);
		}

		const blob = await response.blob();
		if (!blob || !blob.size) {
			return "";
		}

		if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
			return "";
		}

		return URL.createObjectURL(blob);
	}

	async prepareImageSource(props = this.props) {
		const directUrl = props.imageUrl || "";
		const driveObjectGuid = props.driveObjectGuid || "";
		const nonce = this.openNonce;

		if (!directUrl && !driveObjectGuid) {
			this.setState({
				previewImageUrl: "",
				previewImageLoading: false,
				error: "No image URL was available for editing."
			});
			return;
		}

		this.setState({
			previewImageUrl: "",
			previewImageLoading: true,
			previewExportSafe: false,
			error: ""
		});

		try {
			const blobUrl = directUrl
				? await this.tryCreateBlobPreviewUrl(directUrl)
				: await this.tryCreateDriveBlobPreviewUrl(driveObjectGuid);
			if (nonce !== this.openNonce || !blobUrl) {
				return;
			}
			if (blobUrl !== directUrl) {
				this.previewObjectUrl = blobUrl;
			}
			this.setState({
				previewImageUrl: blobUrl,
				previewExportSafe: true
			});
		} catch (err) {
			console.warn("Unable to preload editable image from the primary blob path.", err);

			if (driveObjectGuid && directUrl) {
				try {
					const driveBlobUrl = await this.tryCreateDriveBlobPreviewUrl(driveObjectGuid);
					if (nonce !== this.openNonce || !driveBlobUrl) {
						return;
					}
					if (driveBlobUrl !== directUrl) {
						this.previewObjectUrl = driveBlobUrl;
					}
					this.setState({
						previewImageUrl: driveBlobUrl,
						previewImageLoading: false,
						previewExportSafe: true,
						error: ""
					});
					return;
				} catch (driveErr) {
					console.warn("Unable to preload editable image from Drive content as blob.", driveErr);
				}
			}

			if (nonce === this.openNonce) {
				const safeDirectUrl = isCanvasSafeUrl(directUrl);
				this.setState({
					previewImageUrl: directUrl,
					previewImageLoading: false,
					previewExportSafe: safeDirectUrl,
					error: directUrl
						? (safeDirectUrl
							? ""
							: "The image preview loaded, but the browser blocked export access to the source image. Save is disabled until a canvas-safe source is available.")
						: "The image preview could not be loaded for editing."
				});
			}
		}
	}

	getImageElement() {
		return this.imageRef && this.imageRef.current ? this.imageRef.current : null;
	}

	getCanvasElement() {
		return this.canvasRef && this.canvasRef.current ? this.canvasRef.current : null;
	}

	getCropViewportElement() {
		return this.cropViewportRef && this.cropViewportRef.current ? this.cropViewportRef.current : null;
	}

	handleWindowResize() {
		window.requestAnimationFrame(() => {
			this.syncCropViewportSize();
		});
	}

	isKeyboardInputTarget(target) {
		if (!target) {
			return false;
		}

		if (target.isContentEditable) {
			return true;
		}

		const tagName = `${target.tagName || ""}`.toLowerCase();
		return ["input", "textarea", "select", "option", "button"].includes(tagName);
	}

	getMarkupKeyboardAdjustDirection(key = "") {
		switch (`${key}`.toLowerCase()) {
			case "arrowleft":
				return { deltaX: -1, deltaY: 0 };
			case "arrowright":
				return { deltaX: 1, deltaY: 0 };
			case "arrowup":
				return { deltaX: 0, deltaY: -1 };
			case "arrowdown":
				return { deltaX: 0, deltaY: 1 };
			default:
				return null;
		}
	}

	stopMarkupKeyboardAdjustments(options = {}) {
		const { resetRotateModifier = false } = options;
		if (this.markupKeyboardAdjustState && this.markupKeyboardAdjustState.intervalId) {
			window.clearInterval(this.markupKeyboardAdjustState.intervalId);
		}
		this.markupKeyboardAdjustState = null;
		if (resetRotateModifier) {
			this.markupRotateModifierActive = false;
		}
	}

	runMarkupKeyboardAdjustment(deltaX = 0, deltaY = 0, amount = 1, options = {}) {
		const { rotate = false } = options;
		if (!this.props.open || !this.state.showMarkupModal || !this.getSelectedMarkupItem()) {
			this.stopMarkupKeyboardAdjustments();
			return;
		}

		if (rotate) {
			this.handleRotateSelectedMarkup(deltaX * amount);
			return;
		}

		this.handleNudgeSelectedMarkup(deltaX * amount, deltaY * amount);
	}

	startMarkupKeyboardAdjustment(key, deltaX = 0, deltaY = 0, options = {}) {
		const { rotate = false } = options;
		this.stopMarkupKeyboardAdjustments();
		this.runMarkupKeyboardAdjustment(deltaX, deltaY, 1, { rotate });

		const adjustState = {
			key,
			deltaX,
			deltaY,
			rotate,
			count: 0,
			intervalId: null
		};

		adjustState.intervalId = window.setInterval(() => {
			adjustState.count += 1;
			this.runMarkupKeyboardAdjustment(deltaX, deltaY, 1, { rotate });

			if (adjustState.count > 5) {
				window.clearInterval(adjustState.intervalId);
				adjustState.intervalId = window.setInterval(() => {
					adjustState.count += 1;
					this.runMarkupKeyboardAdjustment(deltaX, deltaY, adjustState.count > 50 ? 20 : 5, { rotate });
				}, 50);
			}
		}, 200);

		this.markupKeyboardAdjustState = adjustState;
	}

	handleWindowKeyDown(event) {
		if (!this.props.open || !this.state.showMarkupModal) {
			return;
		}

		if (this.isKeyboardInputTarget(event && event.target)) {
			return;
		}

		const key = `${event.key || ""}`.toLowerCase();
		if ((event.metaKey || event.ctrlKey) && !event.altKey) {
			if (key === "c") {
				if (this.state.selectedMarkupGuid) {
					event.preventDefault();
					event.stopPropagation();
					this.handleCopySelectedMarkup();
				}
				return;
			}
		}

		if (!this.state.selectedMarkupGuid) {
			return;
		}

		if (key === "r") {
			this.markupRotateModifierActive = true;
			if (this.markupKeyboardAdjustState && !this.markupKeyboardAdjustState.rotate) {
				this.stopMarkupKeyboardAdjustments();
			}
			return;
		}

		const direction = this.getMarkupKeyboardAdjustDirection(key);
		if (!direction) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		if (this.markupRotateModifierActive) {
			const rotationDelta = key === "arrowleft" || key === "arrowup" ? -1 : 1;
			if (
				this.markupKeyboardAdjustState
				&& this.markupKeyboardAdjustState.key === key
				&& this.markupKeyboardAdjustState.rotate
			) {
				return;
			}
			this.startMarkupKeyboardAdjustment(key, rotationDelta, 0, { rotate: true });
			return;
		}

		if (
			this.markupKeyboardAdjustState
			&& this.markupKeyboardAdjustState.key === key
			&& !this.markupKeyboardAdjustState.rotate
		) {
			return;
		}

		this.startMarkupKeyboardAdjustment(key, direction.deltaX, direction.deltaY);
	}

	handleWindowKeyUp(event) {
		const key = `${event.key || ""}`.toLowerCase();
		if (key === "r") {
			this.markupRotateModifierActive = false;
			if (this.markupKeyboardAdjustState && this.markupKeyboardAdjustState.rotate) {
				this.stopMarkupKeyboardAdjustments();
			}
			return;
		}

		if (!this.markupKeyboardAdjustState || key !== `${this.markupKeyboardAdjustState.key || ""}`.toLowerCase()) {
			return;
		}

		this.stopMarkupKeyboardAdjustments();
	}

	handleWindowBlur() {
		this.stopMarkupKeyboardAdjustments({ resetRotateModifier: true });
	}

	handleWindowPaste(event) {
		if (!this.props.open || !this.state.showMarkupModal) {
			return;
		}

		if (this.isKeyboardInputTarget(event && event.target)) {
			return;
		}

		const clipboardImageFile = this.getClipboardImageFileFromPasteEvent(event);
		if (clipboardImageFile) {
			event.preventDefault();
			event.stopPropagation();
			this.handlePasteExternalMarkupImageFile(clipboardImageFile);
			return;
		}

		if (this.state.markupClipboardItem) {
			event.preventDefault();
			event.stopPropagation();
			this.handlePasteMarkupClipboard();
		}
	}

	syncCropViewportSize() {
		const viewport = this.getCropViewportElement();
		if (!viewport) {
			return;
		}

		const computedStyle = window.getComputedStyle(viewport);
		const horizontalPadding =
			(parseFloat(computedStyle.paddingLeft) || 0) +
			(parseFloat(computedStyle.paddingRight) || 0);
		const verticalPadding =
			(parseFloat(computedStyle.paddingTop) || 0) +
			(parseFloat(computedStyle.paddingBottom) || 0);
		const nextWidth = Math.round(Math.max((viewport.clientWidth || 0) - horizontalPadding, 0));
		const nextHeight = Math.round(Math.max((viewport.clientHeight || 0) - verticalPadding, 0));
		if (!nextWidth || !nextHeight) {
			return;
		}

		if (
			nextWidth !== this.state.cropViewportWidth ||
			nextHeight !== this.state.cropViewportHeight
		) {
			this.setState({
				cropViewportWidth: nextWidth,
				cropViewportHeight: nextHeight
			});
		}
	}

	handleImageNameChange(event) {
		const imageName = event && event.target ? event.target.value : "";
		const explicitOutputFormat = /\.(png|jpe?g)$/i.test(imageName)
			? getImageOutputFormatFromFileName(imageName, this.state.outputFormat)
			: "";
		this.setState((prevState) => ({
			imageName,
			customImageName: imageName,
			outputFormat: explicitOutputFormat || prevState.outputFormat,
			customOutputFormat: explicitOutputFormat || prevState.customOutputFormat
		}));
	}

	handleToggleAutoLinkImage(event) {
		this.setState({
			autoLinkImage: !!(event && event.target ? event.target.checked : false)
		});
	}

	handleToggleOverwriteImage(event) {
		const overwriteImage = !!(event && event.target ? event.target.checked : false);
		const overwriteOutputFormat = this.getOverwriteOutputFormat();
		this.setState((prevState) => ({
			overwriteImage,
			outputFormat: overwriteImage ? overwriteOutputFormat : (prevState.customOutputFormat || this.getDefaultOutputFormat()),
			imageName: overwriteImage
				? this.getOverwriteImageName(this.props, overwriteOutputFormat)
				: normalizeImageFileName(
					prevState.customImageName || this.getDefaultImageName(this.props, prevState.customOutputFormat || this.getDefaultOutputFormat()),
					prevState.customOutputFormat || this.getDefaultOutputFormat()
				)
		}));
	}

	handleOutputFormatChange(event) {
		const nextOutputFormat = normalizeImageOutputFormat(event && event.target ? event.target.value : this.state.outputFormat, this.state.outputFormat);
		this.setState((prevState) => {
			const nextImageName = normalizeImageFileName(
				prevState.imageName || prevState.customImageName || this.getDefaultImageName(this.props, nextOutputFormat),
				nextOutputFormat
			);
			return {
				outputFormat: nextOutputFormat,
				customOutputFormat: nextOutputFormat,
				imageName: nextImageName,
				customImageName: nextImageName
			};
		});
	}

	handleImageLoaded() {
		const image = this.getImageElement();
		if (!image) {
			return;
		}

		const sourceImageWidth = image.naturalWidth || image.width || 0;
		const sourceImageHeight = image.naturalHeight || image.height || 0;

		this.setState((prevState) => ({
			sourceReady: !!(sourceImageWidth && sourceImageHeight),
			sourceImageWidth,
			sourceImageHeight,
			previewImageLoading: false,
			error: "",
			previewExportSafe: prevState.previewExportSafe || isCanvasSafeUrl(prevState.previewImageUrl),
			originalPreviewImageUrl: prevState.originalPreviewImageUrl || prevState.previewImageUrl
		}), () => {
			window.requestAnimationFrame(() => {
				this.syncCropViewportSize();
			});
		});
	}

	handleAiPromptChange(event) {
		this.setState({
			aiPrompt: event && event.target ? event.target.value : ""
		});
	}

	applyPreviewImageUrl(nextPreviewImageUrl, extraState = {}) {
		const textDefaults = getMarkupDefaults("text");
		const preservedMarkupCustomFontFamilies = Array.isArray(this.state.markupCustomFontFamilies)
			? this.state.markupCustomFontFamilies
			: [];
		const preservedMarkupFontSourceUrl = `${this.state.markupFontSourceUrl || ""}`;
		this.markupSourceCanvas = null;
		this.setState({
			previewImageUrl: nextPreviewImageUrl || "",
			previewImageLoading: !!nextPreviewImageUrl,
			previewExportSafe: isCanvasSafeUrl(nextPreviewImageUrl),
			sourceReady: false,
			sourceImageWidth: 0,
			sourceImageHeight: 0,
			cropPreset: FREE_CROP_PRESET,
			cropZoom: DEFAULT_CROP_ZOOM,
			cropOffsetX: 0,
			cropOffsetY: 0,
			freeCropWidthPct: 1,
			freeCropHeightPct: 1,
			blurPx: 0,
			blurFillBackground: false,
			blurFillPx: DEFAULT_BLUR_FILL_PX,
			paddingPx: 0,
			paddingMode: "uniform",
			paddingTopPx: 0,
			paddingRightPx: 0,
			paddingBottomPx: 0,
			paddingLeftPx: 0,
			padToTargetSize: false,
			padAspectRatio: "custom",
			padTargetWidth: "",
			padTargetHeight: "",
			showMarkupModal: false,
			markupSourceUrl: "",
			markupSourceImageSize: null,
			markupItems: [],
			selectedMarkupGuid: "",
			markupComponentType: "highlight",
			markupShape: "rectangle",
			markupBorderColor: getMarkupDefaults("highlight").borderColor,
			markupBorderWidth: getMarkupDefaults("highlight").borderWidth,
			markupBorderRadius: getMarkupDefaults("highlight").borderRadius,
			markupFillEnabled: false,
			markupFillColor: getMarkupDefaults("highlight").fillColor,
			markupBlurPx: getMarkupDefaults("highlight").blurPx,
			markupSpotlightBackgroundColor: MARKUP_SPOTLIGHT_DEFAULTS.backgroundColor,
			markupSpotlightOpacity: MARKUP_SPOTLIGHT_DEFAULTS.opacity,
			markupSpotlightBlurPx: MARKUP_SPOTLIGHT_DEFAULTS.blur,
			markupTextValue: textDefaults.textValue,
			markupTextColor: textDefaults.textColor,
			markupFontFamily: textDefaults.fontFamily,
			markupFontSize: textDefaults.fontSize,
			markupFontWeight: textDefaults.fontWeight,
			markupFontStyle: textDefaults.fontStyle,
			markupTextAlign: textDefaults.textAlign,
			markupCustomFontFamilies: preservedMarkupCustomFontFamilies,
			markupFontSourceUrl: preservedMarkupFontSourceUrl,
			markupFontLoading: false,
			markupFontLoadMessage: "",
			markupFontLoadError: "",
			markupColorPickTarget: "",
			markupImageCopyMode: false,
			markupClipboardItem: null,
			markupRotation: 0,
			markupShowItemIds: true,
			markupMouseDown: false,
			resizeEnabled: false,
			resizeWidth: "",
			resizeHeight: "",
			resizeLockAspect: true,
			error: "",
			...extraState
		});
	}

	async handleApplyAiEdit() {
		const aiPrompt = `${this.state.aiPrompt || ""}`.trim();
		if (!aiPrompt) {
			this.setState({
				aiError: "This capability is currently unavailable."
			});
			return;
		}
		if (!this.state.sourceReady || this.state.previewImageLoading) {
			this.setState({
				aiError: "Wait for the image preview to finish loading first."
			});
			return;
		}
		if (!this.state.previewExportSafe) {
			this.setState({
				aiError: "This image preview is not yet available for export-safe editing. Save is disabled until the canvas-safe source finishes loading."
			});
			return;
		}

		this.setState({
			aiEditing: true,
			aiError: ""
		});

		try {
			const aiPayload = this.createAiEditPayload();
			const blob = await this.canvasToBlob(aiPayload.imageCanvas);
			const maskBlob = aiPayload.maskCanvas ? await this.canvasToBlob(aiPayload.maskCanvas) : null;
			const constrainedPrompt = `Preserve the current composition, orientation, and framing as closely as possible. Do not zoom, crop, rotate, or recompose unless the request explicitly asks for it. Maintain the visible subject and current aspect presentation. Requested edit: ${aiPrompt}`;
			const result = await getImagesEdit({
				prompt: constrainedPrompt,
				imageBlob: blob,
				maskBlob,
				imageName: normalizeImageFileName(this.state.imageName || "image-edited.png", "png"),
				size: aiPayload.size
			});
			const normalizedDataUrl = await this.normalizeAiResultToEditorCanvas(result.dataUrl || result.url, aiPayload.restore);

			this.applyPreviewImageUrl(normalizedDataUrl, {
				aiEditing: false,
				aiError: "",
				hasAiEdits: true
			});
		} catch (err) {
			this.setState({
				aiEditing: false,
				aiError: err && err.message ? err.message : `${err}`
			});
		}
	}

	handleRevertAiEdit() {
		if (!this.state.originalPreviewImageUrl) {
			return;
		}

		this.applyPreviewImageUrl(this.state.originalPreviewImageUrl, {
			aiError: "",
			hasAiEdits: false
		});
	}

	handleImageError() {
		if (this.state.previewImageUrl && this.state.previewImageUrl !== this.props.imageUrl) {
			this.revokePreviewObjectUrl();
			this.setState({
				previewImageUrl: this.props.imageUrl || "",
				previewImageLoading: false,
				sourceReady: false,
				previewExportSafe: isCanvasSafeUrl(this.props.imageUrl || ""),
				error: isCanvasSafeUrl(this.props.imageUrl || "")
					? ""
					: "The image preview loaded, but the browser blocked export access to the source image. Save is disabled."
			});
			return;
		}

		this.setState({
			previewImageLoading: false,
			sourceReady: false,
			error: "The image preview could not be loaded."
		});
	}

	getAspectRatioForPreset(cropPreset = this.state.cropPreset) {
		const preset = CROP_PRESET_OPTIONS.find((option) => option.value === cropPreset);
		return preset ? preset.aspectRatio : null;
	}

	getCropMetrics(overrides = {}) {
		const {
			sourceImageWidth = this.state.sourceImageWidth,
			sourceImageHeight = this.state.sourceImageHeight,
			cropViewportWidth = this.state.cropViewportWidth,
			cropViewportHeight = this.state.cropViewportHeight,
			cropPreset = this.state.cropPreset,
			cropZoom = this.state.cropZoom,
			cropOffsetX = this.state.cropOffsetX,
			cropOffsetY = this.state.cropOffsetY,
			freeCropWidthPct = this.state.freeCropWidthPct,
			freeCropHeightPct = this.state.freeCropHeightPct
		} = overrides;

		if (!sourceImageWidth || !sourceImageHeight || !cropViewportWidth || !cropViewportHeight) {
			return null;
		}

		const aspectRatio = this.getAspectRatioForPreset(cropPreset);
		let viewportWidth = cropViewportWidth;
		let viewportHeight = cropViewportHeight;

		if (aspectRatio) {
			if (cropViewportWidth / cropViewportHeight > aspectRatio) {
				viewportHeight = cropViewportHeight;
				viewportWidth = viewportHeight * aspectRatio;
			} else {
				viewportWidth = cropViewportWidth;
				viewportHeight = viewportWidth / aspectRatio;
			}
		} else {
			const imageAspectRatio = sourceImageWidth / sourceImageHeight;
			let fitWidth = cropViewportWidth;
			let fitHeight = cropViewportHeight;
			if (cropViewportWidth / cropViewportHeight > imageAspectRatio) {
				fitHeight = cropViewportHeight;
				fitWidth = fitHeight * imageAspectRatio;
			} else {
				fitWidth = cropViewportWidth;
				fitHeight = fitWidth / imageAspectRatio;
			}

			viewportWidth = fitWidth * clampValue(freeCropWidthPct, MIN_FREE_CROP_SCALE, 1);
			viewportHeight = fitHeight * clampValue(freeCropHeightPct, MIN_FREE_CROP_SCALE, 1);
		}

		const zoom = clampValue(cropZoom, DEFAULT_CROP_ZOOM, MAX_CROP_ZOOM);
		const scale = Math.max(
			viewportWidth / sourceImageWidth,
			viewportHeight / sourceImageHeight
		) * zoom;
		const displayWidth = sourceImageWidth * scale;
		const displayHeight = sourceImageHeight * scale;
		const maxOffsetX = Math.max((displayWidth - viewportWidth) / 2, 0);
		const maxOffsetY = Math.max((displayHeight - viewportHeight) / 2, 0);
		const clampedOffsetX = clampValue(cropOffsetX, -maxOffsetX, maxOffsetX);
		const clampedOffsetY = clampValue(cropOffsetY, -maxOffsetY, maxOffsetY);
		const sourceWidth = clampValue(viewportWidth / scale, 1, sourceImageWidth);
		const sourceHeight = clampValue(viewportHeight / scale, 1, sourceImageHeight);

		return {
			viewportWidth,
			viewportHeight,
			displayWidth,
			displayHeight,
			scale,
			imageLeft: (viewportWidth - displayWidth) / 2 + clampedOffsetX,
			imageTop: (viewportHeight - displayHeight) / 2 + clampedOffsetY,
			clampedOffsetX,
			clampedOffsetY,
			sourceImageWidth,
			sourceImageHeight,
			sourceWidth,
			sourceHeight
		};
	}

	getCurrentCropSourceDimensions(metrics = this.getCropMetrics()) {
		if (!metrics) {
			return {
				width: 0,
				height: 0
			};
		}

		return {
			width: Math.max(1, Math.round(metrics.sourceWidth)),
			height: Math.max(1, Math.round(metrics.sourceHeight))
		};
	}

	handleChangeCropPreset(cropPreset) {
		this.setState({
			cropPreset,
			cropZoom: DEFAULT_CROP_ZOOM,
			cropOffsetX: 0,
			cropOffsetY: 0,
			freeCropWidthPct: 1,
			freeCropHeightPct: 1
		});
	}

	handleCropZoomChange(event) {
		const cropZoom = clampValue(parseFloat(event && event.target ? event.target.value : DEFAULT_CROP_ZOOM) || DEFAULT_CROP_ZOOM, DEFAULT_CROP_ZOOM, MAX_CROP_ZOOM);
		const nextMetrics = this.getCropMetrics({
			cropZoom
		});

		this.setState({
			cropZoom,
			cropOffsetX: nextMetrics ? nextMetrics.clampedOffsetX : 0,
			cropOffsetY: nextMetrics ? nextMetrics.clampedOffsetY : 0
		});
	}

	handleFreeCropWidthChange(event) {
		const freeCropWidthPct = clampValue(parseFloat(event && event.target ? event.target.value : 1) || 1, MIN_FREE_CROP_SCALE, 1);
		const nextMetrics = this.getCropMetrics({
			freeCropWidthPct
		});

		this.setState({
			freeCropWidthPct,
			cropOffsetX: nextMetrics ? nextMetrics.clampedOffsetX : 0,
			cropOffsetY: nextMetrics ? nextMetrics.clampedOffsetY : 0
		});
	}

	handleFreeCropHeightChange(event) {
		const freeCropHeightPct = clampValue(parseFloat(event && event.target ? event.target.value : 1) || 1, MIN_FREE_CROP_SCALE, 1);
		const nextMetrics = this.getCropMetrics({
			freeCropHeightPct
		});

		this.setState({
			freeCropHeightPct,
			cropOffsetX: nextMetrics ? nextMetrics.clampedOffsetX : 0,
			cropOffsetY: nextMetrics ? nextMetrics.clampedOffsetY : 0
		});
	}

	handleResetCrop() {
		this.setState({
			cropZoom: DEFAULT_CROP_ZOOM,
			cropOffsetX: 0,
			cropOffsetY: 0,
			freeCropWidthPct: 1,
			freeCropHeightPct: 1
		});
	}

	handleCropDragStart(event) {
		if (!this.state.sourceReady) {
			return;
		}
		if (event && typeof event.button === "number" && event.button !== 0) {
			return;
		}

		const metrics = this.getCropMetrics();
		if (!metrics) {
			return;
		}

		event.preventDefault();
		this.dragState = {
			startX: event.clientX,
			startY: event.clientY,
			originOffsetX: metrics.clampedOffsetX,
			originOffsetY: metrics.clampedOffsetY
		};

		window.addEventListener("mousemove", this.handleWindowMouseMove);
		window.addEventListener("mouseup", this.handleWindowMouseUp);
		this.setState({
			isDraggingCrop: true
		});
	}

	handleWindowMouseMove(event) {
		if (!this.dragState) {
			return;
		}

		const nextMetrics = this.getCropMetrics({
			cropOffsetX: this.dragState.originOffsetX + (event.clientX - this.dragState.startX),
			cropOffsetY: this.dragState.originOffsetY + (event.clientY - this.dragState.startY)
		});
		if (!nextMetrics) {
			return;
		}

		this.setState({
			cropOffsetX: nextMetrics.clampedOffsetX,
			cropOffsetY: nextMetrics.clampedOffsetY
		});
	}

	handleWindowMouseUp() {
		this.detachCropDragListeners();
	}

	detachCropDragListeners() {
		window.removeEventListener("mousemove", this.handleWindowMouseMove);
		window.removeEventListener("mouseup", this.handleWindowMouseUp);
		this.dragState = null;
		if (this.state.isDraggingCrop) {
			this.setState({
				isDraggingCrop: false
			});
		}
	}

	handleToggleResizeEnabled(event) {
		const resizeEnabled = !!(event && event.target ? event.target.checked : false);
		if (!resizeEnabled) {
			this.setState({
				resizeEnabled: false
			});
			return;
		}

		const cropDimensions = this.getCurrentCropSourceDimensions();
		this.setState((prevState) => ({
			resizeEnabled: true,
			resizeWidth: prevState.resizeWidth || `${cropDimensions.width || ""}`,
			resizeHeight: prevState.resizeHeight || `${cropDimensions.height || ""}`
		}));
	}

	handleToggleResizeLock(event) {
		this.setState({
			resizeLockAspect: !!(event && event.target ? event.target.checked : false)
		});
	}

	getResizeAspectRatio() {
		const cropDimensions = this.getCurrentCropSourceDimensions();
		if (!cropDimensions.width || !cropDimensions.height) {
			return 1;
		}
		return cropDimensions.width / cropDimensions.height;
	}

	handleResizeWidthChange(event) {
		const rawValue = event && event.target ? event.target.value : "";
		const numericValue = rawValue === "" ? "" : `${Math.max(parseInt(rawValue, 10) || 0, 1)}`;
		if (!this.state.resizeLockAspect || numericValue === "") {
			this.setState({
				resizeWidth: numericValue
			});
			return;
		}

		const aspectRatio = this.getResizeAspectRatio();
		const widthValue = parseInt(numericValue, 10) || 1;
		this.setState({
			resizeWidth: `${widthValue}`,
			resizeHeight: `${Math.max(Math.round(widthValue / aspectRatio), 1)}`
		});
	}

	handleResizeHeightChange(event) {
		const rawValue = event && event.target ? event.target.value : "";
		const numericValue = rawValue === "" ? "" : `${Math.max(parseInt(rawValue, 10) || 0, 1)}`;
		if (!this.state.resizeLockAspect || numericValue === "") {
			this.setState({
				resizeHeight: numericValue
			});
			return;
		}

		const aspectRatio = this.getResizeAspectRatio();
		const heightValue = parseInt(numericValue, 10) || 1;
		this.setState({
			resizeHeight: `${heightValue}`,
			resizeWidth: `${Math.max(Math.round(heightValue * aspectRatio), 1)}`
		});
	}

	handleBlurChange(event) {
		this.setState({
			blurPx: clampValue(parseFloat(event && event.target ? event.target.value : 0) || 0, 0, MAX_BLUR_PX)
		});
	}

	handleBlurFillBackgroundChange(event) {
		this.setState({
			blurFillBackground: !!(event && event.target ? event.target.checked : false)
		});
	}

	handleBlurFillPxChange(event) {
		const rawValue = event && event.target ? event.target.value : DEFAULT_BLUR_FILL_PX;
		const numericValue = parseFloat(rawValue);
		this.setState({
			blurFillPx: clampValue(Number.isFinite(numericValue) ? numericValue : DEFAULT_BLUR_FILL_PX, 0, MAX_BLUR_PX)
		});
	}

	handlePaddingChange(event) {
		this.setState({
			paddingPx: clampValue(parseInt(event && event.target ? event.target.value : 0, 10) || 0, 0, MAX_PADDING_INPUT_PX)
		});
	}

	handlePaddingModeChange(event) {
		const paddingMode = event && event.target ? event.target.value : "uniform";
		if (paddingMode !== "custom") {
			this.setState({
				paddingMode: "uniform"
			});
			return;
		}

		this.setState((prevState) => {
			const uniformPadding = clampValue(parseInt(prevState.paddingPx, 10) || 0, 0, MAX_PADDING_INPUT_PX);
			return {
				paddingMode: "custom",
				paddingTopPx: prevState.paddingTopPx || uniformPadding,
				paddingRightPx: prevState.paddingRightPx || uniformPadding,
				paddingBottomPx: prevState.paddingBottomPx || uniformPadding,
				paddingLeftPx: prevState.paddingLeftPx || uniformPadding
			};
		});
	}

	handleSidePaddingChange(side, event) {
		const propertyName = `padding${side}Px`;
		const rawValue = event && event.target ? event.target.value : 0;
		this.setState({
			[propertyName]: clampValue(parseInt(rawValue, 10) || 0, 0, MAX_PADDING_INPUT_PX)
		});
	}

	handleTogglePadToTargetSize(event) {
		const padToTargetSize = !!(event && event.target ? event.target.checked : false);
		if (!padToTargetSize) {
			this.setState({
				padToTargetSize: false
			});
			return;
		}

		const cropDimensions = this.getCurrentCropSourceDimensions();
		const outputContentDimensions = this.getOutputContentDimensions(cropDimensions);
		const currentFrameConfig = this.getOutputFrameConfig(outputContentDimensions);

		this.setState((prevState) => ({
			padToTargetSize: true,
			padTargetWidth: prevState.padTargetWidth || `${currentFrameConfig.width || outputContentDimensions.width || ""}`,
			padTargetHeight: prevState.padTargetHeight || `${currentFrameConfig.height || outputContentDimensions.height || ""}`
		}));
	}

	handlePadAspectRatioChange(event) {
		this.setState({
			padAspectRatio: event && event.target ? event.target.value : "custom"
		});
	}

	handlePadTargetWidthChange(event) {
		const rawValue = event && event.target ? event.target.value : "";
		const numericValue = rawValue === "" ? "" : `${Math.max(Math.min(parseInt(rawValue, 10) || 0, MAX_FINAL_OUTPUT_DIMENSION_PX), 1)}`;
		this.setState({
			padTargetWidth: numericValue
		});
	}

	handlePadTargetHeightChange(event) {
		const rawValue = event && event.target ? event.target.value : "";
		const numericValue = rawValue === "" ? "" : `${Math.max(Math.min(parseInt(rawValue, 10) || 0, MAX_FINAL_OUTPUT_DIMENSION_PX), 1)}`;
		this.setState({
			padTargetHeight: numericValue
		});
	}

	drawSourceCropCanvas() {
		const image = this.getImageElement();
		const canvas = this.getCanvasElement();
		const metrics = this.getCropMetrics();
		if (!image || !canvas || !metrics) {
			throw new Error("Image preview is not ready.");
		}
		if (!image.naturalWidth || !image.naturalHeight) {
			throw new Error("Image has not loaded enough data yet.");
		}

		const sourceX = clampValue((-metrics.imageLeft) / metrics.scale, 0, metrics.sourceImageWidth - 1);
		const sourceY = clampValue((-metrics.imageTop) / metrics.scale, 0, metrics.sourceImageHeight - 1);
		const sourceWidth = clampValue(metrics.viewportWidth / metrics.scale, 1, metrics.sourceImageWidth - sourceX);
		const sourceHeight = clampValue(metrics.viewportHeight / metrics.scale, 1, metrics.sourceImageHeight - sourceY);

		canvas.width = Math.max(1, Math.round(sourceWidth));
		canvas.height = Math.max(1, Math.round(sourceHeight));
		const context = canvas.getContext("2d");
		context.clearRect(0, 0, canvas.width, canvas.height);
		context.drawImage(
			image,
			sourceX,
			sourceY,
			sourceWidth,
			sourceHeight,
			0,
			0,
			canvas.width,
			canvas.height
		);
		return canvas;
	}

	getOutputContentDimensions(sourceCanvas) {
		const fallbackWidth = sourceCanvas ? sourceCanvas.width : 1;
		const fallbackHeight = sourceCanvas ? sourceCanvas.height : 1;

		if (!this.state.resizeEnabled) {
			return {
				width: fallbackWidth,
				height: fallbackHeight
			};
		}

		const widthValue = Math.max(parseInt(this.state.resizeWidth, 10) || fallbackWidth, 1);
		const heightValue = Math.max(parseInt(this.state.resizeHeight, 10) || fallbackHeight, 1);
		return {
			width: widthValue,
			height: heightValue
		};
	}

	getOutputPaddingValues() {
		if (this.state.paddingMode === "custom") {
			return {
				top: clampValue(parseInt(this.state.paddingTopPx, 10) || 0, 0, MAX_PADDING_INPUT_PX),
				right: clampValue(parseInt(this.state.paddingRightPx, 10) || 0, 0, MAX_PADDING_INPUT_PX),
				bottom: clampValue(parseInt(this.state.paddingBottomPx, 10) || 0, 0, MAX_PADDING_INPUT_PX),
				left: clampValue(parseInt(this.state.paddingLeftPx, 10) || 0, 0, MAX_PADDING_INPUT_PX),
				mode: "custom"
			};
		}

		const paddingPx = clampValue(parseInt(this.state.paddingPx, 10) || 0, 0, MAX_PADDING_INPUT_PX);
		return {
			top: paddingPx,
			right: paddingPx,
			bottom: paddingPx,
			left: paddingPx,
			mode: "uniform"
		};
	}

	getOutputFrameConfig(contentDimensions = {}) {
		const contentWidth = Math.max(parseInt(contentDimensions.width, 10) || 1, 1);
		const contentHeight = Math.max(parseInt(contentDimensions.height, 10) || 1, 1);

		if (this.state.padToTargetSize) {
			const aspectRatio = getAspectRatioForValue(this.state.padAspectRatio);
			if (aspectRatio) {
				const frameDimensions = getAspectRatioFrameDimensions(contentWidth, contentHeight, aspectRatio);
				return {
					width: Math.max(frameDimensions.width, 1),
					height: Math.max(frameDimensions.height, 1),
					offsetX: Math.max(Math.floor((frameDimensions.width - contentWidth) / 2), 0),
					offsetY: Math.max(Math.floor((frameDimensions.height - contentHeight) / 2), 0),
					aspectRatio: this.state.padAspectRatio,
					mode: "aspect"
				};
			}

			const frameWidth = Math.max(
				Math.min(parseInt(this.state.padTargetWidth, 10) || contentWidth, MAX_FINAL_OUTPUT_DIMENSION_PX),
				contentWidth
			);
			const frameHeight = Math.max(
				Math.min(parseInt(this.state.padTargetHeight, 10) || contentHeight, MAX_FINAL_OUTPUT_DIMENSION_PX),
				contentHeight
			);

			return {
				width: frameWidth,
				height: frameHeight,
				offsetX: Math.max(Math.floor((frameWidth - contentWidth) / 2), 0),
				offsetY: Math.max(Math.floor((frameHeight - contentHeight) / 2), 0),
				mode: "target"
			};
		}

		const padding = this.getOutputPaddingValues();
		return {
			width: Math.max(contentWidth + padding.left + padding.right, 1),
			height: Math.max(contentHeight + padding.top + padding.bottom, 1),
			offsetX: padding.left,
			offsetY: padding.top,
			padding,
			mode: padding.mode
		};
	}

	createOutputCanvas() {
		const sourceCanvas = this.drawSourceCropCanvas();
		const { width: contentWidth, height: contentHeight } = this.getOutputContentDimensions(sourceCanvas);
		const blurPx = clampValue(parseFloat(this.state.blurPx) || 0, 0, MAX_BLUR_PX);
		const rawBlurFillPx = parseFloat(this.state.blurFillPx);
		const blurFillPx = clampValue(Number.isFinite(rawBlurFillPx) ? rawBlurFillPx : DEFAULT_BLUR_FILL_PX, 0, MAX_BLUR_PX);
		const blurFillBackground = !!this.state.blurFillBackground;
		const outputFrameConfig = this.getOutputFrameConfig({ width: contentWidth, height: contentHeight });

		const outputCanvas = document.createElement("canvas");
		outputCanvas.width = Math.max(outputFrameConfig.width || contentWidth, 1);
		outputCanvas.height = Math.max(outputFrameConfig.height || contentHeight, 1);
		const outputContext = outputCanvas.getContext("2d");
		outputContext.clearRect(0, 0, outputCanvas.width, outputCanvas.height);
		if (blurFillBackground) {
			const coverRect = getCoverDrawRect(
				sourceCanvas.width,
				sourceCanvas.height,
				outputCanvas.width,
				outputCanvas.height,
				DEFAULT_BLUR_FILL_SCALE
			);
			outputContext.save();
			outputContext.filter = `blur(${blurFillPx}px) brightness(${DEFAULT_BLUR_FILL_BRIGHTNESS})`;
			outputContext.drawImage(sourceCanvas, coverRect.x, coverRect.y, coverRect.width, coverRect.height);
			outputContext.restore();
		}
		outputContext.filter = blurPx > 0 ? `blur(${blurPx}px)` : "none";
		outputContext.drawImage(
			sourceCanvas,
			outputFrameConfig.offsetX || 0,
			outputFrameConfig.offsetY || 0,
			contentWidth,
			contentHeight
		);
		outputContext.filter = "none";
		return outputCanvas;
	}

	createAiEditPayload() {
		const outputCanvas = this.createOutputCanvas();
		const originalWidth = outputCanvas.width;
		const originalHeight = outputCanvas.height;
		const aiSide = getBestAiSquareSize(Math.max(originalWidth, originalHeight));
		const scale = Math.min(aiSide / originalWidth, aiSide / originalHeight);
		const placedWidth = Math.max(1, Math.round(originalWidth * scale));
		const placedHeight = Math.max(1, Math.round(originalHeight * scale));
		const offsetX = Math.round((aiSide - placedWidth) / 2);
		const offsetY = Math.round((aiSide - placedHeight) / 2);

		const aiCanvas = document.createElement("canvas");
		aiCanvas.width = aiSide;
		aiCanvas.height = aiSide;
		const aiContext = aiCanvas.getContext("2d");
		aiContext.clearRect(0, 0, aiSide, aiSide);
		aiContext.drawImage(outputCanvas, 0, 0, originalWidth, originalHeight, offsetX, offsetY, placedWidth, placedHeight);

		const imageData = outputCanvas.getContext("2d").getImageData(0, 0, originalWidth, originalHeight).data;
		let hasTransparency = false;
		for (let i = 3; i < imageData.length; i += 4) {
			if (imageData[i] < 250) {
				hasTransparency = true;
				break;
			}
		}

		let maskCanvas = null;
		if (hasTransparency) {
			maskCanvas = document.createElement("canvas");
			maskCanvas.width = aiSide;
			maskCanvas.height = aiSide;
			const maskContext = maskCanvas.getContext("2d");
			maskContext.clearRect(0, 0, aiSide, aiSide);
			maskContext.fillStyle = "#ffffff";
			maskContext.fillRect(offsetX, offsetY, placedWidth, placedHeight);
			maskContext.globalCompositeOperation = "destination-in";
			maskContext.drawImage(outputCanvas, 0, 0, originalWidth, originalHeight, offsetX, offsetY, placedWidth, placedHeight);
			maskContext.globalCompositeOperation = "source-over";
		}

		return {
			imageCanvas: aiCanvas,
			maskCanvas,
			size: `${aiSide}x${aiSide}`,
			restore: {
				offsetX,
				offsetY,
				placedWidth,
				placedHeight,
				originalWidth,
				originalHeight
			}
		};
	}

	async normalizeAiResultToEditorCanvas(resultDataUrl, restore = {}) {
		const {
			offsetX = 0,
			offsetY = 0,
			placedWidth = 0,
			placedHeight = 0,
			originalWidth = 0,
			originalHeight = 0
		} = restore || {};

		if (!resultDataUrl || !originalWidth || !originalHeight || !placedWidth || !placedHeight) {
			return resultDataUrl;
		}

		const image = await new Promise((resolve, reject) => {
			const nextImage = new window.Image();
			nextImage.onload = () => resolve(nextImage);
			nextImage.onerror = () => reject(new Error("Edited image could not be loaded."));
			nextImage.src = resultDataUrl;
		});

		const normalizedCanvas = document.createElement("canvas");
		normalizedCanvas.width = originalWidth;
		normalizedCanvas.height = originalHeight;
		const normalizedContext = normalizedCanvas.getContext("2d");
		normalizedContext.clearRect(0, 0, originalWidth, originalHeight);
		normalizedContext.drawImage(
			image,
			offsetX,
			offsetY,
			placedWidth,
			placedHeight,
			0,
			0,
			originalWidth,
			originalHeight
		);

		return normalizedCanvas.toDataURL("image/png");
	}

	canvasToBlob(canvas, mimeType = "image/png", quality = 0.92) {
		return new Promise((resolve, reject) => {
			let outputCanvas = canvas;
			if (mimeType === "image/jpeg") {
				outputCanvas = document.createElement("canvas");
				outputCanvas.width = canvas.width;
				outputCanvas.height = canvas.height;
				const outputContext = outputCanvas.getContext("2d");
				outputContext.fillStyle = "#ffffff";
				outputContext.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
				outputContext.drawImage(canvas, 0, 0);
			}

			outputCanvas.toBlob((blob) => {
				if (blob) {
					resolve(blob);
				} else {
					reject(new Error("Unable to create edited image blob."));
				}
			}, mimeType, mimeType === "image/jpeg" ? quality : undefined);
		});
	}

	handleOpenMarkupModal() {
		if (!this.state.sourceReady || this.state.previewImageLoading) {
			this.setState({
				error: "Wait for the image preview to finish loading before using markup."
			});
			return;
		}
		if (!this.state.previewExportSafe) {
			this.setState({
				error: "This image preview is not yet canvas-safe, so markup is unavailable until the secure preview source finishes loading."
			});
			return;
		}

		try {
			const outputCanvas = this.createOutputCanvas();
			this.markupSourceCanvas = outputCanvas;
			const markupSourceUrl = outputCanvas.toDataURL("image/png");
			this.setState({
				showMarkupModal: true,
				markupSourceUrl,
				markupSourceImageSize: {
					width: outputCanvas.width,
					height: outputCanvas.height
				},
				markupItems: [],
				selectedMarkupGuid: "",
				markupMouseX: 0,
				markupMouseY: 0,
				markupPointerInCanvas: false,
				markupMouseDown: false,
				markupStartX: 0,
				markupStartY: 0,
				markupCurrentX: 0,
				markupCurrentY: 0,
				markupColorPickTarget: "",
				markupImagePatchBgColor: DEFAULT_IMAGE_PATCH_BG_COLOR,
				markupImagePatchBgTolerance: DEFAULT_IMAGE_PATCH_BG_TOLERANCE,
				markupImagePatchBgFeather: DEFAULT_IMAGE_PATCH_BG_FEATHER,
				markupImagePatchBgPickMode: false,
				markupImagePatchProcessing: false,
				markupImageCopyMode: false,
				markupClipboardItem: null,
				markupCanvasPopoutOpen: false,
				markupRotation: 0,
				error: ""
			});
		} catch (err) {
			this.setState({
				error: err && err.message ? err.message : `${err}`
			});
		}
	}

	handleCloseMarkupModal() {
		this.stopMarkupPressAndHold();
		this.stopMarkupKeyboardAdjustments({ resetRotateModifier: true });
		this.detachMarkupPointerListeners();
		this.markupSourceCanvas = null;
		this.setState({
			showMarkupModal: false,
			markupSourceUrl: "",
			markupSourceImageSize: null,
			markupItems: [],
			selectedMarkupGuid: "",
			markupMouseDown: false,
			markupPointerInCanvas: false,
			markupColorPickTarget: "",
			markupImagePatchBgColor: DEFAULT_IMAGE_PATCH_BG_COLOR,
			markupImagePatchBgTolerance: DEFAULT_IMAGE_PATCH_BG_TOLERANCE,
			markupImagePatchBgFeather: DEFAULT_IMAGE_PATCH_BG_FEATHER,
			markupImagePatchBgPickMode: false,
			markupImagePatchProcessing: false,
			markupImageCopyMode: false,
			markupClipboardItem: null,
			markupCanvasPopoutOpen: false,
			markupRotation: 0
		});
	}

	handleToggleMarkupCanvasPopout() {
		if (!this.state.showMarkupModal || !this.state.markupSourceUrl || !this.state.markupSourceImageSize) {
			return;
		}

		if (this.state.markupCanvasPopoutOpen) {
			this.handleMarkupCanvasPopoutClosed();
			return;
		}

		this.setState({
			markupCanvasPopoutOpen: true
		});
	}

	handleMarkupCanvasPopoutClosed() {
		if (!this.state.markupCanvasPopoutOpen) {
			return;
		}

		this.setState({
			markupCanvasPopoutOpen: false
		});
	}

	handleApplyMarkupComposite(resultDataUrl) {
		if (!resultDataUrl) {
			this.handleCloseMarkupModal();
			return;
		}

		this.stopMarkupPressAndHold();
		this.stopMarkupKeyboardAdjustments({ resetRotateModifier: true });
		this.detachMarkupPointerListeners();
		this.markupSourceCanvas = null;
		this.setState({
			showMarkupModal: false,
			markupSourceUrl: "",
			markupSourceImageSize: null,
			markupItems: [],
			selectedMarkupGuid: "",
			markupMouseDown: false,
			markupPointerInCanvas: false,
			markupColorPickTarget: "",
			markupImagePatchBgColor: DEFAULT_IMAGE_PATCH_BG_COLOR,
			markupImagePatchBgTolerance: DEFAULT_IMAGE_PATCH_BG_TOLERANCE,
			markupImagePatchBgFeather: DEFAULT_IMAGE_PATCH_BG_FEATHER,
			markupImagePatchBgPickMode: false,
			markupImagePatchProcessing: false,
			markupImageCopyMode: false,
			markupClipboardItem: null,
			markupCanvasPopoutOpen: false,
			markupRotation: 0
		}, () => {
			this.applyPreviewImageUrl(resultDataUrl, {
				error: ""
			});
		});
	}

	getMarkupPointerPosition(event) {
		const { markupSourceImageSize } = this.state;
		const svg = this.markupSvgRef.current;
		if (!svg || !markupSourceImageSize) {
			return null;
		}

		const rect = svg.getBoundingClientRect();
		if (!rect.width || !rect.height) {
			return null;
		}

		const x = clampValue(
			((event.clientX - rect.left) / rect.width) * markupSourceImageSize.width,
			0,
			markupSourceImageSize.width
		);
		const y = clampValue(
			((event.clientY - rect.top) / rect.height) * markupSourceImageSize.height,
			0,
			markupSourceImageSize.height
		);

		return {
			x: Math.round(x),
			y: Math.round(y)
		};
	}

	getSelectedMarkupItem() {
		return this.state.markupItems.find((item) => item.guid === this.state.selectedMarkupGuid) || null;
	}

	getSelectedMarkupRect() {
		const selectedMarkupItem = this.getSelectedMarkupItem();
		return selectedMarkupItem && selectedMarkupItem.coords ? selectedMarkupItem.coords : null;
	}

	constrainMarkupRect(nextRect = {}) {
		const { markupSourceImageSize } = this.state;
		const imageWidth = markupSourceImageSize && markupSourceImageSize.width ? markupSourceImageSize.width : 0;
		const imageHeight = markupSourceImageSize && markupSourceImageSize.height ? markupSourceImageSize.height : 0;
		const constrainedWidth = Math.max(Math.round(Number(nextRect.width) || 0), MIN_MARKUP_ITEM_SIZE);
		const constrainedHeight = Math.max(Math.round(Number(nextRect.height) || 0), MIN_MARKUP_ITEM_SIZE);

		if (!imageWidth || !imageHeight) {
			return {
				x: Math.round(Number(nextRect.x) || 0),
				y: Math.round(Number(nextRect.y) || 0),
				width: constrainedWidth,
				height: constrainedHeight
			};
		}

		const width = Math.min(constrainedWidth, imageWidth);
		const height = Math.min(constrainedHeight, imageHeight);
		const maxX = Math.max(imageWidth - width, 0);
		const maxY = Math.max(imageHeight - height, 0);

		return {
			x: clampValue(Math.round(Number(nextRect.x) || 0), 0, maxX),
			y: clampValue(Math.round(Number(nextRect.y) || 0), 0, maxY),
			width,
			height
		};
	}

	setSelectedMarkupRect(nextRect = {}) {
		const constrainedRect = this.constrainMarkupRect(nextRect);
		this.updateSelectedMarkupItem((item) => ({
			...item,
			coords: {
				...item.coords,
				...constrainedRect
			}
		}));
	}

	getMarkupImagePatchStoredSourceRect(markupItem = {}) {
		const rawRect = markupItem && markupItem.sourceRect ? markupItem.sourceRect : null;
		if (!rawRect) {
			return null;
		}

		const imageWidth = this.state.markupSourceImageSize && this.state.markupSourceImageSize.width
			? this.state.markupSourceImageSize.width
			: 0;
		const imageHeight = this.state.markupSourceImageSize && this.state.markupSourceImageSize.height
			? this.state.markupSourceImageSize.height
			: 0;
		let x = Math.max(Math.round(Number(rawRect.x) || 0), 0);
		let y = Math.max(Math.round(Number(rawRect.y) || 0), 0);
		let width = Math.max(Math.round(Number(rawRect.width) || 0), 1);
		let height = Math.max(Math.round(Number(rawRect.height) || 0), 1);

		if (imageWidth) {
			x = clampValue(x, 0, Math.max(imageWidth - 1, 0));
			width = Math.min(width, Math.max(imageWidth - x, 1));
		}
		if (imageHeight) {
			y = clampValue(y, 0, Math.max(imageHeight - 1, 0));
			height = Math.min(height, Math.max(imageHeight - y, 1));
		}

		return { x, y, width, height };
	}

	getMarkupImagePatchSourceRect(markupItem = {}) {
		const embeddedSourceUrl = this.getMarkupImagePatchEmbeddedSourceUrl(markupItem);
		if (embeddedSourceUrl) {
			return null;
		}
		return this.getMarkupImagePatchStoredSourceRect(markupItem) || (markupItem && markupItem.coords ? this.constrainMarkupRect(markupItem.coords) : null);
	}

	getMarkupImagePatchEmbeddedSourceUrl(markupItem = {}) {
		return `${(markupItem && markupItem.sourceDataUrl) || ""}`.trim();
	}

	getMarkupImagePatchEmbeddedSourceSize(markupItem = {}) {
		const rawSize = markupItem && markupItem.sourceImageSize ? markupItem.sourceImageSize : null;
		if (!rawSize) {
			return null;
		}

		const width = Math.max(Math.round(Number(rawSize.width) || 0), 1);
		const height = Math.max(Math.round(Number(rawSize.height) || 0), 1);
		if (!width || !height) {
			return null;
		}

		return { width, height };
	}

	getMarkupImagePatchOriginalEmbeddedSourceUrl(markupItem = {}) {
		return `${(markupItem && (markupItem.sourceOriginalDataUrl || markupItem.sourceDataUrl)) || ""}`.trim();
	}

	getMarkupImagePatchOriginalEmbeddedSourceSize(markupItem = {}) {
		return this.getMarkupImagePatchEmbeddedSourceSize({
			sourceImageSize: markupItem && (markupItem.sourceOriginalImageSize || markupItem.sourceImageSize)
				? (markupItem.sourceOriginalImageSize || markupItem.sourceImageSize)
				: null
		});
	}

	cloneMarkupItemData(markupItem = {}) {
		if (!markupItem) {
			return null;
		}

		return {
			...markupItem,
			coords: {
				...(markupItem.coords || {})
			},
			style: {
				...(markupItem.style || {})
			},
			sourceDataUrl: markupItem.sourceDataUrl || undefined,
			sourceImageSize: markupItem.sourceImageSize
				? {
					...markupItem.sourceImageSize
				}
				: undefined,
			sourceOriginalDataUrl: markupItem.sourceOriginalDataUrl || undefined,
			sourceOriginalImageSize: markupItem.sourceOriginalImageSize
				? {
					...markupItem.sourceOriginalImageSize
				}
				: undefined,
			sourceRect: markupItem.sourceRect
				? {
					...markupItem.sourceRect
				}
				: undefined
		};
	}

	setMarkupClipboardItem(markupItem = null, callback) {
		this.setState({
			markupClipboardItem: markupItem ? this.cloneMarkupItemData(markupItem) : null
		}, callback);
	}

	handleStartMarkupImageCopyMode() {
		this.setState({
			markupImageCopyMode: true,
			markupColorPickTarget: "",
			markupImagePatchBgPickMode: false,
			selectedMarkupGuid: "",
			markupMouseDown: false
		});
	}

	handleCancelMarkupImageCopyMode() {
		this.setState({
			markupImageCopyMode: false,
			markupMouseDown: false
		});
	}

	buildPastedMarkupItem(markupItem = null) {
		if (!markupItem || !markupItem.coords) {
			return null;
		}

		const selectedMarkupItem = this.getSelectedMarkupItem();
		const anchorCoords = selectedMarkupItem && selectedMarkupItem.coords
			? selectedMarkupItem.coords
			: markupItem.coords;
		const nextCoords = this.constrainMarkupRect({
			x: (Number(anchorCoords.x) || 0) + MARKUP_COPY_PASTE_OFFSET_PX,
			y: (Number(anchorCoords.y) || 0) + MARKUP_COPY_PASTE_OFFSET_PX,
			width: Number(markupItem.coords.width) || MIN_MARKUP_ITEM_SIZE,
			height: Number(markupItem.coords.height) || MIN_MARKUP_ITEM_SIZE
		});

		return {
			...this.cloneMarkupItemData(markupItem),
			guid: createMarkupGuid(),
			id: this.getNextMarkupId(markupItem.type || "highlight", markupItem.shape || "rectangle"),
			visible: true,
			coords: nextCoords,
			rotation: normalizeMarkupRotation(markupItem.rotation || 0)
		};
	}

	buildEmbeddedMarkupImagePatchItem(sourceDataUrl = "", sourceImageSize = {}) {
		const embeddedSourceUrl = `${sourceDataUrl || ""}`.trim();
		if (!embeddedSourceUrl) {
			return null;
		}

		const normalizedSourceSize = this.getMarkupImagePatchEmbeddedSourceSize({ sourceImageSize });
		if (!normalizedSourceSize) {
			return null;
		}

		const { markupSourceImageSize } = this.state;
		const sourceWidth = normalizedSourceSize.width;
		const sourceHeight = normalizedSourceSize.height;
		const maxWidth = markupSourceImageSize && markupSourceImageSize.width ? markupSourceImageSize.width : sourceWidth;
		const maxHeight = markupSourceImageSize && markupSourceImageSize.height ? markupSourceImageSize.height : sourceHeight;
		const fitScale = Math.min(
			1,
			maxWidth > 0 ? (maxWidth / sourceWidth) : 1,
			maxHeight > 0 ? (maxHeight / sourceHeight) : 1
		);
		const displayWidth = Math.max(Math.round(sourceWidth * fitScale), MIN_MARKUP_ITEM_SIZE);
		const displayHeight = Math.max(Math.round(sourceHeight * fitScale), MIN_MARKUP_ITEM_SIZE);
		const selectedMarkupItem = this.getSelectedMarkupItem();
		const anchorCoords = selectedMarkupItem && selectedMarkupItem.coords ? selectedMarkupItem.coords : null;
		const nextCoords = this.constrainMarkupRect({
			x: (Number(anchorCoords && anchorCoords.x) || 0) + MARKUP_COPY_PASTE_OFFSET_PX,
			y: (Number(anchorCoords && anchorCoords.y) || 0) + MARKUP_COPY_PASTE_OFFSET_PX,
			width: displayWidth,
			height: displayHeight
		});
		const defaults = getMarkupDefaults("imagePatch");

		return {
			guid: createMarkupGuid(),
			id: this.getNextMarkupId("imagePatch", defaults.shape),
			type: "imagePatch",
			shape: defaults.shape,
			visible: true,
			coords: nextCoords,
			sourceDataUrl: embeddedSourceUrl,
			sourceImageSize: normalizedSourceSize,
			sourceOriginalDataUrl: embeddedSourceUrl,
			sourceOriginalImageSize: normalizedSourceSize,
			style: {
				borderColor: defaults.borderColor,
				borderWidth: defaults.borderWidth,
				borderRadius: defaults.borderRadius,
				borderFillColor: defaults.fillEnabled ? defaults.fillColor : "transparent",
				blurPx: defaults.blurPx
			},
			rotation: 0
		};
	}

	getClipboardImageFileFromPasteEvent(event) {
		const clipboardData = event && event.clipboardData ? event.clipboardData : null;
		if (!clipboardData) {
			return null;
		}

		const clipboardItems = Array.from(clipboardData.items || []);
		const imageItem = clipboardItems.find((item) => item && item.kind === "file" && /^image\//i.test(`${item.type || ""}`));
		if (imageItem && typeof imageItem.getAsFile === "function") {
			const imageFile = imageItem.getAsFile();
			if (imageFile) {
				return imageFile;
			}
		}

		const clipboardFiles = Array.from(clipboardData.files || []);
		return clipboardFiles.find((file) => file && /^image\//i.test(`${file.type || ""}`)) || null;
	}

	async handlePasteExternalMarkupImageFile(imageFile) {
		const pasteNonce = this.openNonce;
		try {
			const sourceDataUrl = await this.readBlobAsDataUrl(imageFile);
			if (!sourceDataUrl || pasteNonce !== this.openNonce || !this.props.open || !this.state.showMarkupModal) {
				return;
			}

			const image = await this.loadImageElement(sourceDataUrl, "The pasted image could not be loaded.");
			if (pasteNonce !== this.openNonce || !this.props.open || !this.state.showMarkupModal) {
				return;
			}

			const pastedMarkupItem = this.buildEmbeddedMarkupImagePatchItem(sourceDataUrl, {
				width: image.naturalWidth || image.width || 1,
				height: image.naturalHeight || image.height || 1
			});
			if (!pastedMarkupItem) {
				return;
			}

			this.setState((prevState) => ({
				markupItems: [...prevState.markupItems, pastedMarkupItem],
				selectedMarkupGuid: pastedMarkupItem.guid,
				markupImageCopyMode: false,
				markupColorPickTarget: "",
				markupImagePatchBgPickMode: false,
				markupRotation: normalizeMarkupRotation(pastedMarkupItem.rotation || 0),
				error: ""
			}), () => {
				this.applyMarkupSelectionToControls(pastedMarkupItem);
				this.setMarkupClipboardItem(pastedMarkupItem);
			});
		} catch (err) {
			this.setState({
				error: err && err.message ? err.message : "The pasted image could not be processed."
			});
		}
	}

	getMarkupPointInItemLocalSpace(point = null, markupItem = null) {
		if (!point || !markupItem || !markupItem.coords) {
			return null;
		}

		const width = Number(markupItem.coords.width) || 0;
		const height = Number(markupItem.coords.height) || 0;
		if (!width || !height) {
			return null;
		}

		const centerX = (Number(markupItem.coords.x) || 0) + (width / 2);
		const centerY = (Number(markupItem.coords.y) || 0) + (height / 2);
		const unrotatedPoint = rotatePointAroundCenter(point.x, point.y, centerX, centerY, -normalizeMarkupRotation(markupItem.rotation || 0));
		const localX = unrotatedPoint.x - (Number(markupItem.coords.x) || 0);
		const localY = unrotatedPoint.y - (Number(markupItem.coords.y) || 0);
		if (localX < 0 || localY < 0 || localX > width || localY > height) {
			return null;
		}

		return {
			x: localX,
			y: localY
		};
	}

	async buildMarkupImagePatchSourceCanvas(markupItem = null, options = {}) {
		const { useOriginalSource = false } = options;
		if (!markupItem || !isImagePatchMarkupComponent(markupItem.type || "")) {
			return null;
		}

		const storedSourceRect = this.getMarkupImagePatchStoredSourceRect(markupItem);
		if (storedSourceRect && this.markupSourceCanvas) {
			const nextCanvas = document.createElement("canvas");
			nextCanvas.width = storedSourceRect.width;
			nextCanvas.height = storedSourceRect.height;
			const nextContext = nextCanvas.getContext("2d");
			nextContext.clearRect(0, 0, nextCanvas.width, nextCanvas.height);
			nextContext.drawImage(
				this.markupSourceCanvas,
				storedSourceRect.x,
				storedSourceRect.y,
				storedSourceRect.width,
				storedSourceRect.height,
				0,
				0,
				nextCanvas.width,
				nextCanvas.height
			);
			return nextCanvas;
		}

		const embeddedSourceUrl = useOriginalSource
			? this.getMarkupImagePatchOriginalEmbeddedSourceUrl(markupItem)
			: this.getMarkupImagePatchEmbeddedSourceUrl(markupItem);
		if (!embeddedSourceUrl) {
			return null;
		}

		const embeddedSourceSize = useOriginalSource
			? this.getMarkupImagePatchOriginalEmbeddedSourceSize(markupItem)
			: this.getMarkupImagePatchEmbeddedSourceSize(markupItem);
		const embeddedImage = await this.loadImageElement(embeddedSourceUrl, "The image patch source could not be loaded.");
		const nextCanvas = document.createElement("canvas");
		nextCanvas.width = embeddedSourceSize ? embeddedSourceSize.width : Math.max(embeddedImage.naturalWidth || embeddedImage.width || 1, 1);
		nextCanvas.height = embeddedSourceSize ? embeddedSourceSize.height : Math.max(embeddedImage.naturalHeight || embeddedImage.height || 1, 1);
		const nextContext = nextCanvas.getContext("2d");
		nextContext.clearRect(0, 0, nextCanvas.width, nextCanvas.height);
		nextContext.drawImage(embeddedImage, 0, 0, nextCanvas.width, nextCanvas.height);
		return nextCanvas;
	}

	async sampleMarkupImagePatchColorAtPoint(markupItem = null, point = null) {
		const localPoint = this.getMarkupPointInItemLocalSpace(point, markupItem);
		if (!localPoint) {
			return "";
		}

		const sourceCanvas = await this.buildMarkupImagePatchSourceCanvas(markupItem, { useOriginalSource: true });
		if (!sourceCanvas) {
			return "";
		}

		const sourceContext = sourceCanvas.getContext("2d");
		const sampleX = clampValue(
			Math.round((localPoint.x / Math.max(Number(markupItem.coords.width) || 1, 1)) * sourceCanvas.width),
			0,
			Math.max(sourceCanvas.width - 1, 0)
		);
		const sampleY = clampValue(
			Math.round((localPoint.y / Math.max(Number(markupItem.coords.height) || 1, 1)) * sourceCanvas.height),
			0,
			Math.max(sourceCanvas.height - 1, 0)
		);
		const imageData = sourceContext.getImageData(sampleX, sampleY, 1, 1).data;
		if (!imageData || imageData.length < 4) {
			return "";
		}

		const alpha = Number((imageData[3] / 255).toFixed(3));
		return `rgba(${imageData[0]},${imageData[1]},${imageData[2]},${alpha})`;
	}

	handleMarkupImagePatchBgColorChange(colorResult) {
		this.setState({
			markupImagePatchBgColor: colorResultToRgba(colorResult, DEFAULT_IMAGE_PATCH_BG_COLOR),
			markupImagePatchBgPickMode: false
		});
	}

	handleMarkupImagePatchBgToleranceChange(event) {
		const nextValue = event && event.target ? event.target.value : event;
		this.setState({
			markupImagePatchBgTolerance: normalizeMarkupImagePatchBgTolerance(nextValue)
		});
	}

	handleMarkupImagePatchBgFeatherChange(event) {
		const nextValue = event && event.target ? event.target.value : event;
		this.setState({
			markupImagePatchBgFeather: normalizeMarkupImagePatchBgFeather(nextValue)
		});
	}

	handleToggleMarkupImagePatchBgPickMode() {
		const selectedMarkupItem = this.getSelectedMarkupItem();
		if (!selectedMarkupItem || !isImagePatchMarkupComponent(selectedMarkupItem.type || "")) {
			return;
		}

		this.setState((prevState) => ({
			markupImagePatchBgPickMode: !prevState.markupImagePatchBgPickMode,
			markupColorPickTarget: "",
			markupImageCopyMode: false,
			error: ""
		}));
	}

	async handleMarkupImagePatchBgPickFromPoint(point = null) {
		const selectedMarkupItem = this.getSelectedMarkupItem();
		if (!selectedMarkupItem || !isImagePatchMarkupComponent(selectedMarkupItem.type || "")) {
			this.setState({
				markupImagePatchBgPickMode: false
			});
			return;
		}

		try {
			const sampledColor = await this.sampleMarkupImagePatchColorAtPoint(selectedMarkupItem, point);
			if (!sampledColor) {
				this.setState({
					error: "Click inside the selected image patch to sample a background color."
				});
				return;
			}

			this.setState({
				markupImagePatchBgColor: sampledColor,
				markupImagePatchBgPickMode: false,
				error: ""
			});
		} catch (err) {
			this.setState({
				markupImagePatchBgPickMode: false,
				error: err && err.message ? err.message : "The image patch background color could not be sampled."
			});
		}
	}

	async handleApplySelectedMarkupImagePatchBackgroundRemoval() {
		const selectedMarkupItem = this.getSelectedMarkupItem();
		if (!selectedMarkupItem || !isImagePatchMarkupComponent(selectedMarkupItem.type || "")) {
			return;
		}

		this.setState({
			markupImagePatchProcessing: true,
			markupImagePatchBgPickMode: false,
			markupColorPickTarget: "",
			markupImageCopyMode: false,
			error: ""
		});

		try {
			const sourceCanvas = await this.buildMarkupImagePatchSourceCanvas(selectedMarkupItem, { useOriginalSource: true });
			if (!sourceCanvas) {
				throw new Error("The image patch source could not be prepared.");
			}

			const sourceContext = sourceCanvas.getContext("2d");
			const sourceImageData = sourceContext.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
			const sourcePixels = sourceImageData.data;
			const [targetRed, targetGreen, targetBlue] = parseMarkupColorChannels(this.state.markupImagePatchBgColor, [255, 255, 255, 255]);
			const tolerance = normalizeMarkupImagePatchBgTolerance(this.state.markupImagePatchBgTolerance);
			const feather = normalizeMarkupImagePatchBgFeather(this.state.markupImagePatchBgFeather);

			for (let pixelIndex = 0; pixelIndex < sourcePixels.length; pixelIndex += 4) {
				const originalAlpha = sourcePixels[pixelIndex + 3];
				if (!originalAlpha) {
					continue;
				}

				const deltaRed = sourcePixels[pixelIndex] - targetRed;
				const deltaGreen = sourcePixels[pixelIndex + 1] - targetGreen;
				const deltaBlue = sourcePixels[pixelIndex + 2] - targetBlue;
				const distance = Math.sqrt((deltaRed * deltaRed) + (deltaGreen * deltaGreen) + (deltaBlue * deltaBlue));
				if (distance <= tolerance) {
					sourcePixels[pixelIndex + 3] = 0;
					continue;
				}
				if (feather > 0 && distance < (tolerance + feather)) {
					const alphaRatio = (distance - tolerance) / feather;
					sourcePixels[pixelIndex + 3] = clampValue(Math.round(originalAlpha * alphaRatio), 0, originalAlpha);
				}
			}

			sourceContext.putImageData(sourceImageData, 0, 0);
			const processedSourceDataUrl = sourceCanvas.toDataURL("image/png");
			const processedSourceSize = {
				width: sourceCanvas.width,
				height: sourceCanvas.height
			};
			let updatedMarkupItem = null;
			this.updateSelectedMarkupItem((item) => {
				const nextStyle = {
					...(item.style || {}),
					backgroundRemoveColor: this.state.markupImagePatchBgColor,
					backgroundRemoveTolerance: tolerance,
					backgroundRemoveFeather: feather
				};
				updatedMarkupItem = {
					...item,
					sourceDataUrl: processedSourceDataUrl,
					sourceImageSize: processedSourceSize,
					sourceOriginalDataUrl: item.sourceRect
						? item.sourceOriginalDataUrl
						: (this.getMarkupImagePatchOriginalEmbeddedSourceUrl(item) || processedSourceDataUrl),
					sourceOriginalImageSize: item.sourceRect
						? item.sourceOriginalImageSize
						: (this.getMarkupImagePatchOriginalEmbeddedSourceSize(item) || processedSourceSize),
					style: nextStyle
				};
				return updatedMarkupItem;
			}, () => {
				this.setState({
					markupImagePatchProcessing: false,
					error: ""
				});
				if (updatedMarkupItem) {
					this.applyMarkupSelectionToControls(updatedMarkupItem);
					this.setMarkupClipboardItem(updatedMarkupItem);
				}
			});
		} catch (err) {
			this.setState({
				markupImagePatchProcessing: false,
				error: err && err.message ? err.message : "The image patch background could not be removed."
			});
		}
	}

	handleResetSelectedMarkupImagePatchBackgroundRemoval() {
		const selectedMarkupItem = this.getSelectedMarkupItem();
		if (!selectedMarkupItem || !isImagePatchMarkupComponent(selectedMarkupItem.type || "")) {
			return;
		}

		let updatedMarkupItem = null;
		this.updateSelectedMarkupItem((item) => {
			const storedSourceRect = this.getMarkupImagePatchStoredSourceRect(item);
			const nextStyle = {
				...(item.style || {}),
				backgroundRemoveColor: DEFAULT_IMAGE_PATCH_BG_COLOR,
				backgroundRemoveTolerance: DEFAULT_IMAGE_PATCH_BG_TOLERANCE,
				backgroundRemoveFeather: DEFAULT_IMAGE_PATCH_BG_FEATHER
			};
			if (storedSourceRect) {
				updatedMarkupItem = {
					...item,
					sourceDataUrl: undefined,
					sourceImageSize: undefined,
					style: nextStyle
				};
				return updatedMarkupItem;
			}

			const originalSourceUrl = this.getMarkupImagePatchOriginalEmbeddedSourceUrl(item);
			const originalSourceSize = this.getMarkupImagePatchOriginalEmbeddedSourceSize(item);
			if (!originalSourceUrl || !originalSourceSize) {
				return item;
			}

			updatedMarkupItem = {
				...item,
				sourceDataUrl: originalSourceUrl,
				sourceImageSize: originalSourceSize,
				sourceOriginalDataUrl: originalSourceUrl,
				sourceOriginalImageSize: originalSourceSize,
				style: nextStyle
			};
			return updatedMarkupItem;
		}, () => {
			this.setState({
				markupImagePatchBgPickMode: false,
				markupImagePatchProcessing: false,
				error: ""
			});
			if (updatedMarkupItem) {
				this.applyMarkupSelectionToControls(updatedMarkupItem);
				this.setMarkupClipboardItem(updatedMarkupItem);
			}
		});
	}

	getResizedMarkupRectFromHandle(startRect = {}, handle = "se", point = null, rotation = 0) {
		if (!point) {
			return this.constrainMarkupRect(startRect);
		}

		const centerX = (startRect.x || 0) + ((startRect.width || 0) / 2);
		const centerY = (startRect.y || 0) + ((startRect.height || 0) / 2);
		const localPoint = rotatePointAroundCenter(point.x, point.y, centerX, centerY, -rotation);
		let left = Number(startRect.x) || 0;
		let top = Number(startRect.y) || 0;
		let right = left + (Number(startRect.width) || MIN_MARKUP_ITEM_SIZE);
		let bottom = top + (Number(startRect.height) || MIN_MARKUP_ITEM_SIZE);
		const { markupSourceImageSize } = this.state;
		const maxRight = markupSourceImageSize && markupSourceImageSize.width ? markupSourceImageSize.width : right;
		const maxBottom = markupSourceImageSize && markupSourceImageSize.height ? markupSourceImageSize.height : bottom;

		if (handle.indexOf("n") >= 0) {
			top = clampValue(localPoint.y, 0, bottom - MIN_MARKUP_ITEM_SIZE);
		}
		if (handle.indexOf("s") >= 0) {
			bottom = clampValue(localPoint.y, top + MIN_MARKUP_ITEM_SIZE, maxBottom);
		}
		if (handle.indexOf("w") >= 0) {
			left = clampValue(localPoint.x, 0, right - MIN_MARKUP_ITEM_SIZE);
		}
		if (handle.indexOf("e") >= 0) {
			right = clampValue(localPoint.x, left + MIN_MARKUP_ITEM_SIZE, maxRight);
		}

		return this.constrainMarkupRect({
			x: left,
			y: top,
			width: right - left,
			height: bottom - top
		});
	}

	getMarkupFillColor() {
		if (!this.state.markupFillEnabled) {
			return "transparent";
		}
		return this.state.markupFillColor || "transparent";
	}

	getMarkupFontFamilyOptions() {
		return buildUniqueStringList([
			...MARKUP_TEXT_FONT_FAMILY_OPTIONS,
			...(Array.isArray(this.state.markupCustomFontFamilies) ? this.state.markupCustomFontFamilies : [])
		]);
	}

	addMarkupFontFamilyOption(fontFamily = "") {
		const normalizedFontFamily = `${fontFamily || ""}`.trim();
		if (!normalizedFontFamily) {
			return;
		}

		this.setState((prevState) => ({
			markupCustomFontFamilies: buildUniqueStringList([
				...(Array.isArray(prevState.markupCustomFontFamilies) ? prevState.markupCustomFontFamilies : []),
				normalizedFontFamily
			])
		}));
	}

	getMarkupTextControlState() {
		const textDefaults = getMarkupDefaults("text");
		return {
			textValue: `${this.state.markupTextValue || textDefaults.textValue}`,
			textColor: this.state.markupTextColor || textDefaults.textColor,
			fontFamily: `${this.state.markupFontFamily || textDefaults.fontFamily}`.trim() || textDefaults.fontFamily,
			fontSize: normalizeMarkupFontSize(this.state.markupFontSize, textDefaults.fontSize),
			fontWeight: normalizeMarkupFontWeight(this.state.markupFontWeight || textDefaults.fontWeight),
			fontStyle: normalizeMarkupFontStyle(this.state.markupFontStyle || textDefaults.fontStyle),
			textAlign: normalizeMarkupTextAlign(this.state.markupTextAlign || textDefaults.textAlign)
		};
	}

	getMarkupTextStyle(markupItem = {}) {
		const textDefaults = getMarkupDefaults("text");
		const itemStyle = markupItem.style || {};
		return {
			textValue: getMarkupTextValue(markupItem),
			textColor: itemStyle.textColor || textDefaults.textColor,
			fontFamily: `${itemStyle.fontFamily || textDefaults.fontFamily}`.trim() || textDefaults.fontFamily,
			fontSize: normalizeMarkupFontSize(itemStyle.fontSize, textDefaults.fontSize),
			fontWeight: normalizeMarkupFontWeight(itemStyle.fontWeight || textDefaults.fontWeight),
			fontStyle: normalizeMarkupFontStyle(itemStyle.fontStyle || textDefaults.fontStyle),
			textAlign: normalizeMarkupTextAlign(itemStyle.textAlign || textDefaults.textAlign)
		};
	}

	getMarkupTextPadding(markupItem = {}) {
		const textStyle = this.getMarkupTextStyle(markupItem);
		return {
			x: Math.max(8, Math.round(textStyle.fontSize * 0.32)),
			y: Math.max(6, Math.round(textStyle.fontSize * 0.24))
		};
	}

	getMarkupTextInnerRect(markupItem = {}) {
		const padding = this.getMarkupTextPadding(markupItem);
		const coords = markupItem.coords || {};
		return {
			x: (coords.x || 0) + padding.x,
			y: (coords.y || 0) + padding.y,
			width: Math.max((coords.width || 0) - (padding.x * 2), 1),
			height: Math.max((coords.height || 0) - (padding.y * 2), 1)
		};
	}

	getTextMeasureContext() {
		if (typeof document === "undefined") {
			return null;
		}
		if (!this.textMeasureCanvas) {
			this.textMeasureCanvas = document.createElement("canvas");
		}
		return this.textMeasureCanvas.getContext("2d");
	}

	buildCanvasFontString(textStyle = {}) {
		const fontStyle = normalizeMarkupFontStyle(textStyle.fontStyle);
		const fontWeight = normalizeMarkupFontWeight(textStyle.fontWeight);
		const fontSize = normalizeMarkupFontSize(textStyle.fontSize);
		const fontFamily = `${textStyle.fontFamily || MARKUP_TEXT_DEFAULT_FONT_FAMILY}`.trim() || MARKUP_TEXT_DEFAULT_FONT_FAMILY;
		return `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`.replace(/\s+/g, " ").trim();
	}

	measureTextWidth(text = "", textStyle = {}) {
		const measureContext = this.getTextMeasureContext();
		if (!measureContext) {
			return `${text || ""}`.length * normalizeMarkupFontSize(textStyle.fontSize, 16) * 0.6;
		}
		measureContext.font = this.buildCanvasFontString(textStyle);
		return measureContext.measureText(`${text || ""}`).width;
	}

	breakMarkupTextToken(token = "", maxWidth = 1, textStyle = {}) {
		if (!token) {
			return [""];
		}
		const safeMaxWidth = Math.max(maxWidth || 1, 1);
		const segments = [];
		let currentSegment = "";
		for (const character of `${token}`) {
			const candidate = `${currentSegment}${character}`;
			if (currentSegment && this.measureTextWidth(candidate, textStyle) > safeMaxWidth) {
				segments.push(currentSegment);
				currentSegment = character;
			} else {
				currentSegment = candidate;
			}
		}
		if (currentSegment) {
			segments.push(currentSegment);
		}
		return segments.length ? segments : [token];
	}

	wrapMarkupTextParagraph(paragraph = "", maxWidth = 1, textStyle = {}) {
		const safeParagraph = `${paragraph || ""}`;
		const trimmedParagraph = safeParagraph.trim();
		if (!trimmedParagraph) {
			return [""];
		}

		const safeMaxWidth = Math.max(maxWidth || 1, 1);
		const tokens = trimmedParagraph.split(/\s+/).filter(Boolean);
		const lines = [];
		let currentLine = "";

		tokens.forEach((token) => {
			const candidate = currentLine ? `${currentLine} ${token}` : token;
			if (currentLine && this.measureTextWidth(candidate, textStyle) > safeMaxWidth) {
				lines.push(currentLine);
				currentLine = "";
			}

			if (this.measureTextWidth(token, textStyle) <= safeMaxWidth) {
				currentLine = currentLine ? `${currentLine} ${token}` : token;
				return;
			}

			const tokenSegments = this.breakMarkupTextToken(token, safeMaxWidth, textStyle);
			tokenSegments.forEach((segment, segmentIndex) => {
				if (!segment) {
					return;
				}
				if (!currentLine) {
					currentLine = segment;
					return;
				}
				const segmentedCandidate = `${currentLine}${segmentIndex === 0 ? " " : ""}${segment}`;
				if (this.measureTextWidth(segmentedCandidate, textStyle) <= safeMaxWidth) {
					currentLine = segmentedCandidate;
				} else {
					lines.push(currentLine);
					currentLine = segment;
				}
			});
		});

		if (currentLine) {
			lines.push(currentLine);
		}

		return lines.length ? lines : [trimmedParagraph];
	}

	getMarkupTextLayout(markupItem = {}) {
		const textStyle = this.getMarkupTextStyle(markupItem);
		const innerRect = this.getMarkupTextInnerRect(markupItem);
		const lineHeight = Math.max(Math.round(textStyle.fontSize * 1.2), textStyle.fontSize + 2);
		const paragraphs = getMarkupTextValue(markupItem).split(/\r?\n/);
		const wrappedLines = [];

		paragraphs.forEach((paragraph, paragraphIndex) => {
			const paragraphLines = this.wrapMarkupTextParagraph(paragraph, innerRect.width, textStyle);
			paragraphLines.forEach((line) => wrappedLines.push(line));
			if (paragraphIndex < paragraphs.length - 1) {
				wrappedLines.push("");
			}
		});

		const maxLines = Math.max(Math.floor(innerRect.height / lineHeight), 1);
		const lines = wrappedLines.slice(0, maxLines);
		const textAnchor = textStyle.textAlign === "center"
			? "middle"
			: (textStyle.textAlign === "right" ? "end" : "start");
		const textX = textStyle.textAlign === "center"
			? innerRect.x + (innerRect.width / 2)
			: (textStyle.textAlign === "right" ? innerRect.x + innerRect.width : innerRect.x);

		return {
			...textStyle,
			innerRect,
			lineHeight,
			lines,
			textAnchor,
			textX
		};
	}

	async ensureMarkupFontsReady(markupItems = []) {
		if (typeof document === "undefined" || !document.fonts || typeof document.fonts.load !== "function") {
			return;
		}

		const textStylesToLoad = buildUniqueStringList(
			(Array.isArray(markupItems) ? markupItems : [])
				.filter((item) => isTextMarkupComponent(item && item.type ? item.type : ""))
				.map((item) => this.buildCanvasFontString(this.getMarkupTextStyle(item)))
		);

		if (!textStylesToLoad.length) {
			return;
		}

		await Promise.race([
			Promise.all(textStylesToLoad.map((fontDescriptor) => document.fonts.load(fontDescriptor))),
			new Promise((resolve) => window.setTimeout(resolve, 2500))
		]);
	}

	updateSelectedTextMarkupItem(updater) {
		const selectedMarkupItem = this.getSelectedMarkupItem();
		if (!selectedMarkupItem || !isTextMarkupComponent(selectedMarkupItem.type || "highlight")) {
			return;
		}
		this.updateSelectedMarkupItem((item) => {
			if (!isTextMarkupComponent(item.type || "highlight")) {
				return item;
			}
			return updater(item);
		});
	}

	getMarkupColorPickTargetLabel(target = "", componentType = this.state.markupComponentType) {
		if (target === MARKUP_COLOR_PICK_TARGETS.spotlightBackground) {
			return "Spotlight Background";
		}
		if (target === MARKUP_COLOR_PICK_TARGETS.text) {
			return "Text Color";
		}
		if (target === MARKUP_COLOR_PICK_TARGETS.fill) {
			return isTextMarkupComponent(componentType) ? "Background Color" : "Fill Color";
		}
		return "Border Color";
	}

	handleMarkupColorPickToggle(target = "") {
		this.setState((prevState) => ({
			markupColorPickTarget: prevState.markupColorPickTarget === target ? "" : target,
			markupImagePatchBgPickMode: false,
			markupImageCopyMode: false
		}));
	}

	getMarkupSampleCanvasContext() {
		const canvas = this.markupSourceCanvas;
		if (!canvas || typeof canvas.getContext !== "function") {
			return null;
		}
		return canvas.getContext("2d");
	}

	sampleMarkupSourceColorAtPoint(point) {
		const sampleContext = this.getMarkupSampleCanvasContext();
		const canvas = this.markupSourceCanvas;
		if (!sampleContext || !canvas || !point) {
			return "";
		}

		const sampleX = clampValue(Math.round(point.x), 0, Math.max((canvas.width || 1) - 1, 0));
		const sampleY = clampValue(Math.round(point.y), 0, Math.max((canvas.height || 1) - 1, 0));
		const imageData = sampleContext.getImageData(sampleX, sampleY, 1, 1).data;
		if (!imageData || imageData.length < 4) {
			return "";
		}
		const alpha = Number((imageData[3] / 255).toFixed(3));
		return `rgba(${imageData[0]},${imageData[1]},${imageData[2]},${alpha})`;
	}

	applyMarkupSampledColor(target = "", colorValue = "") {
		if (!target || !colorValue) {
			this.setState({
				markupColorPickTarget: ""
			});
			return;
		}

		if (target === MARKUP_COLOR_PICK_TARGETS.fill) {
			this.setState({
				markupFillEnabled: true,
				markupFillColor: colorValue,
				markupColorPickTarget: ""
			}, () => {
				this.updateSelectedMarkupItem((item) => ({
					...item,
					style: {
						...item.style,
						borderFillColor: colorValue
					}
				}));
			});
			return;
		}

		if (target === MARKUP_COLOR_PICK_TARGETS.spotlightBackground) {
			this.setState({
				markupSpotlightBackgroundColor: colorValue,
				markupColorPickTarget: ""
			}, () => {
				this.updateSelectedMarkupItem((item) => ({
					...item,
					style: {
						...item.style,
						spotlightBackgroundColor: colorValue
					}
				}));
			});
			return;
		}

		this.setState({
			markupColorPickTarget: ""
		}, () => {
			if (target === MARKUP_COLOR_PICK_TARGETS.text) {
				this.handleMarkupTextColorChange(colorValue);
				return;
			}
			this.handleMarkupBorderColorChange(colorValue);
		});
	}

	handleMarkupColorPickFromPoint(point) {
		const target = this.state.markupColorPickTarget;
		if (!target || !point) {
			return;
		}
		const sampledColor = this.sampleMarkupSourceColorAtPoint(point);
		if (!sampledColor) {
			this.setState({
				markupColorPickTarget: ""
			});
			return;
		}
		this.applyMarkupSampledColor(target, sampledColor);
	}

	getNextMarkupId(componentType = "highlight", shape = "rectangle") {
		const normalizedShape = normalizeMarkupShapeForComponent(componentType, shape);
		const shapeBase = normalizedShape === "rectangle" ? getMarkupComponentMeta(componentType).idBase : normalizedShape;
		const sameTypeCount = this.state.markupItems.filter((item) => {
			if (normalizedShape !== "rectangle") {
				return item.shape === normalizedShape;
			}
			return item.type === componentType && (!item.shape || item.shape === "rectangle");
		}).length;

		return `${shapeBase}${sameTypeCount + 1}`;
	}

	createMarkupItemFromBounds(startX, startY, endX, endY, options = {}) {
		const x = Math.round(Math.min(startX, endX));
		const y = Math.round(Math.min(startY, endY));
		const width = Math.round(Math.abs(endX - startX));
		const height = Math.round(Math.abs(endY - startY));

		if (width < 2 || height < 2) {
			return null;
		}

		const componentType = options.componentType || this.state.markupComponentType;
		const defaults = getMarkupDefaults(componentType);
		const useDefaults = !!options.useDefaults;
		const requestedShape = useDefaults ? defaults.shape : this.state.markupShape;
		const markupShape = normalizeMarkupShapeForComponent(componentType, requestedShape);
		const fillAllowed = markupShapeSupportsFill(markupShape) && !isSpotlightMarkupComponent(componentType);
		const textControlState = this.getMarkupTextControlState();
		const sourceRect = isImagePatchMarkupComponent(componentType)
			? { x, y, width, height }
			: undefined;
		return {
			guid: createMarkupGuid(),
			id: this.getNextMarkupId(componentType, markupShape),
			type: componentType,
			text: isTextMarkupComponent(componentType) ? (textControlState.textValue || MARKUP_TEXT_DEFAULT_VALUE) : undefined,
			shape: markupShape,
			visible: true,
			coords: {
				x,
				y,
				width,
				height
			},
			sourceRect,
			style: {
				borderColor: useDefaults ? defaults.borderColor : this.state.markupBorderColor,
				borderWidth: useDefaults ? defaults.borderWidth : (Number(this.state.markupBorderWidth) || 0),
				borderRadius: markupShapeSupportsBorderRadius(markupShape) ? (useDefaults ? defaults.borderRadius : (Number(this.state.markupBorderRadius) || 0)) : 0,
				borderFillColor: fillAllowed
					? (useDefaults ? (defaults.fillEnabled ? defaults.fillColor : "transparent") : this.getMarkupFillColor())
					: "transparent",
				blurPx: useDefaults ? defaults.blurPx : clampValue(Number(this.state.markupBlurPx) || 0, 0, MAX_BLUR_PX),
				spotlightBackgroundColor: useDefaults ? (defaults.spotlightBackgroundColor || MARKUP_SPOTLIGHT_DEFAULTS.backgroundColor) : this.state.markupSpotlightBackgroundColor,
				spotlightOpacity: useDefaults ? normalizeMarkupSpotlightOpacity(defaults.spotlightOpacity) : normalizeMarkupSpotlightOpacity(this.state.markupSpotlightOpacity),
				spotlightBlurPx: useDefaults ? normalizeMarkupSpotlightBlur(defaults.spotlightBlurPx) : normalizeMarkupSpotlightBlur(this.state.markupSpotlightBlurPx),
				textColor: textControlState.textColor,
				fontFamily: textControlState.fontFamily,
				fontSize: textControlState.fontSize,
				fontWeight: textControlState.fontWeight,
				fontStyle: textControlState.fontStyle,
				textAlign: textControlState.textAlign
			},
			rotation: 0
		};
	}

	applyMarkupSelectionToControls(markupItem) {
		if (!markupItem) {
			return;
		}

		const fillColor = markupItem.style && markupItem.style.borderFillColor ? markupItem.style.borderFillColor : "transparent";
		const spotlightSettings = getMarkupSpotlightSettings(markupItem);
		const nextState = {
			selectedMarkupGuid: markupItem.guid,
			markupImageCopyMode: false,
			markupComponentType: markupItem.type || "highlight",
			markupShape: normalizeMarkupShapeForComponent(markupItem.type || "highlight", markupItem.shape || "rectangle"),
			markupBorderColor: (markupItem.style && markupItem.style.borderColor) || getMarkupDefaults(markupItem.type || "highlight").borderColor,
			markupBorderWidth: Number(markupItem.style && markupItem.style.borderWidth) || 0,
			markupBorderRadius: Number(markupItem.style && markupItem.style.borderRadius) || 0,
			markupFillEnabled: !isTransparentMarkupColor(fillColor),
			markupFillColor: !isTransparentMarkupColor(fillColor) ? fillColor : this.state.markupFillColor,
			markupBlurPx: clampValue(Number(markupItem.style && markupItem.style.blurPx) || getMarkupDefaults(markupItem.type || "highlight").blurPx || 0, 0, MAX_BLUR_PX),
			markupSpotlightBackgroundColor: spotlightSettings.backgroundColor,
			markupSpotlightOpacity: spotlightSettings.opacity,
			markupSpotlightBlurPx: spotlightSettings.blur,
			markupRotation: normalizeMarkupRotation(markupItem.rotation || 0)
		};
		if (isTextMarkupComponent(markupItem.type || "highlight")) {
			const textStyle = this.getMarkupTextStyle(markupItem);
			nextState.markupTextValue = textStyle.textValue;
			nextState.markupTextColor = textStyle.textColor;
			nextState.markupFontFamily = textStyle.fontFamily;
			nextState.markupFontSize = textStyle.fontSize;
			nextState.markupFontWeight = textStyle.fontWeight;
			nextState.markupFontStyle = textStyle.fontStyle;
			nextState.markupTextAlign = textStyle.textAlign;
		}
		if (isImagePatchMarkupComponent(markupItem.type || "highlight")) {
			nextState.markupImagePatchBgColor = (markupItem.style && markupItem.style.backgroundRemoveColor) || DEFAULT_IMAGE_PATCH_BG_COLOR;
			nextState.markupImagePatchBgTolerance = normalizeMarkupImagePatchBgTolerance(markupItem.style && markupItem.style.backgroundRemoveTolerance);
			nextState.markupImagePatchBgFeather = normalizeMarkupImagePatchBgFeather(markupItem.style && markupItem.style.backgroundRemoveFeather);
		}
		nextState.markupImagePatchBgPickMode = false;
		this.setState(nextState);
	}

	updateSelectedMarkupItem(updater, callback) {
		const { selectedMarkupGuid, markupItems } = this.state;
		if (!selectedMarkupGuid) {
			return;
		}

		const nextItems = markupItems.map((item) => {
			if (item.guid !== selectedMarkupGuid) {
				return item;
			}
			return updater(item);
		});

		this.setState({
			markupItems: nextItems
		}, callback);
	}

	toggleMarkupVisibility(guid) {
		this.setState((prevState) => ({
			markupItems: prevState.markupItems.map((item) => {
				if (item.guid !== guid) {
					return item;
				}

				return {
					...item,
					visible: item.visible !== false ? false : true
				};
			})
		}));
	}

	handleMarkupComponentTypeChange(event) {
		const markupComponentType = event && event.target ? event.target.value : "highlight";
		const defaults = getMarkupDefaults(markupComponentType);
		const selectedMarkupItem = this.getSelectedMarkupItem();
		const textControlState = this.getMarkupTextControlState();
		const requestedShape = selectedMarkupItem
			? (selectedMarkupItem.shape || this.state.markupShape || defaults.shape)
			: (this.state.markupShape || defaults.shape);
		const markupShape = normalizeMarkupShapeForComponent(markupComponentType, requestedShape);
		this.setState({
			markupComponentType,
			markupShape,
			markupImageCopyMode: false,
			markupBorderColor: defaults.borderColor,
			markupBorderWidth: defaults.borderWidth,
			markupBorderRadius: defaults.borderRadius,
			markupFillEnabled: !!defaults.fillEnabled,
			markupFillColor: defaults.fillColor,
			markupBlurPx: defaults.blurPx,
			markupSpotlightBackgroundColor: defaults.spotlightBackgroundColor || this.state.markupSpotlightBackgroundColor,
			markupSpotlightOpacity: normalizeMarkupSpotlightOpacity(defaults.spotlightOpacity !== undefined ? defaults.spotlightOpacity : this.state.markupSpotlightOpacity),
			markupSpotlightBlurPx: normalizeMarkupSpotlightBlur(defaults.spotlightBlurPx !== undefined ? defaults.spotlightBlurPx : this.state.markupSpotlightBlurPx)
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				type: markupComponentType,
				text: isTextMarkupComponent(markupComponentType)
					? (item.text || textControlState.textValue || MARKUP_TEXT_DEFAULT_VALUE)
					: item.text,
				sourceRect: isImagePatchMarkupComponent(markupComponentType)
					? this.getMarkupImagePatchSourceRect(item)
					: item.sourceRect,
				shape: normalizeMarkupShapeForComponent(markupComponentType, item.shape || markupShape),
				style: {
					...item.style,
					borderColor: defaults.borderColor,
					borderWidth: defaults.borderWidth,
					borderRadius: markupShapeSupportsBorderRadius(markupShape) ? defaults.borderRadius : 0,
					borderFillColor: defaults.fillEnabled && markupShapeSupportsFill(markupShape) && !isSpotlightMarkupComponent(markupComponentType) ? defaults.fillColor : "transparent",
					blurPx: defaults.blurPx,
					spotlightBackgroundColor: defaults.spotlightBackgroundColor || item.style.spotlightBackgroundColor || MARKUP_SPOTLIGHT_DEFAULTS.backgroundColor,
					spotlightOpacity: normalizeMarkupSpotlightOpacity(defaults.spotlightOpacity !== undefined ? defaults.spotlightOpacity : item.style.spotlightOpacity),
					spotlightBlurPx: normalizeMarkupSpotlightBlur(defaults.spotlightBlurPx !== undefined ? defaults.spotlightBlurPx : item.style.spotlightBlurPx),
					textColor: textControlState.textColor,
					fontFamily: textControlState.fontFamily,
					fontSize: textControlState.fontSize,
					fontWeight: textControlState.fontWeight,
					fontStyle: textControlState.fontStyle,
					textAlign: textControlState.textAlign
				}
			}));
		});
	}

	handleMarkupShapeChange(event) {
		const markupShape = normalizeMarkupShapeForComponent(
			this.state.markupComponentType,
			event && event.target ? event.target.value : "rectangle"
		);
		const fillAllowed = markupShapeSupportsFill(markupShape) && !isSpotlightMarkupComponent(this.state.markupComponentType);
		this.setState({
			markupShape,
			markupBorderRadius: markupShapeSupportsBorderRadius(markupShape) ? this.state.markupBorderRadius : 0,
			markupFillEnabled: fillAllowed ? this.state.markupFillEnabled : false
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				shape: markupShape,
				style: {
					...item.style,
					borderRadius: markupShapeSupportsBorderRadius(markupShape) ? item.style.borderRadius : 0,
					borderFillColor: fillAllowed ? item.style.borderFillColor : "transparent"
				}
			}));
		});
	}

	handleMarkupBlurPxChange(event) {
		const markupBlurPx = clampValue(parseInt(event && event.target ? event.target.value : 0, 10) || 0, 0, MAX_BLUR_PX);
		this.setState({
			markupBlurPx
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					blurPx: markupBlurPx
				}
			}));
		});
	}

	handleMarkupBorderColorChange(colorResult) {
		const markupBorderColor = colorResultToRgba(colorResult, this.state.markupBorderColor);
		this.setState({
			markupBorderColor
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					borderColor: markupBorderColor
				}
			}));
		});
	}

	handleMarkupBorderWidthChange(event) {
		const markupBorderWidth = clampValue(parseInt(event && event.target ? event.target.value : 0, 10) || 0, 0, 16);
		this.setState({
			markupBorderWidth
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					borderWidth: markupBorderWidth
				}
			}));
		});
	}

	handleMarkupBorderRadiusChange(event) {
		const markupBorderRadius = clampValue(parseInt(event && event.target ? event.target.value : 0, 10) || 0, 0, 64);
		this.setState({
			markupBorderRadius
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					borderRadius: markupShapeSupportsBorderRadius(item.shape || "rectangle") ? markupBorderRadius : 0
				}
			}));
		});
	}

	handleMarkupFillEnabledChange(event) {
		const markupFillEnabled = !!(event && event.target ? event.target.checked : false);
		this.setState({
			markupFillEnabled
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					borderFillColor: markupFillEnabled
						? (this.state.markupFillColor || item.style.borderColor || this.state.markupBorderColor)
						: "transparent"
				}
			}));
		});
	}

	handleMarkupFillColorChange(colorResult) {
		const markupFillColor = colorResultToRgba(colorResult, this.state.markupFillColor);
		this.setState({
			markupFillColor
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					borderFillColor: this.state.markupFillEnabled
						? markupFillColor
						: "transparent"
				}
			}));
		});
	}

	handleMarkupSpotlightBackgroundColorChange(colorResult) {
		const markupSpotlightBackgroundColor = colorResultToRgba(colorResult, this.state.markupSpotlightBackgroundColor);
		this.setState({
			markupSpotlightBackgroundColor
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					spotlightBackgroundColor: markupSpotlightBackgroundColor
				}
			}));
		});
	}

	handleMarkupSpotlightOpacityChange(event) {
		const markupSpotlightOpacity = normalizeMarkupSpotlightOpacity(event && event.target ? event.target.value : MARKUP_SPOTLIGHT_DEFAULTS.opacity);
		this.setState({
			markupSpotlightOpacity
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					spotlightOpacity: markupSpotlightOpacity
				}
			}));
		});
	}

	handleMarkupSpotlightBlurPxChange(event) {
		const markupSpotlightBlurPx = normalizeMarkupSpotlightBlur(event && event.target ? event.target.value : MARKUP_SPOTLIGHT_DEFAULTS.blur);
		this.setState({
			markupSpotlightBlurPx
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					spotlightBlurPx: markupSpotlightBlurPx
				}
			}));
		});
	}

	handleMarkupTextValueChange(event) {
		const markupTextValue = event && event.target ? event.target.value : "";
		this.setState({
			markupTextValue
		}, () => {
			this.updateSelectedTextMarkupItem((item) => ({
				...item,
				text: markupTextValue
			}));
		});
	}

	handleMarkupTextColorChange(colorResult) {
		const markupTextColor = colorResultToRgba(colorResult, this.state.markupTextColor);
		this.setState({
			markupTextColor
		}, () => {
			this.updateSelectedTextMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					textColor: markupTextColor
				}
			}));
		});
	}

	handleMarkupFontFamilyChange(event) {
		const markupFontFamily = event && event.target ? event.target.value : "";
		this.setState({
			markupFontFamily
		}, () => {
			this.addMarkupFontFamilyOption(markupFontFamily);
			this.updateSelectedTextMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					fontFamily: `${markupFontFamily || ""}`.trim() || MARKUP_TEXT_DEFAULT_FONT_FAMILY
				}
			}));
		});
	}

	handleMarkupFontSourceUrlChange(event) {
		this.setState({
			markupFontSourceUrl: event && event.target ? event.target.value : "",
			markupFontLoadError: "",
			markupFontLoadMessage: ""
		});
	}

	async loadMarkupFontStylesheet(fontUrl = "") {
		const trimmedFontUrl = `${fontUrl || ""}`.trim();
		if (!trimmedFontUrl) {
			throw new Error("Provide a font stylesheet URL first.");
		}
		if (typeof document === "undefined") {
			throw new Error("Web font loading is unavailable in this environment.");
		}

		const existingEntry = this.markupFontStylesheetRegistry.get(trimmedFontUrl);
		if (existingEntry && existingEntry.promise) {
			return existingEntry.promise;
		}

		const link = document.createElement("link");
		link.rel = "stylesheet";
		link.href = trimmedFontUrl;
		link.crossOrigin = "anonymous";

		const promise = new Promise((resolve, reject) => {
			link.onload = () => resolve(trimmedFontUrl);
			link.onerror = () => reject(new Error("The font stylesheet could not be loaded."));
		});

		this.markupFontStylesheetRegistry.set(trimmedFontUrl, { link, promise });
		document.head.appendChild(link);
		return promise;
	}

	async waitForMarkupFont(fontFamily = "") {
		const trimmedFontFamily = `${fontFamily || ""}`.trim();
		if (!trimmedFontFamily || typeof document === "undefined" || !document.fonts || typeof document.fonts.load !== "function") {
			return;
		}

		const descriptor = this.buildCanvasFontString({
			fontFamily: trimmedFontFamily,
			fontSize: this.state.markupFontSize || getMarkupDefaults("text").fontSize,
			fontWeight: this.state.markupFontWeight || getMarkupDefaults("text").fontWeight,
			fontStyle: this.state.markupFontStyle || getMarkupDefaults("text").fontStyle
		});

		await Promise.race([
			Promise.all([
				document.fonts.load(descriptor),
				document.fonts.ready
			]),
			new Promise((resolve) => window.setTimeout(resolve, 2500))
		]);
	}

	async handleLoadMarkupWebFont() {
		const fontUrl = `${this.state.markupFontSourceUrl || ""}`.trim();
		const fontFamily = getSuggestedMarkupFontFamilyName(this.state.markupFontFamily, fontUrl);
		if (!fontUrl) {
			this.setState({
				markupFontLoadError: "Provide a font stylesheet URL first.",
				markupFontLoadMessage: ""
			});
			return;
		}

		this.setState({
			markupFontLoading: true,
			markupFontLoadError: "",
			markupFontLoadMessage: ""
		});

		try {
			await this.loadMarkupFontStylesheet(fontUrl);
			this.addMarkupFontFamilyOption(fontFamily);
			await this.waitForMarkupFont(fontFamily);
			this.setState({
				markupFontFamily: fontFamily,
				markupFontLoading: false,
				markupFontLoadMessage: `Loaded web font source for ${fontFamily}.`,
				markupFontLoadError: ""
			});
		} catch (err) {
			this.setState({
				markupFontLoading: false,
				markupFontLoadMessage: "",
				markupFontLoadError: err && err.message ? err.message : `${err}`
			});
		}
	}

	async handleMarkupFontFileSelected(event) {
		const file = event && event.target && event.target.files ? event.target.files[0] : null;
		if (!file) {
			return;
		}

		const fontFamily = getSuggestedMarkupFontFamilyName(this.state.markupFontFamily, file.name);
		if (event && event.target) {
			event.target.value = "";
		}

		if (typeof window === "undefined" || typeof window.FontFace === "undefined" || typeof document === "undefined" || !document.fonts) {
			this.setState({
				markupFontLoadError: "This browser cannot load local font files directly.",
				markupFontLoadMessage: ""
			});
			return;
		}

		this.setState({
			markupFontLoading: true,
			markupFontLoadError: "",
			markupFontLoadMessage: ""
		});

		const objectUrl = URL.createObjectURL(file);
		try {
			const fontFace = new window.FontFace(fontFamily, `url(${objectUrl})`);
			const loadedFontFace = await fontFace.load();
			document.fonts.add(loadedFontFace);
			this.markupLoadedFontObjectUrls.push(objectUrl);
			this.addMarkupFontFamilyOption(fontFamily);
			await this.waitForMarkupFont(fontFamily);
			this.setState({
				markupFontFamily: fontFamily,
				markupFontLoading: false,
				markupFontLoadMessage: `Loaded local font file ${file.name} as ${fontFamily}.`,
				markupFontLoadError: ""
			});
		} catch (err) {
			if (typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
				URL.revokeObjectURL(objectUrl);
			}
			this.setState({
				markupFontLoading: false,
				markupFontLoadMessage: "",
				markupFontLoadError: err && err.message ? err.message : `${err}`
			});
		}
	}

	async handleLoadMarkupLocalFonts() {
		if (typeof window === "undefined" || typeof window.queryLocalFonts !== "function") {
			this.setState({
				markupFontLoadError: "This browser does not support local font discovery. You can still type an installed font family name manually.",
				markupFontLoadMessage: ""
			});
			return;
		}

		this.setState({
			markupFontLoading: true,
			markupFontLoadError: "",
			markupFontLoadMessage: ""
		});

		try {
			const localFontRecords = await window.queryLocalFonts();
			const localFontFamilies = buildUniqueStringList(localFontRecords.map((fontRecord) => fontRecord && fontRecord.family));
			this.setState((prevState) => ({
				markupCustomFontFamilies: buildUniqueStringList([
					...(Array.isArray(prevState.markupCustomFontFamilies) ? prevState.markupCustomFontFamilies : []),
					...localFontFamilies
				]),
				markupFontLoading: false,
				markupFontLoadMessage: localFontFamilies.length
					? `Added ${localFontFamilies.length} installed local font families.`
					: "No local fonts were returned by the browser.",
				markupFontLoadError: ""
			}));
		} catch (err) {
			this.setState({
				markupFontLoading: false,
				markupFontLoadMessage: "",
				markupFontLoadError: err && err.message ? err.message : `${err}`
			});
		}
	}

	handleMarkupFontSizeChange(event) {
		const rawValue = event && event.target ? event.target.value : "";
		const markupFontSize = rawValue === ""
			? ""
			: normalizeMarkupFontSize(rawValue, getMarkupDefaults("text").fontSize);
		this.setState({
			markupFontSize
		}, () => {
			this.updateSelectedTextMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					fontSize: normalizeMarkupFontSize(markupFontSize, getMarkupDefaults("text").fontSize)
				}
			}));
		});
	}

	handleMarkupFontWeightChange(event) {
		const markupFontWeight = normalizeMarkupFontWeight(event && event.target ? event.target.value : "400");
		this.setState({
			markupFontWeight
		}, () => {
			this.updateSelectedTextMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					fontWeight: markupFontWeight
				}
			}));
		});
	}

	handleMarkupFontStyleChange(event) {
		const markupFontStyle = normalizeMarkupFontStyle(event && event.target ? event.target.value : "normal");
		this.setState({
			markupFontStyle
		}, () => {
			this.updateSelectedTextMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					fontStyle: markupFontStyle
				}
			}));
		});
	}

	handleMarkupTextAlignChange(event) {
		const markupTextAlign = normalizeMarkupTextAlign(event && event.target ? event.target.value : "left");
		this.setState({
			markupTextAlign
		}, () => {
			this.updateSelectedTextMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					textAlign: markupTextAlign
				}
			}));
		});
	}

	handleSelectedMarkupRectFieldChange(field = "x", event) {
		const selectedRect = this.getSelectedMarkupRect();
		if (!selectedRect) {
			return;
		}

		const rawValue = event && event.target ? event.target.value : "";
		if (rawValue === "") {
			return;
		}

		const numericValue = Math.round(Number(rawValue) || 0);
		if (!Number.isFinite(numericValue)) {
			return;
		}

		const nextRect = {
			x: Number(selectedRect.x) || 0,
			y: Number(selectedRect.y) || 0,
			width: Number(selectedRect.width) || MIN_MARKUP_ITEM_SIZE,
			height: Number(selectedRect.height) || MIN_MARKUP_ITEM_SIZE
		};

		if (field === "width" || field === "height") {
			nextRect[field] = Math.max(numericValue, MIN_MARKUP_ITEM_SIZE);
		} else {
			nextRect[field] = Math.max(numericValue, 0);
		}

		this.setSelectedMarkupRect(nextRect);
	}

	handleMarkupRotationChange(event) {
		const rawValue = event && event.target ? event.target.value : "";
		const markupRotation = rawValue === "" ? 0 : normalizeMarkupRotation(parseFloat(rawValue));
		this.setState({
			markupRotation
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				rotation: markupRotation
			}));
		});
	}

	handleRotateSelectedMarkup(deltaDegrees = 0) {
		const markupRotation = normalizeMarkupRotation((this.state.markupRotation || 0) + deltaDegrees);
		this.setState({
			markupRotation
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				rotation: markupRotation
			}));
		});
	}

	handleMarkupToggleLabels() {
		this.setState((prevState) => ({
			markupShowItemIds: !prevState.markupShowItemIds
		}));
	}

	handleMarkupPreviewScaleChange(event) {
		const markupPreviewScale = clampValue(parseInt(event && event.target ? event.target.value : 100, 10) || 100, 25, 200);
		this.setState({
			markupPreviewScale
		});
	}

	handleMarkupLayoutModeChange(event) {
		const nextLayoutMode = normalizeMarkupLayoutMode(event && event.target ? event.target.value : MARKUP_LAYOUT_MODE_SPLIT);
		this.setState({
			markupLayoutMode: nextLayoutMode
		});
		setLs(MARKUP_LAYOUT_MODE_KEY, nextLayoutMode);
	}

	handleToggleMarkupLeftPanelCollapsed() {
		this.setState((prevState) => {
			const nextValue = !prevState.markupLeftPanelCollapsed;
			setLs(MARKUP_LEFT_PANEL_COLLAPSED_KEY, nextValue ? "true" : "false");
			return {
				markupLeftPanelCollapsed: nextValue
			};
		});
	}

	handleToggleMarkupRightPanelCollapsed() {
		this.setState((prevState) => {
			const nextValue = !prevState.markupRightPanelCollapsed;
			setLs(MARKUP_RIGHT_PANEL_COLLAPSED_KEY, nextValue ? "true" : "false");
			return {
				markupRightPanelCollapsed: nextValue
			};
		});
	}

	handleMarkupMouseDown(event) {
		if (event && typeof event.button === "number" && event.button !== 0) {
			return;
		}

		const point = this.getMarkupPointerPosition(event);
		if (!point) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		if (this.state.markupColorPickTarget || this.state.markupImagePatchBgPickMode) {
			this.setState({
				markupMouseX: point.x,
				markupMouseY: point.y,
				markupPointerInCanvas: true
			});
			return;
		}
		this.setState({
			selectedMarkupGuid: "",
			markupMouseDown: true,
			markupStartX: point.x,
			markupStartY: point.y,
			markupCurrentX: point.x,
			markupCurrentY: point.y,
			markupMouseX: point.x,
			markupMouseY: point.y,
			markupPointerInCanvas: true
		});
	}

	handleMarkupMouseMove(event) {
		const point = this.getMarkupPointerPosition(event);
		if (!point) {
			return;
		}

		if (this.state.markupMouseDown) {
			this.setState({
				markupCurrentX: point.x,
				markupCurrentY: point.y,
				markupMouseX: point.x,
				markupMouseY: point.y,
				markupPointerInCanvas: true
			});
			return;
		}

		this.setState({
			markupMouseX: point.x,
			markupMouseY: point.y,
			markupPointerInCanvas: true
		});
	}

	handleMarkupMouseUp(event) {
		const imageCopyModeActive = !!this.state.markupImageCopyMode;
		const point = this.getMarkupPointerPosition(event) || {
			x: this.state.markupCurrentX,
			y: this.state.markupCurrentY
		};

		if (this.state.markupImagePatchBgPickMode) {
			if (event) {
				event.preventDefault();
				event.stopPropagation();
			}
			this.suppressNextMarkupCanvasClick = true;
			this.handleMarkupImagePatchBgPickFromPoint(point);
			return;
		}

		if (this.state.markupColorPickTarget) {
			if (event) {
				event.preventDefault();
				event.stopPropagation();
			}
			this.suppressNextMarkupCanvasClick = true;
			this.handleMarkupColorPickFromPoint(point);
			return;
		}

		if (!this.state.markupMouseDown) {
			return;
		}

		const nextItem = this.createMarkupItemFromBounds(
			this.state.markupStartX,
			this.state.markupStartY,
			point.x,
			point.y,
			imageCopyModeActive
				? { componentType: "imagePatch", useDefaults: true }
				: undefined
		);

		if (!nextItem) {
			this.setState({
				markupMouseDown: false
			});
			return;
		}

		if (imageCopyModeActive) {
			this.setMarkupClipboardItem(nextItem, () => {
				this.setState({
					selectedMarkupGuid: "",
					markupMouseDown: false,
					markupImageCopyMode: false,
					markupMouseX: point.x,
					markupMouseY: point.y,
					markupPointerInCanvas: true,
					markupCurrentX: point.x,
					markupCurrentY: point.y
				});
			});
			return;
		}

		this.setState((prevState) => ({
			markupItems: [...prevState.markupItems, nextItem],
			selectedMarkupGuid: nextItem.guid,
			markupMouseDown: false,
			markupMouseX: point.x,
			markupMouseY: point.y,
			markupPointerInCanvas: true,
			markupCurrentX: point.x,
			markupCurrentY: point.y
		}), () => {
			this.suppressNextMarkupCanvasClick = true;
			this.applyMarkupSelectionToControls(nextItem);
			if (isImagePatchMarkupComponent(nextItem.type || "")) {
				this.setMarkupClipboardItem(nextItem);
			}
		});
	}

	handleMarkupMouseLeave(event) {
		this.handleMarkupMouseUp(event);
		this.setState({
			markupPointerInCanvas: false
		});
	}

	handleMarkupCanvasClick() {
		if (this.suppressNextMarkupCanvasClick) {
			this.suppressNextMarkupCanvasClick = false;
			return;
		}
		if (this.state.markupColorPickTarget) {
			return;
		}
		if (this.state.markupImagePatchBgPickMode) {
			return;
		}
		if (this.state.markupImageCopyMode) {
			return;
		}
		if (this.state.markupMouseDown) {
			return;
		}

		this.setState({
			selectedMarkupGuid: "",
			markupRotation: 0
		});
	}

	handleSelectMarkupItem(guid) {
		const markupItem = this.state.markupItems.find((item) => item.guid === guid);
		if (!markupItem) {
			return;
		}
		this.applyMarkupSelectionToControls(markupItem);
	}

	handleDeleteMarkupItem(guid) {
		this.setState((prevState) => ({
			markupItems: prevState.markupItems.filter((item) => item.guid !== guid),
			selectedMarkupGuid: prevState.selectedMarkupGuid === guid ? "" : prevState.selectedMarkupGuid,
			markupRotation: prevState.selectedMarkupGuid === guid ? 0 : prevState.markupRotation,
			markupImagePatchBgPickMode: prevState.selectedMarkupGuid === guid ? false : prevState.markupImagePatchBgPickMode,
			markupImagePatchProcessing: prevState.selectedMarkupGuid === guid ? false : prevState.markupImagePatchProcessing
		}));
	}

	handleDeleteSelectedMarkupItem() {
		if (!this.state.selectedMarkupGuid) {
			return;
		}
		this.handleDeleteMarkupItem(this.state.selectedMarkupGuid);
	}

	runMarkupPressAndHold(action) {
		this.markupPressAndHoldCount += 1;
		const nextStepSize = this.markupPressAndHoldCount > 50 ? 20 : this.markupPressAndHoldCount > 5 ? 5 : 1;
		action(nextStepSize);
		const nextDelay = this.markupPressAndHoldCount > 5 ? 50 : 200;
		this.markupPressAndHoldTimer = window.setTimeout(() => {
			this.runMarkupPressAndHold(action);
		}, nextDelay);
	}

	startMarkupPressAndHold(action) {
		this.stopMarkupPressAndHold();
		this.markupPressAndHoldCount = 0;
		this.markupPressAndHoldTimer = window.setTimeout(() => {
			this.runMarkupPressAndHold(action);
		}, 200);
		window.addEventListener("mouseup", this.stopMarkupPressAndHold);
	}

	stopMarkupPressAndHold() {
		if (this.markupPressAndHoldTimer) {
			window.clearTimeout(this.markupPressAndHoldTimer);
			this.markupPressAndHoldTimer = null;
		}
		this.markupPressAndHoldCount = 0;
		window.removeEventListener("mouseup", this.stopMarkupPressAndHold);
	}

	handleToggleMarkupVisibility(guid, event) {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		this.toggleMarkupVisibility(guid);
	}

	handleToggleSelectedMarkupVisibility() {
		if (!this.state.selectedMarkupGuid) {
			return;
		}
		this.toggleMarkupVisibility(this.state.selectedMarkupGuid);
	}

	getMarkupItemIndexByGuid(guid = "", markupItems = this.state.markupItems) {
		if (!guid || !Array.isArray(markupItems)) {
			return -1;
		}
		return markupItems.findIndex((item) => item.guid === guid);
	}

	handleReorderMarkupItem(guid, action = "forward", event) {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}

		this.setState((prevState) => {
			const currentIndex = this.getMarkupItemIndexByGuid(guid, prevState.markupItems);
			if (currentIndex < 0 || prevState.markupItems.length < 2) {
				return null;
			}

			let nextIndex = currentIndex;
			if (action === "forward") {
				nextIndex = Math.min(currentIndex + 1, prevState.markupItems.length - 1);
			} else if (action === "backward") {
				nextIndex = Math.max(currentIndex - 1, 0);
			} else if (action === "front") {
				nextIndex = prevState.markupItems.length - 1;
			} else if (action === "back") {
				nextIndex = 0;
			}

			if (nextIndex === currentIndex) {
				return null;
			}

			const nextItems = [...prevState.markupItems];
			const [movedItem] = nextItems.splice(currentIndex, 1);
			nextItems.splice(nextIndex, 0, movedItem);
			return {
				markupItems: nextItems
			};
		});
	}

	handleReorderSelectedMarkup(action = "forward") {
		if (!this.state.selectedMarkupGuid) {
			return;
		}
		this.handleReorderMarkupItem(this.state.selectedMarkupGuid, action);
	}

	handleCopySelectedMarkup() {
		const selectedMarkupItem = this.getSelectedMarkupItem();
		if (!selectedMarkupItem) {
			return;
		}
		this.setMarkupClipboardItem(selectedMarkupItem, () => {
			this.setState({
				markupImageCopyMode: false
			});
		});
	}

	handlePasteMarkupClipboard() {
		const pastedMarkupItem = this.buildPastedMarkupItem(this.state.markupClipboardItem);
		if (!pastedMarkupItem) {
			return;
		}

		this.setState((prevState) => ({
			markupItems: [...prevState.markupItems, pastedMarkupItem],
			selectedMarkupGuid: pastedMarkupItem.guid,
			markupImageCopyMode: false,
			markupImagePatchBgPickMode: false,
			markupRotation: normalizeMarkupRotation(pastedMarkupItem.rotation || 0)
		}), () => {
			this.applyMarkupSelectionToControls(pastedMarkupItem);
		});
	}

	handleCloneSelectedMarkup() {
		const selectedMarkupItem = this.getSelectedMarkupItem();
		if (!selectedMarkupItem) {
			return;
		}

		const clonedMarkupItem = this.buildPastedMarkupItem(selectedMarkupItem);
		if (!clonedMarkupItem) {
			return;
		}

		this.setState((prevState) => ({
			markupItems: [...prevState.markupItems, clonedMarkupItem],
			selectedMarkupGuid: clonedMarkupItem.guid,
			markupImagePatchBgPickMode: false,
			markupRotation: normalizeMarkupRotation(clonedMarkupItem.rotation || 0)
		}), () => {
			this.applyMarkupSelectionToControls(clonedMarkupItem);
		});
	}

	handleNudgeSelectedMarkup(deltaX = 0, deltaY = 0) {
		const selectedMarkupItem = this.getSelectedMarkupItem();
		if (!selectedMarkupItem) {
			return;
		}

		this.setSelectedMarkupRect({
			x: (selectedMarkupItem.coords.x || 0) + deltaX,
			y: (selectedMarkupItem.coords.y || 0) + deltaY,
			width: selectedMarkupItem.coords.width,
			height: selectedMarkupItem.coords.height
		});
	}

	startMarkupMoveDrag(markupItem, event) {
		const point = this.getMarkupPointerPosition(event);
		if (!markupItem || !point) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		this.markupPointerDrag = {
			type: "move",
			guid: markupItem.guid,
			startPoint: point,
			startCoords: { ...markupItem.coords }
		};
		this.attachMarkupPointerListeners();
	}

	startMarkupResizeDrag(markupItem, handle = "se", event) {
		const point = this.getMarkupPointerPosition(event);
		if (!markupItem || !point || !markupItem.coords) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		this.markupPointerDrag = {
			type: "resize",
			guid: markupItem.guid,
			handle,
			startRect: { ...markupItem.coords },
			rotation: normalizeMarkupRotation(markupItem.rotation || 0)
		};
		this.attachMarkupPointerListeners();
	}

	startMarkupRotateDrag(markupItem, event) {
		const point = this.getMarkupPointerPosition(event);
		if (!markupItem || !point) {
			return;
		}

		const centerX = markupItem.coords.x + (markupItem.coords.width / 2);
		const centerY = markupItem.coords.y + (markupItem.coords.height / 2);
		const startAngle = Math.atan2(point.y - centerY, point.x - centerX);

		event.preventDefault();
		event.stopPropagation();
		this.markupPointerDrag = {
			type: "rotate",
			guid: markupItem.guid,
			centerX,
			centerY,
			startAngle,
			startRotation: normalizeMarkupRotation(markupItem.rotation || 0)
		};
		this.attachMarkupPointerListeners();
	}

	attachMarkupPointerListeners() {
		const targetWindow = this.getMarkupOwnerWindow();
		if (!targetWindow) {
			return;
		}

		if (this.markupPointerListenerWindow && this.markupPointerListenerWindow !== targetWindow) {
			this.detachMarkupPointerListeners();
		}

		targetWindow.addEventListener("mousemove", this.handleWindowMarkupMouseMove);
		targetWindow.addEventListener("mouseup", this.handleWindowMarkupMouseUp);
		this.markupPointerListenerWindow = targetWindow;
	}

	detachMarkupPointerListeners() {
		if (this.markupPointerListenerWindow) {
			try {
				this.markupPointerListenerWindow.removeEventListener("mousemove", this.handleWindowMarkupMouseMove);
				this.markupPointerListenerWindow.removeEventListener("mouseup", this.handleWindowMarkupMouseUp);
			} catch (err) {
				// Ignore listener cleanup failures for already-closed windows.
			}
		}
		this.markupPointerListenerWindow = null;
		this.markupPointerDrag = null;
	}

	handleWindowMarkupMouseMove(event) {
		const drag = this.markupPointerDrag;
		if (!drag) {
			return;
		}

		const point = this.getMarkupPointerPosition(event);
		if (!point) {
			return;
		}

		this.setState({
			markupMouseX: point.x,
			markupMouseY: point.y
		});

		if (drag.type === "move") {
			const selectedMarkupItem = this.state.markupItems.find((item) => item.guid === drag.guid);
			if (!selectedMarkupItem) {
				return;
			}

			const deltaX = point.x - drag.startPoint.x;
			const deltaY = point.y - drag.startPoint.y;
			const nextRect = this.constrainMarkupRect({
				x: drag.startCoords.x + deltaX,
				y: drag.startCoords.y + deltaY,
				width: selectedMarkupItem.coords.width,
				height: selectedMarkupItem.coords.height
			});

			this.setState((prevState) => ({
				markupItems: prevState.markupItems.map((item) => {
					if (item.guid !== drag.guid) {
						return item;
					}

					return {
						...item,
						coords: {
							...item.coords,
							...nextRect
						}
					};
				})
			}));
			return;
		}

		if (drag.type === "resize") {
			const nextRect = this.getResizedMarkupRectFromHandle(drag.startRect, drag.handle, point, drag.rotation);
			this.setState((prevState) => ({
				markupItems: prevState.markupItems.map((item) => {
					if (item.guid !== drag.guid) {
						return item;
					}

					return {
						...item,
						coords: {
							...item.coords,
							...nextRect
						}
					};
				})
			}));
			return;
		}

		if (drag.type === "rotate") {
			const currentAngle = Math.atan2(point.y - drag.centerY, point.x - drag.centerX);
			const nextRotation = normalizeMarkupRotation(drag.startRotation + ((currentAngle - drag.startAngle) * 180 / Math.PI));
			this.setState((prevState) => ({
				markupRotation: nextRotation,
				markupItems: prevState.markupItems.map((item) => {
					if (item.guid !== drag.guid) {
						return item;
					}

					return {
						...item,
						rotation: nextRotation
					};
				})
			}));
		}
	}

	handleWindowMarkupMouseUp() {
		this.detachMarkupPointerListeners();
	}

	getMarkupStrokeConfig(markupItem = {}, { showPlaceholderStroke = true, placeholderStrokeColor = "", placeholderStrokeWidth = 1 } = {}) {
		let strokeWidth = Number(markupItem.style && markupItem.style.borderWidth);
		let stroke = markupItem.style && markupItem.style.borderColor ? markupItem.style.borderColor : "#2563eb";
		let strokeDasharray;
		if (!Number.isFinite(strokeWidth)) {
			strokeWidth = 1;
		}
		if (strokeWidth <= 0 || isTransparentMarkupColor(stroke)) {
			if (showPlaceholderStroke) {
				stroke = placeholderStrokeColor || "#2563eb";
				strokeWidth = Math.max(Number(placeholderStrokeWidth) || 1, 1);
				strokeDasharray = "10,10";
			} else {
				strokeWidth = 0;
			}
		}
		return {
			strokeWidth,
			stroke,
			strokeDasharray
		};
	}

	getMarkupTempItem() {
		if (!this.state.markupMouseDown) {
			return null;
		}

		return this.createMarkupItemFromBounds(
			this.state.markupStartX,
			this.state.markupStartY,
			this.state.markupCurrentX,
			this.state.markupCurrentY,
			this.state.markupImageCopyMode
				? { componentType: "imagePatch", useDefaults: true }
				: undefined
		);
	}

	buildArrowPolygonPoints(coords = {}) {
		const x = coords.x || 0;
		const y = coords.y || 0;
		const width = coords.width || 0;
		const height = coords.height || 0;
		return `${x},${y + (height * 0.33)} ${x + (width * 0.5)},${y + (height * 0.33)} ${x + (width * 0.5)},${y} ${x + width},${y + (height * 0.5)} ${x + (width * 0.5)},${y + height} ${x + (width * 0.5)},${y + (height * 0.67)} ${x},${y + (height * 0.67)}`;
	}

	buildDiamondPolygonPoints(coords = {}) {
		const x = Number(coords.x) || 0;
		const y = Number(coords.y) || 0;
		const width = Number(coords.width) || 0;
		const height = Number(coords.height) || 0;
		const midX = x + (width * 0.5);
		const midY = y + (height * 0.5);
		return `${midX},${y} ${x + width},${midY} ${midX},${y + height} ${x},${midY}`;
	}

	renderMarkupSvgShapeElement(shape = "rectangle", coords = {}, props = {}, style = {}) {
		const normalizedShape = `${shape || "rectangle"}`.trim().toLowerCase();
		const x = Number(coords.x) || 0;
		const y = Number(coords.y) || 0;
		const width = Number(coords.width) || 0;
		const height = Number(coords.height) || 0;
		const shapeProps = {
			strokeWidth: props.strokeWidth,
			stroke: props.stroke,
			fill: props.fill,
			strokeDasharray: props.strokeDasharray,
			opacity: props.opacity,
			pointerEvents: props.pointerEvents
		};
		Object.keys(shapeProps).forEach((key) => {
			if (shapeProps[key] === undefined) {
				delete shapeProps[key];
			}
		});

		if (normalizedShape === "ellipse") {
			return (
				<ellipse
					cx={x + (width * 0.5)}
					cy={y + (height * 0.5)}
					rx={Math.abs(width) * 0.5}
					ry={Math.abs(height) * 0.5}
					{...shapeProps}
				/>
			);
		}

		if (normalizedShape === "diamond") {
			return (
				<polygon
					points={this.buildDiamondPolygonPoints(coords)}
					strokeLinejoin="round"
					{...shapeProps}
				/>
			);
		}

		if (normalizedShape === "arrow") {
			return (
				<polygon
					points={this.buildArrowPolygonPoints(coords)}
					strokeLinejoin="round"
					{...shapeProps}
				/>
			);
		}

		if (normalizedShape === "line") {
			return (
				<line
					x1={x}
					y1={y}
					x2={x + width}
					y2={y + height}
					strokeLinecap="round"
					strokeLinejoin="round"
					{...shapeProps}
					fill="none"
				/>
			);
		}

		return (
			<rect
				x={x}
				y={y}
				width={width}
				height={height}
				rx={style.borderRadius}
				{...shapeProps}
			/>
		);
	}

	renderMarkupSpotlightLayerSvg(markupItems = [], { idPrefix = "markup-spotlight" } = {}) {
		const spotlightItems = (Array.isArray(markupItems) ? markupItems : [])
			.filter((item) => item && item.coords && item.visible !== false && isSpotlightMarkupComponent(item.type || "highlight"));
		const imageSize = this.state.markupSourceImageSize || {};
		const imageWidth = Number(imageSize.width) || 0;
		const imageHeight = Number(imageSize.height) || 0;

		if (!imageWidth || !imageHeight || !spotlightItems.length) {
			return null;
		}

		const spotlight = getMarkupSpotlightLayerSettings(spotlightItems);
		const blur = normalizeMarkupSpotlightBlur(spotlight.blur);
		const safeIdPrefix = sanitizeMarkupSvgId(idPrefix);
		const maskId = `${safeIdPrefix}-mask`;
		const filterId = `${safeIdPrefix}-blur`;

		return (
			<g pointerEvents="none">
				<defs>
					{blur > 0 && (
						<filter
							id={filterId}
							x={-imageWidth * 0.1}
							y={-imageHeight * 0.1}
							width={imageWidth * 1.2}
							height={imageHeight * 1.2}
							filterUnits="userSpaceOnUse"
						>
							<feGaussianBlur in="SourceGraphic" stdDeviation={blur} />
						</filter>
					)}
					<mask id={maskId} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse" x="0" y="0" width={imageWidth} height={imageHeight}>
						<rect x="0" y="0" width={imageWidth} height={imageHeight} fill="#ffffff" />
						{spotlightItems.map((item, index) => {
							const coords = item.coords || {};
							const centerX = Number(coords.x) + ((Number(coords.width) || 0) * 0.5);
							const centerY = Number(coords.y) + ((Number(coords.height) || 0) * 0.5);
							const rotation = normalizeMarkupRotation(item.rotation || 0);
							const rotationTransform = rotation ? `rotate(${rotation} ${centerX} ${centerY})` : undefined;
							const borderRadius = Number(item.style && item.style.borderRadius) || 0;
							return (
								<g key={`spotlight-cutout-${item.guid || item.id || index}`} transform={rotationTransform}>
									{this.renderMarkupSvgShapeElement(item.shape || "rectangle", coords, {
										stroke: "#000000",
										strokeWidth: getMarkupSpotlightCutoutStrokeWidth(item),
										fill: (item.shape || "rectangle").toLowerCase() === "line" ? "none" : "#000000"
									}, {
										borderRadius
									})}
								</g>
							);
						})}
					</mask>
				</defs>
				<g mask={`url(#${maskId})`} pointerEvents="none">
					{blur > 0 && this.state.markupSourceUrl && (
						<image
							href={this.state.markupSourceUrl}
							x="0"
							y="0"
							width={imageWidth}
							height={imageHeight}
							filter={`url(#${filterId})`}
						/>
					)}
					<rect
						x="0"
						y="0"
						width={imageWidth}
						height={imageHeight}
						fill={spotlight.backgroundColor}
						opacity={spotlight.opacity}
					/>
				</g>
			</g>
		);
	}

	renderMarkupSvgItem(markupItem, { isDraft = false } = {}) {
		if (!markupItem || !markupItem.coords) {
			return null;
		}
		if (!isDraft && markupItem.visible === false) {
			return null;
		}

		const isSelected = !isDraft && markupItem.guid === this.state.selectedMarkupGuid;
		const isTextMarkup = isTextMarkupComponent(markupItem.type || "highlight");
		const isImagePatchMarkup = isImagePatchMarkupComponent(markupItem.type || "highlight");
		const isSpotlightMarkup = isSpotlightMarkupComponent(markupItem.type || "highlight");
		const imagePatchEmbeddedSourceUrl = isImagePatchMarkup ? this.getMarkupImagePatchEmbeddedSourceUrl(markupItem) : "";
		const { strokeWidth, stroke, strokeDasharray } = this.getMarkupStrokeConfig(markupItem, {
			showPlaceholderStroke: (isTextMarkup || isImagePatchMarkup || isSpotlightMarkup) ? (isDraft || isSelected) : true,
			placeholderStrokeColor: isImagePatchMarkup ? "#2563eb" : "",
			placeholderStrokeWidth: isImagePatchMarkup ? 2 : 1
		});
		const fill = markupShapeSupportsFill(markupItem.shape || "rectangle") && !isSpotlightMarkup && markupItem.style && markupItem.style.borderFillColor ? markupItem.style.borderFillColor : "transparent";
		const blurPx = clampValue(Number(markupItem.style && markupItem.style.blurPx) || 0, 0, MAX_BLUR_PX);
		const isBlurRedact = isBlurMarkupComponent(markupItem.type || "highlight") && blurPx > 0;
		const imagePatchSourceRect = isImagePatchMarkup ? this.getMarkupImagePatchSourceRect(markupItem) : null;
		const rotation = normalizeMarkupRotation(markupItem.rotation || 0);
		const colorPickActive = !!this.state.markupColorPickTarget;
		const imagePatchBgPickModeActive = !!this.state.markupImagePatchBgPickMode;
		const imageCopyModeActive = !!this.state.markupImageCopyMode;
		const imageSamplingModeActive = colorPickActive || imagePatchBgPickModeActive || imageCopyModeActive;
		const centerX = markupItem.coords.x + (markupItem.coords.width / 2);
		const centerY = markupItem.coords.y + (markupItem.coords.height / 2);
		const textLayout = isTextMarkup ? this.getMarkupTextLayout(markupItem) : null;
		const markupPreviewScaleRatio = Math.max((this.state.markupPreviewScale || 100) / 100, 0.01);
		const overlayScaleInverse = 1 / markupPreviewScaleRatio;
		const rotateHandleOffset = 30 * overlayScaleInverse;
		const deleteHandleOffset = 10 * overlayScaleInverse;
		const resizeHandleSize = 12 * overlayScaleInverse;
		const resizeHandleRadius = 2 * overlayScaleInverse;
		const actionCircleRadius = 11 * overlayScaleInverse;
		const actionStrokeWidth = 2 * overlayScaleInverse;
		const deleteStrokeWidth = 3 * overlayScaleInverse;
		const actionFontSize = 14 * overlayScaleInverse;
		const connectorDashSize = 4 * overlayScaleInverse;
		const rotatedTopRight = rotatePointAroundCenter(markupItem.coords.x + markupItem.coords.width, markupItem.coords.y, centerX, centerY, rotation);
		const rotatedTopCenter = rotatePointAroundCenter(markupItem.coords.x + (markupItem.coords.width / 2), markupItem.coords.y, centerX, centerY, rotation);
		const rotatedTopLeft = rotatePointAroundCenter(markupItem.coords.x, markupItem.coords.y, centerX, centerY, rotation);
		const rotatedBottomRight = rotatePointAroundCenter(markupItem.coords.x + markupItem.coords.width, markupItem.coords.y + markupItem.coords.height, centerX, centerY, rotation);
		const rotatedBottomLeft = rotatePointAroundCenter(markupItem.coords.x, markupItem.coords.y + markupItem.coords.height, centerX, centerY, rotation);
		const rotatedHandlePoint = rotatePointAroundCenter(markupItem.coords.x + (markupItem.coords.width / 2), markupItem.coords.y - rotateHandleOffset, centerX, centerY, rotation);
		const deleteX = rotatedTopRight.x + deleteHandleOffset;
		const deleteY = rotatedTopRight.y - deleteHandleOffset;
		const resizeHandles = [
			{ id: "nw", point: rotatedTopLeft, cursor: "nwse-resize" },
			{ id: "ne", point: rotatedTopRight, cursor: "nesw-resize" },
			{ id: "se", point: rotatedBottomRight, cursor: "nwse-resize" },
			{ id: "sw", point: rotatedBottomLeft, cursor: "nesw-resize" }
		];
		const filterId = `markup-blur-filter-${markupItem.guid || "draft"}`;
		const clipId = `markup-blur-clip-${markupItem.guid || "draft"}`;
		const textClipId = `markup-text-clip-${markupItem.guid || "draft"}`;
		const imagePatchClipId = `markup-patch-clip-${markupItem.guid || "draft"}`;
		const imagePatchScaleX = imagePatchSourceRect ? (markupItem.coords.width / Math.max(imagePatchSourceRect.width, 1)) : 1;
		const imagePatchScaleY = imagePatchSourceRect ? (markupItem.coords.height / Math.max(imagePatchSourceRect.height, 1)) : 1;
		const commonProps = {
			strokeWidth,
			stroke,
			fill,
			strokeDasharray,
			opacity: isDraft ? 0.8 : 1
		};

		return (
			<g
				key={markupItem.guid || `draft-${markupItem.shape}`}
				onMouseDown={(event) => {
					if (isDraft || imageSamplingModeActive) {
						return;
					}
					event.preventDefault();
					event.stopPropagation();
					if (isSelected) {
						this.startMarkupMoveDrag(markupItem, event);
					}
				}}
				onClick={(event) => {
					if (isDraft || imageSamplingModeActive) {
						return;
					}
					event.stopPropagation();
					this.handleSelectMarkupItem(markupItem.guid);
				}}
				style={{
					cursor: imageSamplingModeActive || isDraft ? "crosshair" : "pointer",
					pointerEvents: imageSamplingModeActive ? "none" : "auto"
				}}
			>
				<g transform={rotation ? `rotate(${rotation} ${centerX} ${centerY})` : undefined}>
					{(isBlurRedact && this.state.markupSourceUrl) || isTextMarkup || (isImagePatchMarkup && (imagePatchSourceRect || imagePatchEmbeddedSourceUrl)) ? (
						<defs>
							{isBlurRedact && this.state.markupSourceUrl && (
								<>
									<filter id={filterId}>
										<feGaussianBlur stdDeviation={Math.max(blurPx / 2, 0.01)} />
									</filter>
									<clipPath id={clipId}>
										<rect
											x={markupItem.coords.x}
											y={markupItem.coords.y}
											width={markupItem.coords.width}
											height={markupItem.coords.height}
											rx={Number(markupItem.style && markupItem.style.borderRadius) || 0}
										/>
									</clipPath>
								</>
							)}
							{isImagePatchMarkup && (imagePatchSourceRect || imagePatchEmbeddedSourceUrl) && (
								<clipPath id={imagePatchClipId}>
									<rect
										x={markupItem.coords.x}
										y={markupItem.coords.y}
										width={markupItem.coords.width}
										height={markupItem.coords.height}
										rx={Number(markupItem.style && markupItem.style.borderRadius) || 0}
									/>
								</clipPath>
							)}
							{isTextMarkup && (
								<clipPath id={textClipId}>
									<rect
										x={markupItem.coords.x}
										y={markupItem.coords.y}
										width={markupItem.coords.width}
										height={markupItem.coords.height}
										rx={Number(markupItem.style && markupItem.style.borderRadius) || 0}
									/>
								</clipPath>
							)}
						</defs>
					) : null}
					{isBlurRedact && this.state.markupSourceUrl && (
						<image
							href={this.state.markupSourceUrl}
							x="0"
							y="0"
							width={this.state.markupSourceImageSize ? this.state.markupSourceImageSize.width : 1}
							height={this.state.markupSourceImageSize ? this.state.markupSourceImageSize.height : 1}
							clipPath={`url(#${clipId})`}
							filter={`url(#${filterId})`}
							opacity={isDraft ? 0.92 : 1}
							pointerEvents="none"
						/>
					)}
					{isImagePatchMarkup && imagePatchEmbeddedSourceUrl && (
						<image
							href={imagePatchEmbeddedSourceUrl}
							x={markupItem.coords.x}
							y={markupItem.coords.y}
							width={markupItem.coords.width}
							height={markupItem.coords.height}
							clipPath={`url(#${imagePatchClipId})`}
							preserveAspectRatio="none"
							opacity={isDraft ? 0.92 : 1}
							pointerEvents="none"
						/>
					)}
					{isImagePatchMarkup && !imagePatchEmbeddedSourceUrl && imagePatchSourceRect && this.state.markupSourceUrl && this.state.markupSourceImageSize && (
						<image
							href={this.state.markupSourceUrl}
							x={markupItem.coords.x - (imagePatchSourceRect.x * imagePatchScaleX)}
							y={markupItem.coords.y - (imagePatchSourceRect.y * imagePatchScaleY)}
							width={this.state.markupSourceImageSize.width * imagePatchScaleX}
							height={this.state.markupSourceImageSize.height * imagePatchScaleY}
							clipPath={`url(#${imagePatchClipId})`}
							preserveAspectRatio="none"
							opacity={isDraft ? 0.92 : 1}
							pointerEvents="none"
						/>
					)}
					{isTextMarkup ? (
						<>
							<rect
								x={markupItem.coords.x}
								y={markupItem.coords.y}
								width={markupItem.coords.width}
								height={markupItem.coords.height}
								rx={Number(markupItem.style && markupItem.style.borderRadius) || 0}
								{...commonProps}
							/>
							{isTransparentMarkupColor(fill) && strokeWidth === 0 && (
								<rect
									x={markupItem.coords.x}
									y={markupItem.coords.y}
									width={markupItem.coords.width}
									height={markupItem.coords.height}
									rx={Number(markupItem.style && markupItem.style.borderRadius) || 0}
									fill="rgba(255,255,255,0.001)"
									stroke="none"
								/>
							)}
							{textLayout && (
								<g clipPath={`url(#${textClipId})`} pointerEvents="none">
									{textLayout.lines.map((line, index) => (
										<text
											key={`${markupItem.guid || "draft"}-text-line-${index}`}
											x={textLayout.textX}
											y={textLayout.innerRect.y + (index * textLayout.lineHeight)}
											fill={textLayout.textColor}
											fontFamily={textLayout.fontFamily}
											fontSize={textLayout.fontSize}
											fontWeight={textLayout.fontWeight}
											fontStyle={textLayout.fontStyle}
											textAnchor={textLayout.textAnchor}
											dominantBaseline="hanging"
											xmlSpace="preserve"
										>
											{line || "\u00A0"}
										</text>
									))}
								</g>
							)}
						</>
					) : isImagePatchMarkup ? (
						<rect
							x={markupItem.coords.x}
							y={markupItem.coords.y}
							width={markupItem.coords.width}
							height={markupItem.coords.height}
							rx={Number(markupItem.style && markupItem.style.borderRadius) || 0}
							strokeWidth={strokeWidth}
							stroke={stroke}
							fill="transparent"
							strokeDasharray={strokeDasharray}
							opacity={isDraft ? 0.8 : 1}
						/>
					) : isSpotlightMarkup ? (
						<>
							{this.renderMarkupSvgShapeElement(markupItem.shape || "rectangle", markupItem.coords, {
								...commonProps,
								fill: "transparent"
							}, {
								borderRadius: Number(markupItem.style && markupItem.style.borderRadius) || 0
							})}
							{this.renderMarkupSvgShapeElement(markupItem.shape || "rectangle", markupItem.coords, {
								...getMarkupSpotlightHitTargetStyle(markupItem),
								opacity: 1,
								pointerEvents: "auto"
							}, {
								borderRadius: Number(markupItem.style && markupItem.style.borderRadius) || 0
							})}
						</>
					) : (
						this.renderMarkupSvgShapeElement(markupItem.shape || "rectangle", markupItem.coords, commonProps, {
							borderRadius: Number(markupItem.style && markupItem.style.borderRadius) || 0
						})
					)}
					{this.state.markupShowItemIds && !isDraft && (
						<g pointerEvents="none">
							<rect
								x={markupItem.coords.x + 4}
								y={markupItem.coords.y + markupItem.coords.height - 26}
								width={Math.max(((markupItem.id || "").length * 9) + 10, 54)}
								height={22}
								rx={6}
								fill="rgba(0,0,0,0.88)"
							/>
							<text
								x={markupItem.coords.x + 10}
								y={markupItem.coords.y + markupItem.coords.height - 10}
								fill="#7CFC00"
								fontSize="16"
								fontWeight="bold"
							>
								{markupItem.id}
							</text>
						</g>
					)}
				</g>
				{isSelected && !isDraft && (
					<>
						<line
							x1={rotatedTopCenter.x}
							y1={rotatedTopCenter.y}
							x2={rotatedHandlePoint.x}
							y2={rotatedHandlePoint.y}
							stroke="#2563eb"
							strokeWidth={actionStrokeWidth}
							strokeDasharray={`${connectorDashSize},${connectorDashSize}`}
							pointerEvents="none"
						/>
						<circle
							cx={rotatedHandlePoint.x}
							cy={rotatedHandlePoint.y}
							r={actionCircleRadius}
							fill="#ffffff"
							stroke="#2563eb"
							strokeWidth={actionStrokeWidth}
							onMouseDown={(event) => {
								this.startMarkupRotateDrag(markupItem, event);
							}}
						/>
						<text
							x={rotatedHandlePoint.x}
							y={rotatedHandlePoint.y}
							fill="#2563eb"
							fontSize={actionFontSize}
							fontWeight="bold"
							textAnchor="middle"
							dominantBaseline="central"
							pointerEvents="none"
						>
							↻
						</text>
						{resizeHandles.map((handle) => (
							<rect
								key={`${markupItem.guid}-${handle.id}`}
								x={handle.point.x - (resizeHandleSize / 2)}
								y={handle.point.y - (resizeHandleSize / 2)}
								width={resizeHandleSize}
								height={resizeHandleSize}
								rx={resizeHandleRadius}
								fill="#ffffff"
								stroke="#2563eb"
								strokeWidth={actionStrokeWidth}
								style={{ cursor: handle.cursor }}
								onMouseDown={(event) => {
									this.startMarkupResizeDrag(markupItem, handle.id, event);
								}}
							/>
						))}
						<circle
							cx={deleteX}
							cy={deleteY}
							r={actionCircleRadius}
							fill="#dc2626"
							stroke="#ffffff"
							strokeWidth={deleteStrokeWidth}
							onMouseDown={(event) => {
								event.preventDefault();
								event.stopPropagation();
							}}
							onMouseUp={(event) => {
								event.preventDefault();
								event.stopPropagation();
							}}
							onClick={(event) => {
								event.preventDefault();
								event.stopPropagation();
								this.handleDeleteMarkupItem(markupItem.guid);
							}}
						/>
						<text
							x={deleteX}
							y={deleteY}
							fill="#ffffff"
							fontSize={actionFontSize}
							fontWeight="bold"
							textAnchor="middle"
							dominantBaseline="central"
							pointerEvents="none"
						>
							×
						</text>
					</>
				)}
			</g>
		);
	}

	drawRoundedRect(context, x, y, width, height, radius = 0) {
		const safeRadius = Math.max(0, Math.min(Number(radius) || 0, Math.min(width, height) / 2));
		context.beginPath();
		context.moveTo(x + safeRadius, y);
		context.lineTo(x + width - safeRadius, y);
		context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
		context.lineTo(x + width, y + height - safeRadius);
		context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
		context.lineTo(x + safeRadius, y + height);
		context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
		context.lineTo(x, y + safeRadius);
		context.quadraticCurveTo(x, y, x + safeRadius, y);
		context.closePath();
	}

	drawArrowPath(context, x, y, width, height) {
		context.beginPath();
		context.moveTo(x, y + (height * 0.33));
		context.lineTo(x + (width * 0.5), y + (height * 0.33));
		context.lineTo(x + (width * 0.5), y);
		context.lineTo(x + width, y + (height * 0.5));
		context.lineTo(x + (width * 0.5), y + height);
		context.lineTo(x + (width * 0.5), y + (height * 0.67));
		context.lineTo(x, y + (height * 0.67));
		context.closePath();
	}

	drawDiamondPath(context, x, y, width, height) {
		context.beginPath();
		context.moveTo(x + (width * 0.5), y);
		context.lineTo(x + width, y + (height * 0.5));
		context.lineTo(x + (width * 0.5), y + height);
		context.lineTo(x, y + (height * 0.5));
		context.closePath();
	}

	drawMarkupShapePath(context, shape = "rectangle", x = 0, y = 0, width = 0, height = 0, borderRadius = 0) {
		const normalizedShape = `${shape || "rectangle"}`.trim().toLowerCase();
		if (normalizedShape === "ellipse") {
			context.beginPath();
			context.ellipse(x + (width * 0.5), y + (height * 0.5), Math.abs(width) * 0.5, Math.abs(height) * 0.5, 0, 0, Math.PI * 2);
			return;
		}
		if (normalizedShape === "diamond") {
			this.drawDiamondPath(context, x, y, width, height);
			return;
		}
		if (normalizedShape === "arrow") {
			this.drawArrowPath(context, x, y, width, height);
			return;
		}
		if (normalizedShape === "line") {
			context.beginPath();
			context.moveTo(x, y);
			context.lineTo(x + width, y + height);
			return;
		}

		this.drawRoundedRect(context, x, y, width, height, borderRadius);
	}

	drawMarkupSpotlightLayerToCanvas(context, markupItems = [], sourceImage = null, canvasWidth = 1, canvasHeight = 1) {
		const spotlightItems = (Array.isArray(markupItems) ? markupItems : [])
			.filter((item) => item && item.coords && item.visible !== false && isSpotlightMarkupComponent(item.type || "highlight"));
		if (!spotlightItems.length) {
			return;
		}

		const spotlight = getMarkupSpotlightLayerSettings(spotlightItems);
		const overlayCanvas = document.createElement("canvas");
		overlayCanvas.width = Math.max(Math.round(canvasWidth) || 1, 1);
		overlayCanvas.height = Math.max(Math.round(canvasHeight) || 1, 1);
		const overlayContext = overlayCanvas.getContext("2d");

		if (!overlayContext) {
			return;
		}

		const blur = normalizeMarkupSpotlightBlur(spotlight.blur);
		if (blur > 0 && sourceImage) {
			overlayContext.save();
			overlayContext.filter = `blur(${blur}px)`;
			overlayContext.drawImage(sourceImage, 0, 0, overlayCanvas.width, overlayCanvas.height);
			overlayContext.filter = "none";
			overlayContext.restore();
		}

		overlayContext.save();
		overlayContext.globalAlpha = normalizeMarkupSpotlightOpacity(spotlight.opacity);
		overlayContext.fillStyle = spotlight.backgroundColor || MARKUP_SPOTLIGHT_DEFAULTS.backgroundColor;
		overlayContext.fillRect(0, 0, overlayCanvas.width, overlayCanvas.height);
		overlayContext.restore();

		overlayContext.save();
		overlayContext.globalCompositeOperation = "destination-out";
		spotlightItems.forEach((item) => {
			const coords = item.coords || {};
			const width = Number(coords.width) || 0;
			const height = Number(coords.height) || 0;
			if (!width || !height) {
				return;
			}
			const centerX = (Number(coords.x) || 0) + (width / 2);
			const centerY = (Number(coords.y) || 0) + (height / 2);
			const borderRadius = Number(item.style && item.style.borderRadius) || 0;
			const shape = `${item.shape || "rectangle"}`.trim().toLowerCase();
			const cutoutStrokeWidth = getMarkupSpotlightCutoutStrokeWidth(item);

			overlayContext.save();
			overlayContext.translate(centerX, centerY);
			overlayContext.rotate((normalizeMarkupRotation(item.rotation || 0) * Math.PI) / 180);
			this.drawMarkupShapePath(overlayContext, shape, -(width / 2), -(height / 2), width, height, borderRadius);
			overlayContext.fillStyle = "rgba(0,0,0,1)";
			overlayContext.strokeStyle = "rgba(0,0,0,1)";
			overlayContext.lineCap = "round";
			overlayContext.lineJoin = "round";
			overlayContext.lineWidth = cutoutStrokeWidth;
			if (shape === "line") {
				overlayContext.stroke();
			} else {
				overlayContext.fill();
				overlayContext.stroke();
			}
			overlayContext.restore();
		});
		overlayContext.restore();

		context.drawImage(overlayCanvas, 0, 0, canvasWidth, canvasHeight);
	}

	drawMarkupTextToCanvas(context, markupItem, strokeWidth, fill) {
		if (!markupItem || !markupItem.coords) {
			return;
		}

		const coords = markupItem.coords;
		const textLayout = this.getMarkupTextLayout(markupItem);
		const padding = this.getMarkupTextPadding(markupItem);
		const localInnerX = -(coords.width / 2) + padding.x;
		const localInnerY = -(coords.height / 2) + padding.y;
		const localInnerWidth = Math.max(coords.width - (padding.x * 2), 1);
		const localTextX = textLayout.textAlign === "center"
			? localInnerX + (localInnerWidth / 2)
			: (textLayout.textAlign === "right" ? localInnerX + localInnerWidth : localInnerX);

		this.drawRoundedRect(
			context,
			-(coords.width / 2),
			-(coords.height / 2),
			coords.width,
			coords.height,
			Number(markupItem.style && markupItem.style.borderRadius) || 0
		);
		if (fill !== "transparent") {
			context.fill();
		}
		if (strokeWidth > 0) {
			context.stroke();
		}

		context.save();
		this.drawRoundedRect(
			context,
			-(coords.width / 2),
			-(coords.height / 2),
			coords.width,
			coords.height,
			Number(markupItem.style && markupItem.style.borderRadius) || 0
		);
		context.clip();
		context.fillStyle = textLayout.textColor;
		context.font = this.buildCanvasFontString(textLayout);
		context.textBaseline = "top";
		context.textAlign = textLayout.textAlign;
		textLayout.lines.forEach((line, index) => {
			context.fillText(line || " ", localTextX, localInnerY + (index * textLayout.lineHeight));
		});
		context.restore();
	}

	drawMarkupItemToCanvas(context, markupItem, sourceImage, canvasWidth = 1, canvasHeight = 1, embeddedPatchImages = new Map()) {
		if (!markupItem || !markupItem.coords) {
			return;
		}
		if (markupItem.visible === false) {
			return;
		}

		const { strokeWidth, stroke, strokeDasharray } = this.getMarkupStrokeConfig(markupItem, { showPlaceholderStroke: false });
		const fill = markupItem.style && markupItem.style.borderFillColor ? markupItem.style.borderFillColor : "transparent";
		const blurPx = clampValue(Number(markupItem.style && markupItem.style.blurPx) || 0, 0, MAX_BLUR_PX);
		const isBlurRedact = isBlurMarkupComponent(markupItem.type || "highlight") && blurPx > 0 && sourceImage;
		const isTextMarkup = isTextMarkupComponent(markupItem.type || "highlight");
		const isImagePatchMarkup = isImagePatchMarkupComponent(markupItem.type || "highlight");
		const isSpotlightMarkup = isSpotlightMarkupComponent(markupItem.type || "highlight");
		const imagePatchEmbeddedSourceUrl = isImagePatchMarkup ? this.getMarkupImagePatchEmbeddedSourceUrl(markupItem) : "";
		const imagePatchEmbeddedImage = imagePatchEmbeddedSourceUrl ? embeddedPatchImages.get(imagePatchEmbeddedSourceUrl) : null;
		const imagePatchSourceRect = isImagePatchMarkup ? this.getMarkupImagePatchSourceRect(markupItem) : null;
		const rotation = normalizeMarkupRotation(markupItem.rotation || 0);
		const centerX = markupItem.coords.x + (markupItem.coords.width / 2);
		const centerY = markupItem.coords.y + (markupItem.coords.height / 2);

		if (isBlurRedact) {
			context.save();
			context.translate(centerX, centerY);
			context.rotate((rotation * Math.PI) / 180);
			this.drawMarkupShapePath(
				context,
				markupItem.shape || "rectangle",
				-(markupItem.coords.width / 2),
				-(markupItem.coords.height / 2),
				markupItem.coords.width,
				markupItem.coords.height,
				Number(markupItem.style && markupItem.style.borderRadius) || 0
			);
			context.clip();
			context.filter = `blur(${blurPx}px)`;
			context.drawImage(sourceImage, -centerX, -centerY, canvasWidth, canvasHeight);
			context.filter = "none";
			context.restore();
		}

		if (isImagePatchMarkup && imagePatchEmbeddedImage) {
			context.save();
			context.translate(centerX, centerY);
			context.rotate((rotation * Math.PI) / 180);
			this.drawRoundedRect(
				context,
				-(markupItem.coords.width / 2),
				-(markupItem.coords.height / 2),
				markupItem.coords.width,
				markupItem.coords.height,
				Number(markupItem.style && markupItem.style.borderRadius) || 0
			);
			context.clip();
			context.drawImage(
				imagePatchEmbeddedImage,
				-(markupItem.coords.width / 2),
				-(markupItem.coords.height / 2),
				markupItem.coords.width,
				markupItem.coords.height
			);
			context.restore();
		}

		if (isImagePatchMarkup && !imagePatchEmbeddedImage && imagePatchSourceRect && sourceImage) {
			context.save();
			context.translate(centerX, centerY);
			context.rotate((rotation * Math.PI) / 180);
			this.drawRoundedRect(
				context,
				-(markupItem.coords.width / 2),
				-(markupItem.coords.height / 2),
				markupItem.coords.width,
				markupItem.coords.height,
				Number(markupItem.style && markupItem.style.borderRadius) || 0
			);
			context.clip();
			context.drawImage(
				sourceImage,
				imagePatchSourceRect.x,
				imagePatchSourceRect.y,
				imagePatchSourceRect.width,
				imagePatchSourceRect.height,
				-(markupItem.coords.width / 2),
				-(markupItem.coords.height / 2),
				markupItem.coords.width,
				markupItem.coords.height
			);
			context.restore();
		}

		context.save();
		context.lineWidth = strokeWidth;
		context.strokeStyle = stroke;
		context.fillStyle = fill;
		context.setLineDash(strokeDasharray ? strokeDasharray.split(",").map((value) => Number(value) || 0) : []);
		context.translate(centerX, centerY);
		context.rotate((rotation * Math.PI) / 180);
		context.lineCap = markupItem.shape === "line" ? "round" : "butt";
		context.lineJoin = "round";
		if (isTextMarkup) {
			this.drawMarkupTextToCanvas(context, markupItem, strokeWidth, fill);
		} else {
			this.drawMarkupShapePath(
				context,
				markupItem.shape || "rectangle",
				-(markupItem.coords.width / 2),
				-(markupItem.coords.height / 2),
				markupItem.coords.width,
				markupItem.coords.height,
				Number(markupItem.style && markupItem.style.borderRadius) || 0
			);
		}

		if (!isTextMarkup && !isImagePatchMarkup) {
			if (!isSpotlightMarkup && !isBlurRedact && markupShapeSupportsFill(markupItem.shape || "rectangle") && fill !== "transparent") {
				context.fill();
			}
			if (!isSpotlightMarkup && isBlurRedact && markupShapeSupportsFill(markupItem.shape || "rectangle") && fill !== "transparent") {
				context.fill();
			}
			if (strokeWidth > 0) {
				context.stroke();
			}
		}
		if (isImagePatchMarkup && strokeWidth > 0) {
			context.stroke();
		}
		context.restore();
	}

	async loadMarkupEmbeddedPatchImages(markupItems = []) {
		const embeddedSourceUrls = buildUniqueStringList(markupItems.map((item) => (
			isImagePatchMarkupComponent(item && item.type ? item.type : "")
				? this.getMarkupImagePatchEmbeddedSourceUrl(item)
				: ""
		)));
		if (!embeddedSourceUrls.length) {
			return new Map();
		}

		const loadedEntries = await Promise.all(embeddedSourceUrls.map(async (sourceUrl) => (
			[sourceUrl, await this.loadImageElement(sourceUrl, "A pasted image could not be loaded for export.")]
		)));
		return new Map(loadedEntries);
	}

	async buildMarkupCompositeDataUrl() {
		await this.ensureMarkupFontsReady(this.state.markupItems);
		const image = await this.loadImageElement(this.state.markupSourceUrl, "The markup image could not be loaded.");
		const embeddedPatchImages = await this.loadMarkupEmbeddedPatchImages(this.state.markupItems);

		const canvas = document.createElement("canvas");
		canvas.width = this.state.markupSourceImageSize ? this.state.markupSourceImageSize.width : (image.naturalWidth || image.width || 1);
		canvas.height = this.state.markupSourceImageSize ? this.state.markupSourceImageSize.height : (image.naturalHeight || image.height || 1);
		const context = canvas.getContext("2d");
		context.clearRect(0, 0, canvas.width, canvas.height);
		context.drawImage(image, 0, 0, canvas.width, canvas.height);
		this.drawMarkupSpotlightLayerToCanvas(context, this.state.markupItems, image, canvas.width, canvas.height);
		this.state.markupItems.forEach((item) => this.drawMarkupItemToCanvas(context, item, image, canvas.width, canvas.height, embeddedPatchImages));
		return canvas.toDataURL("image/png");
	}

	async handleApplyInlineMarkup() {
		try {
			const resultDataUrl = await this.buildMarkupCompositeDataUrl();
			this.handleApplyMarkupComposite(resultDataUrl);
		} catch (err) {
			this.setState({
				error: err && err.message ? err.message : `${err}`
			});
		}
	}

	async handleSaveImage() {
		const imageName = `${this.state.imageName || ""}`.trim();
		if (!imageName) {
			this.setState({
				error: "Specify an image name before saving."
			});
			return;
		}
		if (!this.state.previewExportSafe) {
			this.setState({
				error: "This image preview is not yet canvas-safe, so it cannot be exported. Wait for the secure preview source to load."
			});
			return;
		}

		this.setState({
			saving: true,
			error: ""
		});

		try {
			const outputCanvas = this.createOutputCanvas();
			const mimeType = getMimeTypeForImageOutputFormat(this.state.outputFormat);
			const blob = await this.canvasToBlob(outputCanvas, mimeType);
			if (this.props.onCreate) {
				await Promise.resolve(this.props.onCreate({
					blob,
					imageName: this.state.overwriteImage ? imageName : normalizeImageFileName(imageName, this.state.outputFormat),
					imageFormat: this.state.outputFormat,
					mimeType,
					autoLink: this.state.autoLinkImage,
					overwriteImage: this.state.overwriteImage,
					overwriteTargetPath: this.props.overwriteTargetPath || ""
				}));
			}
		} catch (err) {
			const message = err && err.message ? err.message : `${err}`;
			this.setState({
				error: `Image save failed: ${message}`
			});
		} finally {
			this.setState({
				saving: false
			});
		}
	}

	render() {
		const {
			open,
			onClose,
			imageLabel,
			autoLinkLabel,
			overwriteTargetPath
		} = this.props;
		const {
			autoLinkImage,
			blurFillBackground,
			blurFillPx,
			blurPx,
			cropViewportHeight,
			cropViewportWidth,
			cropPreset,
			cropZoom,
			error,
			freeCropHeightPct,
			freeCropWidthPct,
			imageName,
			isDraggingCrop,
			outputFormat,
			overwriteImage,
			padTargetHeight,
			padTargetWidth,
			padToTargetSize,
			showMarkupModal,
			markupBorderColor,
			markupBorderRadius,
			markupBorderWidth,
			markupBlurPx,
			markupColorPickTarget,
			markupComponentType,
			markupFillColor,
			markupFillEnabled,
			markupFontLoadError,
			markupFontLoadMessage,
			markupFontLoading,
			markupFontFamily,
			markupFontSize,
			markupFontSourceUrl,
			markupFontStyle,
			markupFontWeight,
			markupImagePatchBgColor,
			markupImagePatchBgFeather,
			markupImagePatchBgTolerance,
			markupImagePatchProcessing,
			markupItems,
			markupLayoutMode,
			markupLeftPanelCollapsed,
			markupMouseX,
			markupMouseY,
			markupImagePatchBgPickMode,
			markupPointerInCanvas,
			markupPreviewScale,
			markupRightPanelCollapsed,
			markupRotation,
			markupShape,
			markupShowItemIds,
			markupSpotlightBackgroundColor,
			markupSpotlightBlurPx,
			markupSpotlightOpacity,
			markupSourceUrl,
			markupSourceImageSize,
			markupCustomFontFamilies,
			markupTextAlign,
			markupTextColor,
			markupTextValue,
			padAspectRatio,
			paddingBottomPx,
			paddingLeftPx,
			paddingMode,
			paddingPx,
			paddingRightPx,
			paddingTopPx,
			previewImageLoading,
			previewImageUrl,
			previewExportSafe,
			resizeEnabled,
			resizeHeight,
			resizeLockAspect,
			resizeWidth,
			saving,
			sourceReady
		} = this.state;

		if (!open) {
			return null;
		}

		const compactLayout = typeof window !== "undefined" && window.innerWidth < 1200;
		const colorPopoverContainer = typeof document !== "undefined" ? document.body : undefined;
		const colorPopoverStyle = { zIndex: 210000 };
		const viewportHeight = typeof window !== "undefined" ? window.innerHeight || 900 : 900;
		const overlayStyle = {
			position: "fixed",
			inset: 0,
			backgroundColor: "rgba(15, 23, 42, 0.45)",
			zIndex: 200000,
			padding: 0,
			margin: 0,
			display: "block",
			overflow: "hidden"
		};
		const dialogStyle = {
			position: "absolute",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			width: "100%",
			height: "100%",
			maxWidth: "none",
			maxHeight: "none",
			margin: 0,
			backgroundColor: "#ffffff",
			borderRadius: 0,
			boxSizing: "border-box",
			display: "flex",
			flexDirection: "column",
			overflow: "hidden"
		};
		const headerStyle = {
			padding: "22px 28px 10px 28px",
			borderBottom: "1px solid #e5e7eb",
			display: "flex",
			alignItems: "flex-start",
			justifyContent: "space-between",
			gap: "16px"
		};
		const bodyStyle = {
			flex: "1 1 auto",
			minHeight: 0,
			overflow: "auto",
			padding: "18px 28px 12px 28px",
			display: "flex",
			flexDirection: "column",
			gap: "16px"
		};
		const markupBodyStyle = {
			flex: "1 1 auto",
			minHeight: 0,
			overflow: "hidden",
			padding: "10px 14px 8px 14px",
			display: "flex",
			flexDirection: "column",
			gap: "10px"
		};
		const footerStyle = {
			flex: "0 0 auto",
			borderTop: "1px solid #e5e7eb",
			padding: "14px 28px 18px 28px",
			display: "flex",
			justifyContent: "space-between",
			gap: "12px",
			flexWrap: "wrap",
			backgroundColor: "#ffffff"
		};
		const savingOverlayStyle = {
			position: "absolute",
			inset: 0,
			background: "rgba(255, 255, 255, 0.7)",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			zIndex: 2,
			backdropFilter: "blur(1px)"
		};
		const savingPanelStyle = {
			display: "flex",
			alignItems: "center",
			gap: "10px",
			padding: "12px 16px",
			borderRadius: "12px",
			background: "#ffffff",
			boxShadow: "0 10px 28px rgba(15, 23, 42, 0.14)",
			color: "#111827",
			fontWeight: 600
		};
		const previewPaneHeight = compactLayout
			? Math.min(Math.max(viewportHeight - 560, 260), 520)
			: Math.min(Math.max(viewportHeight - 470, 340), 620);
		const markupEditorHeight = compactLayout
			? Math.min(Math.max(viewportHeight - 260, 420), 760)
			: Math.min(Math.max(viewportHeight - 220, 520), 900);
		const toolRailStyle = {
			display: "flex",
			flexDirection: "column",
			gap: "16px",
			minWidth: 0,
			padding: compactLayout ? 0 : "18px",
			border: compactLayout ? "none" : "1px solid #e5e7eb",
			borderRadius: compactLayout ? 0 : "16px",
			background: compactLayout ? "transparent" : "#fbfcfe"
		};
		const markupSidePanelBaseStyle = {
			...toolRailStyle,
			minHeight: 0,
			height: compactLayout ? `${markupEditorHeight}px` : "100%",
			maxHeight: compactLayout ? `${markupEditorHeight}px` : "100%",
			overflowX: "hidden",
			overflowY: "auto",
			padding: compactLayout ? "0 0 24px 0" : toolRailStyle.padding,
			boxSizing: "border-box"
		};
		const markupCanvasPaneStyle = {
			border: "1px solid #dbe2ea",
			borderRadius: "16px",
			background: "#f8fafc",
			minHeight: 0,
			height: compactLayout ? `${markupEditorHeight}px` : "100%",
			maxHeight: compactLayout ? `${markupEditorHeight}px` : "100%",
			overflowX: "auto",
			overflowY: "auto",
			padding: "20px",
			boxSizing: "border-box"
		};
		const markupCanvasPopoutPaneStyle = {
			border: "1px solid #dbe2ea",
			borderRadius: "16px",
			background: "#f8fafc",
			minHeight: 0,
			minWidth: 0,
			width: "100%",
			height: "100%",
			maxHeight: "100%",
			overflowX: "auto",
			overflowY: "auto",
			padding: "20px",
			boxSizing: "border-box",
			flex: "1 1 auto"
		};
		const markupToolbarRowStyle = {
			display: "flex",
			justifyContent: "flex-start",
			alignItems: "flex-end",
			gap: "12px",
			flexWrap: "nowrap",
			minWidth: "max-content"
		};
		const markupToolbarSectionStyle = {
			display: "flex",
			flexDirection: "row",
			alignItems: "flex-end",
			justifyContent: "flex-start",
			flexWrap: "nowrap",
			gap: "8px"
		};
		const markupToolbarButtonSectionStyle = {
			display: "flex",
			flexDirection: "row",
			alignItems: "center",
			justifyContent: "flex-start",
			flexWrap: "nowrap",
			gap: "6px",
			paddingBottom: "1px"
		};
		const markupToolbarSectionDividerStyle = {
			width: "1px",
			height: "34px",
			alignSelf: "center",
			background: "rgba(107,114,128,0.6)",
			flex: "0 0 auto"
		};
		const markupToolbarCoordsSectionStyle = {
			display: "flex",
			flexDirection: "row",
			gap: "8px",
			alignItems: "center",
			paddingBottom: "1px"
		};
		const markupItemListPanelStyle = {
			...markupSidePanelBaseStyle
		};
		const markupPropertiesPanelStyle = {
			...markupSidePanelBaseStyle
		};
		const markupStackedRightPanelStyle = {
			...markupSidePanelBaseStyle,
			overflowY: "hidden",
			padding: compactLayout ? "0 0 24px 0" : "14px"
		};
		const markupStackedRightSectionStyle = {
			flex: "1 1 0",
			minHeight: 0,
			overflowX: "hidden",
			overflowY: "auto",
			padding: "14px",
			boxSizing: "border-box",
			border: "1px solid #e5e7eb",
			borderRadius: "12px",
			background: "#ffffff"
		};
		const editorGridStyle = {
			display: "grid",
			gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 3fr) minmax(360px, 1fr)",
			gap: "24px",
			alignItems: "start",
			minWidth: 0
		};
		const toolSectionStyle = {
			display: "flex",
			flexDirection: "column",
			gap: "12px",
			minWidth: 0
		};
		const checkboxLabelStyle = {
			display: "flex",
			alignItems: "center",
			gap: "10px",
			margin: 0,
			color: "#374151",
			fontWeight: 500
		};
		const checkboxHelpStyle = {
			color: "#6b7280",
			cursor: "help",
			fontSize: "14px"
		};
		const cropMetrics = this.getCropMetrics();
		const outputDimensions = sourceReady ? this.getOutputContentDimensions({
			width: this.getCurrentCropSourceDimensions(cropMetrics).width,
			height: this.getCurrentCropSourceDimensions(cropMetrics).height
		}) : { width: 0, height: 0 };
		const outputFrameConfig = sourceReady ? this.getOutputFrameConfig(outputDimensions) : { width: 1, height: 1, offsetX: 0, offsetY: 0, mode: "uniform" };
		const outputFrameWidth = Math.max(outputFrameConfig.width || 1, 1);
		const outputFrameHeight = Math.max(outputFrameConfig.height || 1, 1);
		const padAspectRatioValue = `${padAspectRatio || "custom"}`;
		const padAspectRatioIsCustom = padAspectRatioValue === "custom";
		const paddingModeValue = paddingMode === "custom" ? "custom" : "uniform";
		const displayedPadTargetWidth = padToTargetSize && !padAspectRatioIsCustom ? `${outputFrameWidth || ""}` : padTargetWidth;
		const displayedPadTargetHeight = padToTargetSize && !padAspectRatioIsCustom ? `${outputFrameHeight || ""}` : padTargetHeight;
		const customPaddingHasValue = [paddingTopPx, paddingRightPx, paddingBottomPx, paddingLeftPx]
			.some((value) => (parseInt(value, 10) || 0) > 0);
		const customPaddingSummary = customPaddingHasValue
			? ` plus top ${paddingTopPx || 0}px, right ${paddingRightPx || 0}px, bottom ${paddingBottomPx || 0}px, left ${paddingLeftPx || 0}px padding`
			: "";
		const outputFrameDescription = padToTargetSize
			? (padAspectRatioIsCustom
				? " with the current image centered inside the final frame"
				: ` framed to ${padAspectRatioValue} around the current image`)
			: (paddingModeValue === "custom"
				? customPaddingSummary
				: (paddingPx > 0 ? ` plus ${paddingPx}px transparent padding on each side` : ""));
		let previewFrameMetrics = null;
		let previewBlurFillMetrics = null;
		if (cropMetrics && cropViewportWidth && cropViewportHeight && outputDimensions.width && outputDimensions.height) {
			const fitScale = Math.min(
				cropViewportWidth / outputFrameWidth,
				cropViewportHeight / outputFrameHeight
			);
			const displayScale = Number.isFinite(fitScale) && fitScale > 0 ? fitScale : 1;
			const outerDisplayWidth = Math.max(1, outputFrameWidth * displayScale);
			const outerDisplayHeight = Math.max(1, outputFrameHeight * displayScale);
			const contentDisplayWidth = Math.max(1, outputDimensions.width * displayScale);
			const contentDisplayHeight = Math.max(1, outputDimensions.height * displayScale);
			const contentLeft = Math.max((outputFrameConfig.offsetX || 0) * displayScale, 0);
			const contentTop = Math.max((outputFrameConfig.offsetY || 0) * displayScale, 0);
			const contentScaleX = contentDisplayWidth / cropMetrics.viewportWidth;
			const contentScaleY = contentDisplayHeight / cropMetrics.viewportHeight;

			previewFrameMetrics = {
				outerDisplayWidth,
				outerDisplayHeight,
				contentDisplayWidth,
				contentDisplayHeight,
				contentLeft,
				contentTop,
				imageLeft: cropMetrics.imageLeft * contentScaleX,
				imageTop: cropMetrics.imageTop * contentScaleY,
				imageWidth: cropMetrics.displayWidth * contentScaleX,
				imageHeight: cropMetrics.displayHeight * contentScaleY
			};

			const blurFillCoverRect = getCoverDrawRect(
				previewFrameMetrics.contentDisplayWidth,
				previewFrameMetrics.contentDisplayHeight,
				previewFrameMetrics.outerDisplayWidth,
				previewFrameMetrics.outerDisplayHeight,
				DEFAULT_BLUR_FILL_SCALE
			);
			const blurFillScale = blurFillCoverRect.width / previewFrameMetrics.contentDisplayWidth;
			previewBlurFillMetrics = {
				...blurFillCoverRect,
				imageLeft: previewFrameMetrics.imageLeft * blurFillScale,
				imageTop: previewFrameMetrics.imageTop * blurFillScale,
				imageWidth: previewFrameMetrics.imageWidth * blurFillScale,
				imageHeight: previewFrameMetrics.imageHeight * blurFillScale
			};
		}

		if (showMarkupModal && markupSourceUrl) {
			const selectedMarkupItem = this.getSelectedMarkupItem();
			const selectedMarkupIndex = selectedMarkupItem ? this.getMarkupItemIndexByGuid(selectedMarkupItem.guid, markupItems) : -1;
			const selectedMarkupLayerNumber = selectedMarkupIndex >= 0 ? markupItems.length - selectedMarkupIndex : 0;
			const selectedMarkupVisible = selectedMarkupItem ? selectedMarkupItem.visible !== false : true;
			const selectedMarkupIsText = isTextMarkupComponent(selectedMarkupItem && selectedMarkupItem.type ? selectedMarkupItem.type : "");
			const selectedMarkupIsImagePatch = isImagePatchMarkupComponent(selectedMarkupItem && selectedMarkupItem.type ? selectedMarkupItem.type : "");
			const selectedMarkupImagePatchStoredSourceRect = selectedMarkupIsImagePatch ? this.getMarkupImagePatchStoredSourceRect(selectedMarkupItem) : null;
			const selectedMarkupImagePatchEmbeddedSourceUrl = selectedMarkupIsImagePatch ? this.getMarkupImagePatchEmbeddedSourceUrl(selectedMarkupItem) : "";
			const selectedMarkupImagePatchEmbeddedSourceSize = selectedMarkupIsImagePatch ? this.getMarkupImagePatchEmbeddedSourceSize(selectedMarkupItem) : null;
			const selectedMarkupImagePatchOriginalEmbeddedSourceUrl = selectedMarkupIsImagePatch ? this.getMarkupImagePatchOriginalEmbeddedSourceUrl(selectedMarkupItem) : "";
			const selectedMarkupImagePatchCanReset = selectedMarkupIsImagePatch && (
				(!!selectedMarkupImagePatchStoredSourceRect && !!selectedMarkupImagePatchEmbeddedSourceUrl)
				|| (
					!selectedMarkupImagePatchStoredSourceRect
					&& !!selectedMarkupImagePatchEmbeddedSourceUrl
					&& !!selectedMarkupImagePatchOriginalEmbeddedSourceUrl
					&& selectedMarkupImagePatchEmbeddedSourceUrl !== selectedMarkupImagePatchOriginalEmbeddedSourceUrl
				)
			);
			const markupDraftItem = this.getMarkupTempItem();
			const markupLayerItems = [...markupItems].reverse();
			const markupPreviewScaleRatio = markupPreviewScale / 100;
			const markupCanvasWidth = markupSourceImageSize ? markupSourceImageSize.width : 1;
			const markupCanvasHeight = markupSourceImageSize ? markupSourceImageSize.height : 1;
			const markupFontFamilyOptions = this.getMarkupFontFamilyOptions();
			const markupTextFontSizeOptions = MARKUP_TEXT_FONT_SIZE_OPTIONS.map((option) => (
				<option key={`markup-text-font-size-${option}`} value={option}>{`${option}px`}</option>
			));
			const markupBorderWidthOptions = MARKUP_BORDER_WIDTH_OPTIONS.map((option) => (
				<option key={`markup-border-width-${option}`} value={option}>{option}</option>
			));
			const markupBorderRadiusOptions = MARKUP_BORDER_RADIUS_OPTIONS.map((option) => (
				<option key={`markup-border-radius-${option}`} value={option}>{option}</option>
			));
			const markupBlurIntensityOptions = MARKUP_BLUR_INTENSITY_OPTIONS.map((option) => (
				<option key={`markup-blur-intensity-${option}`} value={option}>{`${option}px`}</option>
			));
			const markupSpotlightBlurOptions = MARKUP_SPOTLIGHT_BLUR_OPTIONS.map((option) => (
				<option key={`markup-spotlight-blur-${option}`} value={option}>{`${option}px`}</option>
			));
			const markupShapeOptions = getMarkupShapeOptions(markupComponentType);
			const blurControlsVisible = isBlurMarkupComponent(markupComponentType);
			const spotlightControlsVisible = isSpotlightMarkupComponent(markupComponentType);
			const imagePatchControlsVisible = isImagePatchMarkupComponent(markupComponentType) || selectedMarkupIsImagePatch;
			const fillControlsVisible = !isImagePatchMarkupComponent(markupComponentType)
				&& !isSpotlightMarkupComponent(markupComponentType)
				&& markupShapeSupportsFill(markupShape);
			const textControlsVisible = isTextMarkupComponent(markupComponentType) || selectedMarkupIsText;
			const fillToggleLabel = isTextMarkupComponent(markupComponentType) ? "Background" : "Fill";
			const fillColorLabel = isTextMarkupComponent(markupComponentType) ? "Background Color" : "Fill Color";
			const selectedTextStyle = selectedMarkupIsText ? this.getMarkupTextStyle(selectedMarkupItem) : null;
			const textControlsSubtitle = selectedMarkupIsText
				? `Editing ${selectedMarkupItem.id}`
				: "These settings apply to the next text box you draw.";
			const colorPickActiveLabel = markupColorPickTarget
				? this.getMarkupColorPickTargetLabel(markupColorPickTarget, markupComponentType)
				: "";
			const imagePatchBgPickActiveLabel = markupImagePatchBgPickMode ? "Patch Background Color" : "";
			const markupImageCopyMode = !!this.state.markupImageCopyMode;
			const markupClipboardItem = this.state.markupClipboardItem;
			const markupClipboardContainsImageData = !!(markupClipboardItem && isImagePatchMarkupComponent(markupClipboardItem.type || ""));
			const markupClipboardReadyLabel = markupClipboardItem ? getMarkupItemDescriptor(markupClipboardItem) : "";
			const localFontDiscoverySupported = typeof window !== "undefined" && typeof window.queryLocalFonts === "function";
			const selectedMarkupCanMoveForward = selectedMarkupIndex >= 0 && selectedMarkupIndex < (markupItems.length - 1);
			const selectedMarkupCanMoveBackward = selectedMarkupIndex > 0;
			const markupLayerButtonStyle = {
				minWidth: "0",
				padding: "2px 7px",
				fontSize: "11px",
				lineHeight: 1.2,
				color: "#64748b",
				borderColor: "#cbd5e1",
				background: "#f8fafc"
			};
			const markupVisibilityButtonStyle = {
				minWidth: "32px",
				padding: "2px 7px",
				fontSize: "11px",
				lineHeight: 1.2,
				color: "#475569",
				borderColor: "#cbd5e1",
				background: "#f8fafc"
			};
			const markupCardFooterStyle = (isSelected = false) => ({
				display: "flex",
				flexWrap: "wrap",
				gap: "4px",
				alignItems: "center",
				justifyContent: "flex-start",
				padding: "6px 10px",
				borderTop: "1px solid rgba(203, 213, 225, 0.8)",
				background: isSelected ? "rgba(219, 234, 254, 0.55)" : "rgba(248, 250, 252, 0.96)"
			});
			const markupSelectedFooterCardStyle = {
				display: "flex",
				flexDirection: "column",
				border: "1px solid #dbe2ea",
				borderRadius: "10px",
				overflow: "hidden",
				background: "#ffffff"
			};
			const resolvedMarkupLayoutMode = normalizeMarkupLayoutMode(markupLayoutMode);
			const markupIsStackedRightLayout = resolvedMarkupLayoutMode === MARKUP_LAYOUT_MODE_STACKED_RIGHT_SPLIT || resolvedMarkupLayoutMode === MARKUP_LAYOUT_MODE_STACKED_RIGHT_APPEND;
			const showMarkupLeftPanel = compactLayout
				? !markupLeftPanelCollapsed
				: resolvedMarkupLayoutMode === MARKUP_LAYOUT_MODE_SPLIT && !markupLeftPanelCollapsed;
			const showMarkupRightPanel = !markupRightPanelCollapsed;
			const markupDesktopGridColumns = [];
			if (!compactLayout) {
				if (resolvedMarkupLayoutMode === MARKUP_LAYOUT_MODE_SPLIT && showMarkupLeftPanel) {
					markupDesktopGridColumns.push("minmax(260px, 320px)");
				}
				markupDesktopGridColumns.push("minmax(0, 4fr)");
				if (showMarkupRightPanel) {
					markupDesktopGridColumns.push("minmax(320px, 1fr)");
				}
			}
			const markupCanvasDisplayWidth = markupCanvasWidth * markupPreviewScaleRatio;
			const markupCanvasDisplayHeight = markupCanvasHeight * markupPreviewScaleRatio;
			const markupImageSizeLabel = markupSourceImageSize ? `${markupCanvasWidth} x ${markupCanvasHeight}` : "";
			const allowMarkupCanvasPopout = isFutureEnabled(this.props.urlParams || null);
			const markupCanvasPopoutOpen = allowMarkupCanvasPopout && !!this.state.markupCanvasPopoutOpen;
			const markupWorkspaceStyle = {
				flex: "1 1 0",
				minHeight: 0,
				display: "grid",
				gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : markupDesktopGridColumns.join(" "),
				gap: "22px",
				minWidth: 0,
				overflowX: "hidden",
				overflowY: compactLayout ? "auto" : "hidden"
			};
			const markupCanvasWrapperStyle = {
				position: "relative",
				width: `${markupCanvasDisplayWidth}px`,
				height: `${markupCanvasDisplayHeight}px`,
				minWidth: `${markupCanvasDisplayWidth}px`,
				minHeight: `${markupCanvasDisplayHeight}px`
			};
			const markupPointerBadgeMaxLeft = Math.max(markupCanvasDisplayWidth - 112, 12);
			const markupPointerBadgeMaxTop = Math.max(markupCanvasDisplayHeight - 34, 12);
			const markupPointerBadgeStyle = {
				position: "absolute",
				left: `${clampValue((markupMouseX * markupPreviewScaleRatio) + 14, 12, markupPointerBadgeMaxLeft)}px`,
				top: `${clampValue((markupMouseY * markupPreviewScaleRatio) + 14, 12, markupPointerBadgeMaxTop)}px`,
				pointerEvents: "none",
				fontFamily: "Courier, monospace",
				fontSize: "11px",
				color: "#0f172a",
				background: "rgba(219, 234, 254, 0.96)",
				border: "1px solid rgba(96, 165, 250, 0.85)",
				borderRadius: "8px",
				padding: "4px 7px",
				boxShadow: "0 4px 10px rgba(15, 23, 42, 0.12)",
				whiteSpace: "nowrap"
			};
			const markupCanvasPopoutViewportStyle = {
				position: "relative",
				width: "100%",
				height: "100%",
				minHeight: "100%",
				display: "flex",
				flexDirection: "column",
				padding: "12px",
				boxSizing: "border-box",
				background: "#e2e8f0"
			};
			const markupCanvasPopoutHudStyle = {
				position: "absolute",
				top: "14px",
				right: "14px",
				display: "flex",
				alignItems: "center",
				gap: "8px",
				zIndex: 20,
				pointerEvents: "none"
			};
			const markupCanvasStatusBadgeStyle = {
				fontFamily: "Courier, monospace",
				fontSize: "13px",
				color: "#0f172a",
				background: "rgba(219, 234, 254, 0.96)",
				border: "1px solid rgba(96, 165, 250, 0.85)",
				borderRadius: "8px",
				padding: "8px 10px",
				minWidth: "110px",
				textAlign: "center",
				boxShadow: "0 4px 10px rgba(15, 23, 42, 0.12)"
			};
			const markupCanvasImageSizeBadgeStyle = {
				fontFamily: "Courier, monospace",
				fontSize: "11px",
				color: "#334155",
				background: "rgba(226, 232, 240, 0.96)",
				border: "1px solid rgba(148, 163, 184, 0.5)",
				borderRadius: "8px",
				padding: "5px 8px",
				minWidth: "96px",
				textAlign: "center",
				boxShadow: "0 4px 10px rgba(15, 23, 42, 0.12)"
			};
			const markupCanvasWorkspaceContent = (
				<div style={markupCanvasPaneStyle}>
					<div style={markupCanvasWrapperStyle}>
						<svg
							ref={this.markupSvgRef}
							viewBox={`0 0 ${markupCanvasWidth} ${markupCanvasHeight}`}
							width={markupCanvasDisplayWidth}
							height={markupCanvasDisplayHeight}
							style={{
								display: "block",
								background: "linear-gradient(45deg, #eef2f7 25%, transparent 25%, transparent 75%, #eef2f7 75%, #eef2f7), linear-gradient(45deg, #eef2f7 25%, transparent 25%, transparent 75%, #eef2f7 75%, #eef2f7)",
								backgroundSize: "24px 24px",
								backgroundPosition: "0 0, 12px 12px",
								boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.08)",
								cursor: "crosshair"
							}}
							onMouseDown={this.handleMarkupMouseDown}
							onMouseMove={this.handleMarkupMouseMove}
							onMouseUp={this.handleMarkupMouseUp}
							onMouseLeave={this.handleMarkupMouseLeave}
							onClick={this.handleMarkupCanvasClick}
						>
							<image href={markupSourceUrl} x="0" y="0" width={markupCanvasWidth} height={markupCanvasHeight} />
							{this.renderMarkupSpotlightLayerSvg(markupDraftItem ? [...markupItems, markupDraftItem] : markupItems, { idPrefix: "markup-spotlight-inline" })}
							{markupItems.map((item) => this.renderMarkupSvgItem(item))}
							{markupDraftItem && this.renderMarkupSvgItem(markupDraftItem, { isDraft: true })}
						</svg>
						{markupPointerInCanvas && (
							<div style={markupPointerBadgeStyle}>
								{markupMouseX}, {markupMouseY}
							</div>
						)}
					</div>
				</div>
			);
			const markupCanvasPopoutContent = (
				<div style={markupCanvasPopoutPaneStyle}>
					<div style={markupCanvasWrapperStyle}>
						<svg
							ref={this.markupSvgRef}
							viewBox={`0 0 ${markupCanvasWidth} ${markupCanvasHeight}`}
							width={markupCanvasDisplayWidth}
							height={markupCanvasDisplayHeight}
							style={{
								display: "block",
								background: "linear-gradient(45deg, #eef2f7 25%, transparent 25%, transparent 75%, #eef2f7 75%, #eef2f7), linear-gradient(45deg, #eef2f7 25%, transparent 25%, transparent 75%, #eef2f7 75%, #eef2f7)",
								backgroundSize: "24px 24px",
								backgroundPosition: "0 0, 12px 12px",
								boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.08)",
								cursor: "crosshair"
							}}
							onMouseDown={this.handleMarkupMouseDown}
							onMouseMove={this.handleMarkupMouseMove}
							onMouseUp={this.handleMarkupMouseUp}
							onMouseLeave={this.handleMarkupMouseLeave}
							onClick={this.handleMarkupCanvasClick}
						>
							<image href={markupSourceUrl} x="0" y="0" width={markupCanvasWidth} height={markupCanvasHeight} />
							{this.renderMarkupSpotlightLayerSvg(markupDraftItem ? [...markupItems, markupDraftItem] : markupItems, { idPrefix: "markup-spotlight-popout" })}
							{markupItems.map((item) => this.renderMarkupSvgItem(item))}
							{markupDraftItem && this.renderMarkupSvgItem(markupDraftItem, { isDraft: true })}
						</svg>
						{markupPointerInCanvas && (
							<div style={markupPointerBadgeStyle}>
								{markupMouseX}, {markupMouseY}
							</div>
						)}
					</div>
				</div>
			);
			const markupCanvasPopoutPlaceholderContent = (
				<div
					style={Object.assign({}, markupCanvasPaneStyle, {
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						textAlign: "center",
						padding: "24px"
					})}
				>
					<div style={{ maxWidth: "320px", display: "flex", flexDirection: "column", gap: "12px", alignItems: "center" }}>
						<div style={{ fontSize: "18px", fontWeight: 600, color: "#0f172a" }}>Canvas Popped Out</div>
						<div style={{ color: "#475569", fontSize: "14px", lineHeight: 1.5 }}>
							The live markup canvas is open in a separate window. Close that window or return it here to continue editing in this modal.
						</div>
						<Button bsStyle="primary" onClick={this.handleMarkupCanvasPopoutClosed}>
							Return Canvas
						</Button>
					</div>
				</div>
			);
			const inlineMarkupCanvasWorkspaceContent = markupCanvasPopoutOpen
				? markupCanvasPopoutPlaceholderContent
				: markupCanvasWorkspaceContent;
			const markupItemListPanelBody = (
				<>
					<div>
						<div style={{ color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Item List</div>
						<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5, marginTop: "8px" }}>
							Select markups here, hide or show them, and use list order to control stacking on the image.
						</div>
					</div>
					<div style={{ paddingTop: "14px", borderTop: "1px solid #e5e7eb", display: "flex", flexDirection: "column", gap: "8px" }}>
						<div style={{ color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Markup list</div>
						<div style={{ color: "#4b5563", fontSize: "12px", lineHeight: 1.5 }}>
							Top of this list renders in front on the canvas.
						</div>
						{markupItems.length === 0 && (
							<div style={{ color: "#9ca3af", fontSize: "12px", lineHeight: 1.5 }}>
								No markup yet.
							</div>
						)}
						{markupItems.length > 0 && (
							<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
								{markupLayerItems.map((item, layerIndex) => {
									const itemIndex = markupItems.length - 1 - layerIndex;
									const isFrontmost = itemIndex === markupItems.length - 1;
									const isBackmost = itemIndex === 0;
									const isSelected = item.guid === this.state.selectedMarkupGuid;
									const cardBorder = isSelected ? "1px solid #2563eb" : "1px solid #d1d5db";
									const cardBackground = isSelected ? "#eff6ff" : "#ffffff";
									return (
										<div
											key={item.guid}
											style={{
												display: "flex",
												flexDirection: "column",
												border: cardBorder,
												background: cardBackground,
												borderRadius: "10px",
												opacity: item.visible === false ? 0.55 : 1,
												overflow: "hidden"
											}}
										>
											<div
												role="button"
												tabIndex={0}
												onClick={() => this.handleSelectMarkupItem(item.guid)}
												onKeyDown={(event) => {
													if (event.key === "Enter" || event.key === " ") {
														event.preventDefault();
														this.handleSelectMarkupItem(item.guid);
													}
												}}
												style={{
													textAlign: "left",
													padding: "10px 12px",
													color: "#111827",
													cursor: "pointer"
												}}
											>
												<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
													<div style={{ fontWeight: 600, minWidth: 0 }}>{item.id}</div>
													<div style={{ fontSize: "11px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>
														{layerIndex === 0 ? "front" : (layerIndex === markupLayerItems.length - 1 ? "back" : `layer ${itemIndex + 1}`)}
													</div>
												</div>
												<div style={{ fontSize: "12px", color: "#6b7280" }}>
													{getMarkupItemDescriptor(item)} • {item.coords.width} x {item.coords.height}
													{isTextMarkupComponent(item.type || "highlight") ? ` • ${getMarkupTextPreview(item, 24)}` : ""}
													{isBlurMarkupComponent(item.type || "highlight") ? ` • ${clampValue(Number(item.style && item.style.blurPx) || 0, 0, MAX_BLUR_PX)}px blur` : ""}
													{item.visible === false ? " • hidden" : ""}
												</div>
											</div>
											<div style={markupCardFooterStyle(isSelected)}>
												<Button
													bsSize="xsmall"
													onClick={(event) => this.handleReorderMarkupItem(item.guid, "front", event)}
													title="Bring markup to front"
													style={markupLayerButtonStyle}
													disabled={isFrontmost}
												>
													Top
												</Button>
												<Button
													bsSize="xsmall"
													onClick={(event) => this.handleReorderMarkupItem(item.guid, "forward", event)}
													title="Move markup forward one layer"
													style={markupLayerButtonStyle}
													disabled={isFrontmost}
												>
													Up
												</Button>
												<Button
													bsSize="xsmall"
													onClick={(event) => this.handleReorderMarkupItem(item.guid, "backward", event)}
													title="Move markup backward one layer"
													style={markupLayerButtonStyle}
													disabled={isBackmost}
												>
													Down
												</Button>
												<Button
													bsSize="xsmall"
													onClick={(event) => this.handleReorderMarkupItem(item.guid, "back", event)}
													title="Send markup to back"
													style={markupLayerButtonStyle}
													disabled={isBackmost}
												>
													Back
												</Button>
												<Button
													bsSize="xsmall"
													onClick={(event) => this.handleToggleMarkupVisibility(item.guid, event)}
													title={item.visible === false ? "Show markup" : "Hide markup"}
													style={markupVisibilityButtonStyle}
												>
													<FontAwesomeIcon icon={item.visible === false ? "eye-slash" : "eye"} />
												</Button>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>
				</>
			);
			const markupPropertiesPanelBody = (
				<>
					<div style={toolSectionStyle}>
						<div style={{ color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Markup</div>
						<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
							{isTextMarkupComponent(markupComponentType)
								? "Drag on the image to place a text box, then adjust the copy, font, and background controls."
								: (isImagePatchMarkupComponent(markupComponentType)
									? "Image Patch draws a movable image-area object directly on the canvas."
									: `Drag on the image to draw a ${getMarkupItemTypeLabel(markupComponentType).toLowerCase()} ${getMarkupShapeLabel(markupShape).toLowerCase()}.`
								)
							}
							{" "}Use the zoom control for precision, then click a markup to select or delete it.
						</div>
						{imagePatchControlsVisible && (
							<div style={{ color: "#4b5563", fontSize: "12px", lineHeight: 1.5 }}>
								Image-area paste objects stay linked to the original pixels from this edit session, pasted external images stay embedded with the markup, and background-removed patches can be reset back to their original source.
							</div>
						)}
						<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
							{markupImageCopyMode
								? "Area copy mode is active. Drag on the image to copy raw image data into the clipboard."
								: (markupClipboardItem
									? `Clipboard ready: ${markupClipboardReadyLabel}${markupClipboardContainsImageData ? " (image pixels)" : ""}. Paste creates a movable copy.`
									: "Clipboard empty. Use the toolbar Area button to capture pixels, the toolbar Copy button to copy a selected markup item, or Cmd/Ctrl+V to paste an external image."
								)
							}
						</div>
						{markupColorPickTarget && (
							<div style={{ color: "#0f766e", fontSize: "13px", lineHeight: 1.5, fontWeight: 600 }}>
								Eyedropper active: click the image to sample {colorPickActiveLabel}.
							</div>
						)}
						{markupImagePatchBgPickMode && (
							<div style={{ color: "#0f766e", fontSize: "13px", lineHeight: 1.5, fontWeight: 600 }}>
								Eyedropper active: click the selected image patch to sample {imagePatchBgPickActiveLabel.toLowerCase()}.
							</div>
						)}
						{markupImagePatchProcessing && (
							<div style={{ color: "#1d4ed8", fontSize: "13px", lineHeight: 1.5, fontWeight: 600 }}>
								Processing image patch background removal...
							</div>
						)}
					</div>
					{textControlsVisible && (
						<div style={{ ...toolSectionStyle, paddingTop: "14px", borderTop: "1px solid #e5e7eb" }}>
							<div style={{ color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Text settings</div>
							<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>{textControlsSubtitle}</div>
							<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
								<label style={{ color: "#6b7280", fontSize: "12px", fontWeight: 600 }}>Text</label>
								<textarea
									className="form-control"
									rows={4}
									value={markupTextValue}
									onChange={this.handleMarkupTextValueChange}
									placeholder={MARKUP_TEXT_DEFAULT_VALUE}
								/>
							</div>
							<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
								<label style={{ color: "#6b7280", fontSize: "12px", fontWeight: 600 }}>Font Family</label>
								<>
									<input
										className="form-control"
										type="text"
										list="markup-text-font-family-options"
										value={markupFontFamily}
										onChange={this.handleMarkupFontFamilyChange}
										placeholder={MARKUP_TEXT_DEFAULT_FONT_FAMILY}
									/>
									<datalist id="markup-text-font-family-options">
										{markupFontFamilyOptions.map((option) => (
											<option key={`markup-text-font-family-${option}`} value={option} />
										))}
									</datalist>
								</>
							</div>
							<div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "10px 12px", border: "1px solid #dbe2ea", borderRadius: "10px", background: "#ffffff" }}>
								<div style={{ color: "#6b7280", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>More Fonts</div>
								<div style={{ color: "#4b5563", fontSize: "12px", lineHeight: 1.5 }}>
									Type an installed font family name directly, load a web-font stylesheet, upload a local font file, or scan local fonts when supported.
								</div>
								<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
									<input
										className="form-control"
										type="text"
										value={markupFontSourceUrl}
										onChange={this.handleMarkupFontSourceUrlChange}
										placeholder="https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap"
										style={{ flex: "1 1 260px", minWidth: "220px" }}
									/>
									<Button bsSize="small" onClick={this.handleLoadMarkupWebFont} disabled={markupFontLoading}>
										Load Web Font
									</Button>
								</div>
								<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
									<label className="btn btn-default btn-sm" style={{ margin: 0 }}>
										Upload Font File
										<input
											type="file"
											accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2"
											onChange={this.handleMarkupFontFileSelected}
											disabled={markupFontLoading}
											style={{ display: "none" }}
										/>
									</label>
									<Button bsSize="small" onClick={this.handleLoadMarkupLocalFonts} disabled={markupFontLoading || !localFontDiscoverySupported}>
										{localFontDiscoverySupported ? "Scan Local Fonts" : "Local Fonts Unsupported"}
									</Button>
									<div style={{ color: "#6b7280", fontSize: "12px" }}>
										{markupFontFamilyOptions.length} suggestions{markupCustomFontFamilies.length ? ` • ${markupCustomFontFamilies.length} custom/local` : ""}
									</div>
								</div>
								{markupFontLoadMessage && (
									<div style={{ color: "#166534", fontSize: "12px", lineHeight: 1.5 }}>
										{markupFontLoadMessage}
									</div>
								)}
								{markupFontLoadError && (
									<div style={{ color: "#b91c1c", fontSize: "12px", lineHeight: 1.5 }}>
										{markupFontLoadError}
									</div>
								)}
							</div>
							<div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px" }}>
								<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
									<label style={{ color: "#6b7280", fontSize: "12px", fontWeight: 600 }}>Font Size</label>
									<div style={{ display: "flex", gap: "8px" }}>
										<input
											className="form-control"
											type="number"
											min="1"
											max={`${MAX_MARKUP_TEXT_FONT_SIZE}`}
											value={markupFontSize}
											onChange={this.handleMarkupFontSizeChange}
										/>
										<select className="form-control" value={markupFontSize || getMarkupDefaults("text").fontSize} onChange={this.handleMarkupFontSizeChange}>
											{markupTextFontSizeOptions}
										</select>
									</div>
								</div>
								<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
									<label style={{ color: "#6b7280", fontSize: "12px", fontWeight: 600 }}>Text Color</label>
									<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
										<ColorPicker
											id="popover-inline-markup-text-color"
											title="Text Color"
											onChangeColor={this.handleMarkupTextColorChange}
											onClearColor={() => {}}
											presetColors={env.defaultColors}
											color={markupTextColor}
											placement="bottom"
											hideClear={true}
											disableAlpha={false}
											container={colorPopoverContainer}
											popoverStyle={colorPopoverStyle}
										/>
										<Button
											bsSize="small"
											bsStyle={markupColorPickTarget === MARKUP_COLOR_PICK_TARGETS.text ? "info" : "default"}
											title={markupColorPickTarget === MARKUP_COLOR_PICK_TARGETS.text ? "Cancel text color eyedropper" : "Pick text color from image"}
											onClick={() => this.handleMarkupColorPickToggle(MARKUP_COLOR_PICK_TARGETS.text)}
											style={{ height: "32px", padding: "4px 8px" }}
										>
											<FontAwesomeIcon icon="crosshairs" />
										</Button>
									</div>
								</div>
							</div>
							<div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "10px" }}>
								<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
									<label style={{ color: "#6b7280", fontSize: "12px", fontWeight: 600 }}>Weight</label>
									<select className="form-control" value={markupFontWeight} onChange={this.handleMarkupFontWeightChange}>
										{MARKUP_TEXT_FONT_WEIGHT_OPTIONS.map((option) => (
											<option key={`markup-text-font-weight-${option.value}`} value={option.value}>{option.label}</option>
										))}
									</select>
								</div>
								<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
									<label style={{ color: "#6b7280", fontSize: "12px", fontWeight: 600 }}>Style</label>
									<select className="form-control" value={markupFontStyle} onChange={this.handleMarkupFontStyleChange}>
										{MARKUP_TEXT_FONT_STYLE_OPTIONS.map((option) => (
											<option key={`markup-text-font-style-${option.value}`} value={option.value}>{option.label}</option>
										))}
									</select>
								</div>
								<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
									<label style={{ color: "#6b7280", fontSize: "12px", fontWeight: 600 }}>Align</label>
									<select className="form-control" value={markupTextAlign} onChange={this.handleMarkupTextAlignChange}>
										{MARKUP_TEXT_ALIGN_OPTIONS.map((option) => (
											<option key={`markup-text-align-${option.value}`} value={option.value}>{option.label}</option>
										))}
									</select>
								</div>
							</div>
							<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
								Use the toolbar Background controls for opaque, translucent, or fully transparent text box fills.
							</div>
						</div>
					)}
					<div style={{ ...toolSectionStyle, paddingTop: "14px", borderTop: "1px solid #e5e7eb" }}>
						<div style={{ color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Selected markup</div>
						{selectedMarkupItem ? (
							<>
								<div style={{ color: "#111827", fontWeight: 600 }}>{selectedMarkupItem.id}</div>
								<div style={{ color: "#6b7280", fontSize: "12px" }}>{getMarkupItemDescriptor(selectedMarkupItem)}</div>
								{selectedMarkupIsText && selectedTextStyle && (
									<>
										<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
											text: {getMarkupTextPreview(selectedMarkupItem, 72)}
										</div>
										<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
											font: {selectedTextStyle.fontFamily} • {selectedTextStyle.fontSize}px • {selectedTextStyle.fontWeight}{selectedTextStyle.fontStyle !== "normal" ? ` • ${selectedTextStyle.fontStyle}` : ""}
										</div>
									</>
								)}
								{selectedMarkupIsImagePatch && !selectedMarkupImagePatchStoredSourceRect && selectedMarkupImagePatchEmbeddedSourceSize && (
									<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
										pasted image: {selectedMarkupImagePatchEmbeddedSourceSize.width} x {selectedMarkupImagePatchEmbeddedSourceSize.height}
									</div>
								)}
								{selectedMarkupIsImagePatch && selectedMarkupImagePatchStoredSourceRect && (
									<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
										copied from: {selectedMarkupImagePatchStoredSourceRect.x}, {selectedMarkupImagePatchStoredSourceRect.y} • {selectedMarkupImagePatchStoredSourceRect.width} x {selectedMarkupImagePatchStoredSourceRect.height}
									</div>
								)}
								{selectedMarkupIsImagePatch && selectedMarkupImagePatchEmbeddedSourceUrl && selectedMarkupImagePatchStoredSourceRect && (
									<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
										embedded alpha patch ready for export
									</div>
								)}
								<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
									x: {selectedMarkupItem.coords.x}, y: {selectedMarkupItem.coords.y}, width: {selectedMarkupItem.coords.width}, height: {selectedMarkupItem.coords.height}
								</div>
								<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
									layer: {selectedMarkupLayerNumber} of {markupItems.length} {markupItems.length > 1 ? "(top renders in front)" : ""}
								</div>
								{selectedMarkupIsImagePatch && (
									<div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "12px", border: "1px solid #dbe2ea", borderRadius: "10px", background: "#ffffff" }}>
										<div style={{ color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Background Removal</div>
										<div style={{ color: "#4b5563", fontSize: "12px", lineHeight: 1.5 }}>
											Sample a background color from the selected patch, then remove matching pixels with tolerance and feathering.
										</div>
										<div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
											<div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
												<label style={{ color: "#6b7280", fontSize: "12px", fontWeight: 600 }}>Background Color</label>
												<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
													<ColorPicker
														id="popover-inline-markup-image-patch-bg-color"
														title="Image Patch Background Color"
														onChangeColor={this.handleMarkupImagePatchBgColorChange}
														onClearColor={() => {}}
														presetColors={env.defaultColors}
														color={markupImagePatchBgColor}
														placement="bottom"
														hideClear={true}
														disableAlpha={false}
														container={colorPopoverContainer}
														popoverStyle={colorPopoverStyle}
													/>
													<Button
														bsSize="small"
														bsStyle={markupImagePatchBgPickMode ? "info" : "default"}
														title={markupImagePatchBgPickMode ? "Cancel image patch background eyedropper" : "Sample background color from the selected image patch"}
														onClick={this.handleToggleMarkupImagePatchBgPickMode}
														disabled={markupImagePatchProcessing}
														style={{ height: "32px", padding: "4px 8px" }}
													>
														<FontAwesomeIcon icon="crosshairs" />
													</Button>
												</div>
											</div>
										</div>
										<div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px" }}>
											<div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
												<label style={{ color: "#6b7280", fontSize: "12px", fontWeight: 600 }}>Tolerance</label>
												<input
													className="form-control"
													type="number"
													min="0"
													max={`${MAX_IMAGE_PATCH_BG_TOLERANCE}`}
													value={markupImagePatchBgTolerance}
													onChange={this.handleMarkupImagePatchBgToleranceChange}
													disabled={markupImagePatchProcessing}
												/>
											</div>
											<div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
												<label style={{ color: "#6b7280", fontSize: "12px", fontWeight: 600 }}>Feather</label>
												<input
													className="form-control"
													type="number"
													min="0"
													max={`${MAX_IMAGE_PATCH_BG_FEATHER}`}
													value={markupImagePatchBgFeather}
													onChange={this.handleMarkupImagePatchBgFeatherChange}
													disabled={markupImagePatchProcessing}
												/>
											</div>
										</div>
										<div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
											<Button bsSize="small" bsStyle="primary" onClick={this.handleApplySelectedMarkupImagePatchBackgroundRemoval} disabled={markupImagePatchProcessing}>
												<FontAwesomeIcon icon="magic" /> Remove Background
											</Button>
											<Button bsSize="small" onClick={this.handleResetSelectedMarkupImagePatchBackgroundRemoval} disabled={!selectedMarkupImagePatchCanReset || markupImagePatchProcessing}>
												<FontAwesomeIcon icon="undo" /> Reset
											</Button>
										</div>
									</div>
								)}
								<div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px" }}>
									<div>
										<div style={{ color: "#6b7280", fontSize: "12px", fontWeight: 600 }}>X</div>
										<input
											className="form-control"
											type="number"
											value={selectedMarkupItem.coords.x}
											onChange={(event) => this.handleSelectedMarkupRectFieldChange("x", event)}
										/>
									</div>
									<div>
										<div style={{ color: "#6b7280", fontSize: "12px", fontWeight: 600 }}>Y</div>
										<input
											className="form-control"
											type="number"
											value={selectedMarkupItem.coords.y}
											onChange={(event) => this.handleSelectedMarkupRectFieldChange("y", event)}
										/>
									</div>
									<div>
										<div style={{ color: "#6b7280", fontSize: "12px", fontWeight: 600 }}>Width</div>
										<input
											className="form-control"
											type="number"
											min={MIN_MARKUP_ITEM_SIZE}
											value={selectedMarkupItem.coords.width}
											onChange={(event) => this.handleSelectedMarkupRectFieldChange("width", event)}
										/>
									</div>
									<div>
										<div style={{ color: "#6b7280", fontSize: "12px", fontWeight: 600 }}>Height</div>
										<input
											className="form-control"
											type="number"
											min={MIN_MARKUP_ITEM_SIZE}
											value={selectedMarkupItem.coords.height}
											onChange={(event) => this.handleSelectedMarkupRectFieldChange("height", event)}
										/>
									</div>
								</div>
								{isBlurMarkupComponent(selectedMarkupItem.type || "highlight") && (
									<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
										blur: {clampValue(Number(selectedMarkupItem.style && selectedMarkupItem.style.blurPx) || 0, 0, MAX_BLUR_PX)}px
									</div>
								)}
								{isSpotlightMarkupComponent(selectedMarkupItem.type || "highlight") && (
									<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
										dim: {Math.round(getMarkupSpotlightSettings(selectedMarkupItem).opacity * 100)}%, spotlight blur: {getMarkupSpotlightSettings(selectedMarkupItem).blur}px
									</div>
								)}
								<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
									rotation: {normalizeMarkupRotation(selectedMarkupItem.rotation || 0)}°
								</div>
								<div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
									<Button bsSize="small" title="Clone selected markup" onClick={this.handleCloneSelectedMarkup}>
										<FontAwesomeIcon icon="clone" />
									</Button>
									<Button
										bsSize="small"
										title="Move left"
										onClick={() => this.handleNudgeSelectedMarkup(-1, 0)}
										onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleNudgeSelectedMarkup(-step, 0))}
										onMouseLeave={this.stopMarkupPressAndHold}
									>
										<FontAwesomeIcon icon="arrow-left" />
									</Button>
									<Button
										bsSize="small"
										title="Move right"
										onClick={() => this.handleNudgeSelectedMarkup(1, 0)}
										onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleNudgeSelectedMarkup(step, 0))}
										onMouseLeave={this.stopMarkupPressAndHold}
									>
										<FontAwesomeIcon icon="arrow-right" />
									</Button>
									<Button
										bsSize="small"
										title="Move up"
										onClick={() => this.handleNudgeSelectedMarkup(0, -1)}
										onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleNudgeSelectedMarkup(0, -step))}
										onMouseLeave={this.stopMarkupPressAndHold}
									>
										<FontAwesomeIcon icon="arrow-up" />
									</Button>
									<Button
										bsSize="small"
										title="Move down"
										onClick={() => this.handleNudgeSelectedMarkup(0, 1)}
										onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleNudgeSelectedMarkup(0, step))}
										onMouseLeave={this.stopMarkupPressAndHold}
									>
										<FontAwesomeIcon icon="arrow-down" />
									</Button>
									<Button
										bsSize="small"
										title="Rotate left"
										onClick={() => this.handleRotateSelectedMarkup(-1)}
										onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleRotateSelectedMarkup(-step))}
										onMouseLeave={this.stopMarkupPressAndHold}
									>
										<FontAwesomeIcon icon="undo" />
									</Button>
									<Button
										bsSize="small"
										title="Rotate right"
										onClick={() => this.handleRotateSelectedMarkup(1)}
										onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleRotateSelectedMarkup(step))}
										onMouseLeave={this.stopMarkupPressAndHold}
									>
										<FontAwesomeIcon icon="redo" />
									</Button>
									<Button bsStyle="danger" bsSize="small" title="Delete selected markup" onClick={this.handleDeleteSelectedMarkupItem}>
										<FontAwesomeIcon icon="trash" />
									</Button>
								</div>
								<div style={markupSelectedFooterCardStyle}>
									<div style={{ padding: "10px 12px", color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
										Layer & Visibility
									</div>
									<div style={markupCardFooterStyle(true)}>
										<Button
											bsSize="xsmall"
											title="Bring selected markup to front"
											onClick={() => this.handleReorderSelectedMarkup("front")}
											disabled={!selectedMarkupCanMoveForward}
											style={markupLayerButtonStyle}
										>
											Top
										</Button>
										<Button
											bsSize="xsmall"
											title="Move selected markup forward one layer"
											onClick={() => this.handleReorderSelectedMarkup("forward")}
											disabled={!selectedMarkupCanMoveForward}
											style={markupLayerButtonStyle}
										>
											Up
										</Button>
										<Button
											bsSize="xsmall"
											title="Move selected markup backward one layer"
											onClick={() => this.handleReorderSelectedMarkup("backward")}
											disabled={!selectedMarkupCanMoveBackward}
											style={markupLayerButtonStyle}
										>
											Down
										</Button>
										<Button
											bsSize="xsmall"
											title="Send selected markup to back"
											onClick={() => this.handleReorderSelectedMarkup("back")}
											disabled={!selectedMarkupCanMoveBackward}
											style={markupLayerButtonStyle}
										>
											Back
										</Button>
										<Button
											bsSize="xsmall"
											title={selectedMarkupVisible ? "Hide selected markup" : "Show selected markup"}
											onClick={this.handleToggleSelectedMarkupVisibility}
											style={markupVisibilityButtonStyle}
										>
											<FontAwesomeIcon icon={selectedMarkupVisible ? "eye" : "eye-slash"} />
										</Button>
									</div>
								</div>
								<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
									<div style={{ color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Rotation</div>
									<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
										<Button
											bsSize="small"
											onClick={() => this.handleRotateSelectedMarkup(-1)}
											onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleRotateSelectedMarkup(-step))}
											onMouseLeave={this.stopMarkupPressAndHold}
											title="Rotate left"
										>
											-1°
										</Button>
										<input
											className="form-control"
											type="number"
											value={markupRotation}
											onChange={this.handleMarkupRotationChange}
											style={{ width: "88px" }}
										/>
										<Button
											bsSize="small"
											onClick={() => this.handleRotateSelectedMarkup(1)}
											onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleRotateSelectedMarkup(step))}
											onMouseLeave={this.stopMarkupPressAndHold}
											title="Rotate right"
										>
											+1°
										</Button>
									</div>
								</div>
								<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
									Drag the corner handles on the canvas to resize the selected markup. The top item in the markup list renders in front of lower items.
								</div>
							</>
						) : (
							<div style={{ color: "#6b7280", fontSize: "13px" }}>Click a markup on the image to inspect or delete it.</div>
						)}
					</div>
				</>
			);
			const markupItemListPanelContent = (
				<div style={markupItemListPanelStyle}>
					{markupItemListPanelBody}
				</div>
			);
			const markupPropertiesPanelContent = (
				<div style={markupPropertiesPanelStyle}>
					{markupPropertiesPanelBody}
				</div>
			);
			const markupStackedRightSplitPanelContent = (
				<div style={markupStackedRightPanelStyle}>
					<div style={markupStackedRightSectionStyle}>
						{markupItemListPanelBody}
					</div>
					<div style={markupStackedRightSectionStyle}>
						{markupPropertiesPanelBody}
					</div>
				</div>
			);
			const markupStackedRightAppendPanelContent = (
				<div style={markupPropertiesPanelStyle}>
					{markupItemListPanelBody}
					<div style={{ paddingTop: "14px", marginTop: "14px", borderTop: "1px solid #e5e7eb" }}>
						{markupPropertiesPanelBody}
					</div>
				</div>
			);

			return (
				<div style={overlayStyle} onMouseDown={saving ? undefined : onClose}>
					<div style={dialogStyle} onMouseDown={(event) => event.stopPropagation()}>
						<div style={headerStyle}>
							<div style={{ minWidth: 0, flex: 1 }}>
								<h3 style={{ margin: 0, textAlign: "center" }}>Edit Image</h3>
								<div style={{ marginTop: "10px", textAlign: "center", color: "#666666" }}>Markup</div>
							</div>
							<Button bsStyle="link" onClick={onClose} style={{ color: "#111827", padding: 0, fontSize: "28px", lineHeight: 1 }}>
								<FontAwesomeIcon icon="times" />
							</Button>
						</div>
						<div style={markupBodyStyle}>
							<ButtonToolbar style={{ flex: "0 0 auto", backgroundColor: "#dddddd", padding: "10px 12px", borderRadius: "12px", width: "100%", overflowX: "auto", overflowY: "hidden" }}>
								<div style={markupToolbarRowStyle}>
									<div style={markupToolbarSectionStyle}>
										<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
											<div style={{ fontSize: "x-small" }}>Select Component</div>
											<select onChange={this.handleMarkupComponentTypeChange} value={markupComponentType}>
												{MARKUP_COMPONENT_OPTIONS.map((option) => (
													<option key={`markup-component-${option.value}`} value={option.value}>{option.label}</option>
												))}
											</select>
										</div>
									</div>
									<div style={markupToolbarSectionDividerStyle} />
									<div style={markupToolbarButtonSectionStyle}>
										<Button bsStyle={markupImageCopyMode ? "info" : "default"} bsSize="small" onClick={markupImageCopyMode ? this.handleCancelMarkupImageCopyMode : this.handleStartMarkupImageCopyMode} title={markupImageCopyMode ? "Cancel image area copy mode" : "Copy raw image pixels from a dragged area"}>
											<FontAwesomeIcon icon={markupImageCopyMode ? "times" : "copy"} /> {markupImageCopyMode ? "Cancel" : "Area"}
										</Button>
										<Button bsSize="small" onClick={this.handleCopySelectedMarkup} disabled={!selectedMarkupItem} title="Copy the selected markup item, including image patches">
											<FontAwesomeIcon icon="copy" />
										</Button>
										<Button bsSize="small" onClick={this.handlePasteMarkupClipboard} disabled={!markupClipboardItem} title="Paste the current clipboard item onto the canvas">
											<FontAwesomeIcon icon="paste" />
										</Button>
									</div>
									<div style={markupToolbarSectionDividerStyle} />
									<div style={markupToolbarSectionStyle}>
										<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
											<div style={{ fontSize: "x-small" }}>Shape</div>
											<select onChange={this.handleMarkupShapeChange} value={markupShape}>
												{markupShapeOptions.map((option) => (
													<option key={`markup-shape-${option.value}`} value={option.value}>{option.label}</option>
												))}
											</select>
										</div>
										<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
											<div style={{ fontSize: "x-small" }}>Border Color</div>
											<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
												<ColorPicker
													id="popover-inline-markup-border-color"
													title="Markup Border Color"
													onChangeColor={this.handleMarkupBorderColorChange}
													onClearColor={() => {}}
													presetColors={env.defaultColors}
													color={markupBorderColor}
													placement="bottom"
													hideClear={true}
													disableAlpha={false}
													container={colorPopoverContainer}
													popoverStyle={colorPopoverStyle}
												/>
												<Button
													bsSize="small"
													bsStyle={markupColorPickTarget === MARKUP_COLOR_PICK_TARGETS.border ? "info" : "default"}
													title={markupColorPickTarget === MARKUP_COLOR_PICK_TARGETS.border ? "Cancel border color eyedropper" : "Pick border color from image"}
													onClick={() => this.handleMarkupColorPickToggle(MARKUP_COLOR_PICK_TARGETS.border)}
													style={{ height: "32px", padding: "4px 8px" }}
												>
													<FontAwesomeIcon icon="crosshairs" />
												</Button>
											</div>
										</div>
										<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
											<div style={{ fontSize: "x-small" }}>Border Width</div>
											<select onChange={this.handleMarkupBorderWidthChange} value={markupBorderWidth}>
												{markupBorderWidthOptions}
											</select>
										</div>
										<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
											<div style={{ fontSize: "x-small" }}>Border Radius</div>
											<select onChange={this.handleMarkupBorderRadiusChange} value={markupShapeSupportsBorderRadius(markupShape) ? markupBorderRadius : 0} disabled={!markupShapeSupportsBorderRadius(markupShape)}>
												{markupBorderRadiusOptions}
											</select>
										</div>
										{blurControlsVisible && (
											<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
												<div style={{ fontSize: "x-small" }}>Blur Intensity</div>
												<select onChange={this.handleMarkupBlurPxChange} value={markupBlurPx}>
													{markupBlurIntensityOptions}
												</select>
											</div>
										)}
										{spotlightControlsVisible && (
											<>
												<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
													<div style={{ fontSize: "x-small" }}>Background</div>
													<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
														<ColorPicker
															id="popover-inline-markup-spotlight-background-color"
															title="Spotlight Background"
															onChangeColor={this.handleMarkupSpotlightBackgroundColorChange}
															onClearColor={() => {}}
															presetColors={env.defaultColors}
															color={markupSpotlightBackgroundColor}
															placement="bottom"
															hideClear={true}
															disableAlpha={false}
															container={colorPopoverContainer}
															popoverStyle={colorPopoverStyle}
														/>
														<Button
															bsSize="small"
															bsStyle={markupColorPickTarget === MARKUP_COLOR_PICK_TARGETS.spotlightBackground ? "info" : "default"}
															title={markupColorPickTarget === MARKUP_COLOR_PICK_TARGETS.spotlightBackground ? "Cancel spotlight background eyedropper" : "Pick spotlight background color from image"}
															onClick={() => this.handleMarkupColorPickToggle(MARKUP_COLOR_PICK_TARGETS.spotlightBackground)}
															style={{ height: "32px", padding: "4px 8px" }}
														>
															<FontAwesomeIcon icon="crosshairs" />
														</Button>
													</div>
												</div>
												<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10, minWidth: "130px" }}>
													<div style={{ fontSize: "x-small" }}>Dim</div>
													<input
														type="range"
														min="0"
														max="100"
														step="1"
														value={Math.round(normalizeMarkupSpotlightOpacity(markupSpotlightOpacity) * 100)}
														onChange={this.handleMarkupSpotlightOpacityChange}
														style={{ width: "120px" }}
													/>
													<div style={{ fontSize: "11px", color: "#4b5563", lineHeight: 1 }}>{Math.round(normalizeMarkupSpotlightOpacity(markupSpotlightOpacity) * 100)}%</div>
												</div>
												<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
													<div style={{ fontSize: "x-small" }}>Spotlight Blur</div>
													<select onChange={this.handleMarkupSpotlightBlurPxChange} value={normalizeMarkupSpotlightBlur(markupSpotlightBlurPx)}>
														{markupSpotlightBlurOptions}
													</select>
												</div>
											</>
										)}
										{fillControlsVisible && (
											<>
												<label style={{ display: "flex", alignItems: "center", gap: "8px", margin: "18px 6px 0 0", fontWeight: 500 }}>
													<input type="checkbox" checked={markupFillEnabled} onChange={this.handleMarkupFillEnabledChange} />
													<span>{fillToggleLabel}</span>
												</label>
												<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
													<div style={{ fontSize: "x-small" }}>{fillColorLabel}</div>
													<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
														<ColorPicker
															id="popover-inline-markup-fill-color"
															title={fillColorLabel}
															onChangeColor={this.handleMarkupFillColorChange}
															onClearColor={() => {}}
															presetColors={env.defaultColors}
															color={markupFillEnabled ? markupFillColor : "transparent"}
															placement="bottom"
															hideClear={true}
															disableAlpha={false}
															container={colorPopoverContainer}
															popoverStyle={colorPopoverStyle}
														/>
														<Button
															bsSize="small"
															bsStyle={markupColorPickTarget === MARKUP_COLOR_PICK_TARGETS.fill ? "info" : "default"}
															title={markupColorPickTarget === MARKUP_COLOR_PICK_TARGETS.fill ? `Cancel ${fillColorLabel.toLowerCase()} eyedropper` : `Pick ${fillColorLabel.toLowerCase()} from image`}
															onClick={() => this.handleMarkupColorPickToggle(MARKUP_COLOR_PICK_TARGETS.fill)}
															style={{ height: "32px", padding: "4px 8px" }}
														>
															<FontAwesomeIcon icon="crosshairs" />
														</Button>
													</div>
												</div>
											</>
										)}
									</div>
									<div style={markupToolbarSectionDividerStyle} />
									<div style={markupToolbarButtonSectionStyle}>
										<Button style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center" }} bsStyle="primary" onClick={this.handleMarkupToggleLabels} title={markupShowItemIds ? "Hide item labels on canvas" : "Show item labels on canvas"}>
											<FontAwesomeIcon style={{ fontSize: "12px" }} icon={markupShowItemIds ? "align-slash" : "align-justify"} />
											<div style={{ fontSize: "12px", paddingLeft: "5px" }}>{markupShowItemIds ? "Hide Labels" : "Show Labels"}</div>
										</Button>
									</div>
									<div style={markupToolbarSectionDividerStyle} />
									<div style={markupToolbarSectionStyle}>
										<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center" }}>
											<div style={{ fontSize: "x-small" }}>Zoom</div>
											<select style={{ borderRadius: "5px", border: "1px solid #cccccc", marginBottom: 0 }} value={markupPreviewScale} onChange={this.handleMarkupPreviewScaleChange}>
												{MARKUP_ZOOM_OPTIONS.map((option) => (
													<option key={`markup-zoom-${option}`} value={option}>{option}%</option>
												))}
											</select>
										</div>
										<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center" }}>
											<div style={{ fontSize: "x-small" }}>Layout</div>
											<select style={{ borderRadius: "5px", border: "1px solid #cccccc", marginBottom: 0, minWidth: "124px" }} value={resolvedMarkupLayoutMode} onChange={this.handleMarkupLayoutModeChange}>
												<option value={MARKUP_LAYOUT_MODE_SPLIT}>Split</option>
												<option value={MARKUP_LAYOUT_MODE_STACKED_RIGHT_SPLIT}>Right Split</option>
												<option value={MARKUP_LAYOUT_MODE_STACKED_RIGHT_APPEND}>Right Append</option>
											</select>
										</div>
									</div>
									<div style={markupToolbarSectionDividerStyle} />
									<div style={markupToolbarButtonSectionStyle}>
										{(compactLayout || resolvedMarkupLayoutMode === MARKUP_LAYOUT_MODE_SPLIT) && (
											<Button bsStyle={showMarkupLeftPanel ? "primary" : "default"} bsSize="small" onClick={this.handleToggleMarkupLeftPanelCollapsed} title={showMarkupLeftPanel ? "Hide item list" : "Show item list"}>
												List
											</Button>
										)}
										<Button bsStyle={showMarkupRightPanel ? "primary" : "default"} bsSize="small" onClick={this.handleToggleMarkupRightPanelCollapsed} title={showMarkupRightPanel ? (markupIsStackedRightLayout ? "Hide inspector panels" : "Hide inspector") : (markupIsStackedRightLayout ? "Show inspector panels" : "Show inspector")}>
											Inspector
										</Button>
										{allowMarkupCanvasPopout && (
											<Button bsStyle={markupCanvasPopoutOpen ? "info" : "default"} bsSize="small" onClick={this.handleToggleMarkupCanvasPopout} title={markupCanvasPopoutOpen ? "Return the markup canvas to this window" : "Open the markup canvas in a separate window"}>
												{markupCanvasPopoutOpen ? "Return Canvas" : "Canvas"}
											</Button>
										)}
									</div>
									<div style={markupToolbarSectionDividerStyle} />
									<div style={markupToolbarCoordsSectionStyle}>
										{markupImageCopyMode && (
											<div style={{ fontSize: "11px", color: "#075985", background: "#e0f2fe", borderRadius: "8px", padding: "5px 8px", minWidth: "116px", textAlign: "center", fontWeight: 600 }}>
												Drag to Copy
											</div>
										)}
										{markupImageSizeLabel && (
											<div style={{ fontFamily: "Courier", fontSize: "11px", color: "#334155", background: "#e2e8f0", borderRadius: "8px", padding: "5px 8px", minWidth: "96px", textAlign: "center" }}>
												{markupImageSizeLabel}
											</div>
										)}
										<div style={{ fontFamily: "Courier", fontSize: "13px", color: "#0f172a", background: "#dbeafe", borderRadius: "8px", padding: "8px 10px", minWidth: "110px", textAlign: "center" }}>
											{markupMouseX}, {markupMouseY}
										</div>
									</div>
								</div>
							</ButtonToolbar>
							<div style={markupWorkspaceStyle}>
								{compactLayout
									? (
										<>
											{inlineMarkupCanvasWorkspaceContent}
											{showMarkupLeftPanel && markupItemListPanelContent}
											{showMarkupRightPanel && markupPropertiesPanelContent}
										</>
									)
									: (
										<>
											{resolvedMarkupLayoutMode === MARKUP_LAYOUT_MODE_SPLIT && showMarkupLeftPanel && markupItemListPanelContent}
											{inlineMarkupCanvasWorkspaceContent}
											{showMarkupRightPanel && (
												resolvedMarkupLayoutMode === MARKUP_LAYOUT_MODE_SPLIT
													? markupPropertiesPanelContent
													: (resolvedMarkupLayoutMode === MARKUP_LAYOUT_MODE_STACKED_RIGHT_SPLIT
														? markupStackedRightSplitPanelContent
														: markupStackedRightAppendPanelContent)
											)}
										</>
									)}
							</div>
						</div>
						<div style={footerStyle}>
							<Button onClick={this.handleCloseMarkupModal}>
								<FontAwesomeIcon icon="arrow-left" /> Back to Edit
							</Button>
							<div style={{ display: "flex", gap: "10px", alignItems: "center", justifyContent: "flex-end", marginLeft: "auto", flexWrap: "wrap" }}>
								<div style={{ color: "#6b7280", fontSize: "13px", alignSelf: "center" }}>Markup is flattened into the image when you apply it.</div>
								<Button bsStyle="primary" onClick={this.handleApplyInlineMarkup} title="Apply markup to the image">
									<FontAwesomeIcon icon="save" /> Apply
								</Button>
							</div>
						</div>
					</div>
					<PopoutWindowPortal
						open={!!markupCanvasPopoutOpen}
						title="Image Markup Canvas"
						name="image-editor-markup-canvas-popout"
						onClose={this.handleMarkupCanvasPopoutClosed}
						backgroundColor="#e2e8f0"
						maximize
					>
						<div style={markupCanvasPopoutViewportStyle}>
							<div style={markupCanvasPopoutHudStyle}>
								{markupImageCopyMode && (
									<div style={{ fontSize: "11px", color: "#075985", background: "#e0f2fe", borderRadius: "8px", padding: "5px 8px", minWidth: "116px", textAlign: "center", fontWeight: 600, boxShadow: "0 4px 10px rgba(15, 23, 42, 0.12)" }}>
										Drag to Copy
									</div>
								)}
								{markupImageSizeLabel && (
									<div style={markupCanvasImageSizeBadgeStyle}>
										{markupImageSizeLabel}
									</div>
								)}
								<div style={markupCanvasStatusBadgeStyle}>
									{markupMouseX}, {markupMouseY}
								</div>
							</div>
							{markupCanvasPopoutContent}
						</div>
					</PopoutWindowPortal>
				</div>
			);
		}

		return (
			<div style={overlayStyle} onMouseDown={saving ? undefined : onClose}>
				<div style={dialogStyle} onMouseDown={(event) => event.stopPropagation()}>
					<div style={headerStyle}>
						<div style={{ minWidth: 0, flex: 1 }}>
							<h3 style={{ margin: 0, textAlign: "center" }}>Edit Image</h3>
							{imageLabel && <div style={{ marginTop: "10px", textAlign: "center", color: "#666666" }}>{imageLabel}</div>}
						</div>
						<Button bsStyle="link" onClick={onClose} disabled={saving} style={{ color: "#111827", padding: 0, fontSize: "28px", lineHeight: 1 }}>
							<FontAwesomeIcon icon="times" />
						</Button>
					</div>
					<div style={bodyStyle}>
						<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 1fr) 180px", gap: "12px", alignItems: "end" }}>
							<div style={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: 0 }}>
								<label style={{ color: "gray" }}>Image name</label>
								<input className="form-control" type="text" value={imageName} onChange={this.handleImageNameChange} placeholder={`image-edited.${getImageOutputExtension(outputFormat)}`} disabled={overwriteImage} />
							</div>
							<div style={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: 0 }}>
								<label style={{ color: "gray" }}>Save as</label>
								<select className="form-control" value={outputFormat} onChange={this.handleOutputFormatChange} disabled={overwriteImage}>
									{IMAGE_OUTPUT_FORMAT_OPTIONS.map((option) => (
										<option key={option.value} value={option.value}>{option.label}</option>
									))}
								</select>
							</div>
						</div>
						{overwriteImage && (
							<div style={{ color: "#666666", fontSize: "12px", lineHeight: 1.4, marginTop: "-6px" }}>
								Overwrite keeps the current file path and format of the existing image.
							</div>
						)}
						<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px 24px" }}>
							<label style={checkboxLabelStyle}>
								<input type="checkbox" checked={autoLinkImage} onChange={this.handleToggleAutoLinkImage} />
								<span>{autoLinkLabel || "Auto-link Image"}</span>
								<span title="After saving, update the item's image property to the saved file." style={checkboxHelpStyle}>
									<FontAwesomeIcon icon="info-circle" />
								</span>
							</label>
							{!!overwriteTargetPath && (
								<label style={checkboxLabelStyle}>
									<input type="checkbox" checked={overwriteImage} onChange={this.handleToggleOverwriteImage} />
									<span>Overwrite Image</span>
									<span title="Save back to the exact Drive image you are editing instead of creating a new file." style={checkboxHelpStyle}>
										<FontAwesomeIcon icon="info-circle" />
									</span>
								</label>
							)}
						</div>
						{!previewImageLoading && previewImageUrl && !previewExportSafe && (
							<div style={{ color: "#92400e", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: "10px", padding: "10px 12px" }}>
								This image is available for preview, but the browser has not loaded a canvas-safe source yet, so save is temporarily disabled.
							</div>
						)}
						<div style={editorGridStyle}>
							<div style={{ display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 }}>
								<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
									<div style={{ color: "gray" }}>Image preview</div>
									<Button bsSize="small" onClick={this.handleOpenMarkupModal} disabled={!sourceReady || previewImageLoading || !previewExportSafe}>
										<FontAwesomeIcon icon="pen" /> Markup
									</Button>
								</div>
								<div
									ref={this.cropViewportRef}
									style={{
										position: "relative",
										border: "1px solid #dddddd",
										borderRadius: "16px",
										height: `${previewPaneHeight}px`,
										maxHeight: `${previewPaneHeight}px`,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										background: "#f8fafc",
										overflow: "hidden",
										padding: compactLayout ? "18px" : "28px"
									}}
								>
									{previewImageUrl && (
										<img
											ref={this.imageRef}
											alt="Editable preview"
											src={previewImageUrl}
											onLoad={this.handleImageLoaded}
											onError={this.handleImageError}
											style={{ display: "none" }}
										/>
									)}
									{sourceReady && previewFrameMetrics ? (
										<div
											onMouseDown={this.handleCropDragStart}
											style={{
												width: `${previewFrameMetrics.outerDisplayWidth}px`,
												height: `${previewFrameMetrics.outerDisplayHeight}px`,
												maxWidth: "100%",
												maxHeight: "100%",
												position: "relative",
												overflow: "hidden",
												borderRadius: "12px",
												background: "linear-gradient(45deg, #eef2f7 25%, transparent 25%, transparent 75%, #eef2f7 75%, #eef2f7), linear-gradient(45deg, #eef2f7 25%, transparent 25%, transparent 75%, #eef2f7 75%, #eef2f7)",
												backgroundSize: "24px 24px",
												backgroundPosition: "0 0, 12px 12px",
												boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.08)",
												cursor: isDraggingCrop ? "grabbing" : "grab"
											}}
										>
											{blurFillBackground && previewBlurFillMetrics && (
												<div
													style={{
														position: "absolute",
														left: `${previewBlurFillMetrics.x}px`,
														top: `${previewBlurFillMetrics.y}px`,
														width: `${previewBlurFillMetrics.width}px`,
														height: `${previewBlurFillMetrics.height}px`,
														overflow: "hidden",
														pointerEvents: "none",
														userSelect: "none"
													}}
												>
													<img
														alt=""
														src={previewImageUrl}
														draggable={false}
														onDragStart={(event) => event.preventDefault()}
														style={{
															position: "absolute",
															left: `${previewBlurFillMetrics.imageLeft}px`,
															top: `${previewBlurFillMetrics.imageTop}px`,
															width: `${previewBlurFillMetrics.imageWidth}px`,
															height: `${previewBlurFillMetrics.imageHeight}px`,
															maxWidth: "none",
															filter: `blur(${blurFillPx}px) brightness(${DEFAULT_BLUR_FILL_BRIGHTNESS})`,
															userSelect: "none",
															pointerEvents: "none"
														}}
													/>
												</div>
											)}
											<div
												style={{
													position: "absolute",
													left: `${previewFrameMetrics.contentLeft}px`,
													top: `${previewFrameMetrics.contentTop}px`,
													width: `${previewFrameMetrics.contentDisplayWidth}px`,
													height: `${previewFrameMetrics.contentDisplayHeight}px`,
													overflow: "hidden",
													borderRadius: "10px",
													boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.06)"
												}}
											>
												<img
													alt="Preview crop"
													src={previewImageUrl}
													draggable={false}
													onDragStart={(event) => event.preventDefault()}
													style={{
														position: "absolute",
														left: `${previewFrameMetrics.imageLeft}px`,
														top: `${previewFrameMetrics.imageTop}px`,
														width: `${previewFrameMetrics.imageWidth}px`,
														height: `${previewFrameMetrics.imageHeight}px`,
														maxWidth: "none",
														userSelect: "none",
														pointerEvents: "none",
														filter: blurPx > 0 ? `blur(${blurPx}px)` : "none"
													}}
												/>
											</div>
										</div>
									) : (
										<div style={{ padding: "20px", color: "#777777", textAlign: "center" }}>
											{previewImageLoading || sourceReady ? "Preparing preview..." : "Select a valid image to edit."}
										</div>
									)}
									{previewImageLoading && (
										<div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255, 255, 255, 0.55)", color: "#374151", fontWeight: 600 }}>
											<FontAwesomeIcon icon="spinner" spin style={{ marginRight: "8px" }} /> Loading image...
										</div>
									)}
								</div>
								<div style={{ color: "#666666", fontSize: "12px", lineHeight: 1.4 }}>
									Drag inside the preview to reframe the crop. Blur, resize, and padding are previewed live.
								</div>
							</div>
							<div style={toolRailStyle}>
								<div style={toolSectionStyle}>
									<div style={{ color: "#666666", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
										Crop preset
									</div>
									<div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
										{CROP_PRESET_OPTIONS.map((option) => (
											<Button
												key={option.value}
												bsSize="small"
												bsStyle={cropPreset === option.value ? "primary" : "default"}
												active={cropPreset === option.value}
												onClick={() => this.handleChangeCropPreset(option.value)}
												disabled={!sourceReady}
											>
												{option.label}
											</Button>
										))}
									</div>
									<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
										<div style={{ minWidth: "68px", color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
											Zoom
										</div>
										<input
											type="range"
											min={DEFAULT_CROP_ZOOM}
											max={MAX_CROP_ZOOM}
											step={0.05}
											value={cropZoom}
											onInput={this.handleCropZoomChange}
											onChange={this.handleCropZoomChange}
											disabled={!sourceReady}
											style={{ flex: 1 }}
										/>
										<div style={{ minWidth: "44px", textAlign: "right", color: "#666666" }}>
											{cropZoom.toFixed(2)}x
										</div>
									</div>
									{cropPreset === FREE_CROP_PRESET && (
										<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: "10px" }}>
											<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
												<div style={{ minWidth: "68px", color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
													Width
												</div>
												<input
													type="range"
													min={MIN_FREE_CROP_SCALE}
													max={1}
													step={0.01}
													value={freeCropWidthPct}
													onInput={this.handleFreeCropWidthChange}
													onChange={this.handleFreeCropWidthChange}
													disabled={!sourceReady}
													style={{ flex: 1 }}
												/>
											</div>
											<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
												<div style={{ minWidth: "68px", color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
													Height
												</div>
												<input
													type="range"
													min={MIN_FREE_CROP_SCALE}
													max={1}
													step={0.01}
													value={freeCropHeightPct}
													onInput={this.handleFreeCropHeightChange}
													onChange={this.handleFreeCropHeightChange}
													disabled={!sourceReady}
													style={{ flex: 1 }}
												/>
											</div>
										</div>
									)}
									<div style={{ display: "flex", justifyContent: "flex-end" }}>
										<Button bsSize="small" onClick={this.handleResetCrop} disabled={!sourceReady}>
											Reset Crop
										</Button>
									</div>
								</div>
								<div style={{ ...toolSectionStyle, paddingTop: "14px", borderTop: "1px solid #e5e7eb" }}>
									<div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
										<label style={{ display: "flex", alignItems: "center", gap: "10px", margin: 0, fontWeight: 600 }}>
											<input type="checkbox" checked={resizeEnabled} onChange={this.handleToggleResizeEnabled} />
											<span>Resize output</span>
										</label>
										<label style={{ display: "flex", alignItems: "center", gap: "8px", margin: 0, color: "#666666" }}>
											<input type="checkbox" checked={resizeLockAspect} onChange={this.handleToggleResizeLock} disabled={!resizeEnabled} />
											<span>Lock ratio</span>
										</label>
									</div>
									<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: "10px" }}>
										<div>
											<label style={{ color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Width</label>
											<input className="form-control" type="number" min="1" value={resizeWidth} disabled={!resizeEnabled} onChange={this.handleResizeWidthChange} />
										</div>
										<div>
											<label style={{ color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Height</label>
											<input className="form-control" type="number" min="1" value={resizeHeight} disabled={!resizeEnabled} onChange={this.handleResizeHeightChange} />
										</div>
									</div>
									<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
										<div style={{ minWidth: "88px", color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
											Blur
										</div>
										<input
											type="range"
											min={0}
											max={MAX_BLUR_PX}
											step={0.5}
											value={blurPx}
											onInput={this.handleBlurChange}
											onChange={this.handleBlurChange}
											disabled={!sourceReady}
											style={{ flex: 1 }}
										/>
										<div style={{ minWidth: "48px", textAlign: "right", color: "#666666" }}>
											{blurPx.toFixed(1)}px
										</div>
									</div>
									<div style={{ ...toolSectionStyle, gap: "8px", padding: "12px", border: "1px solid #dbeafe", borderRadius: "10px", background: "#eff6ff" }}>
										<label style={{ display: "flex", alignItems: "center", gap: "10px", margin: 0, fontWeight: 600 }}>
											<input type="checkbox" checked={blurFillBackground} onChange={this.handleBlurFillBackgroundChange} disabled={!sourceReady} />
											<span>Blur-fill background <span style={{ color: "#2563eb" }}>(Experimental)</span></span>
										</label>
										<div style={{ color: "#475569", fontSize: "12px", lineHeight: 1.5 }}>
											Best for photos. This bakes a blurred cover copy behind the image to fill transparent padding or a final-size frame.
										</div>
										<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
											<div style={{ minWidth: "88px", color: "#475569", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
												Fill blur
											</div>
											<input
												type="range"
												min={0}
												max={MAX_BLUR_PX}
												step={0.5}
												value={blurFillPx}
												onInput={this.handleBlurFillPxChange}
												onChange={this.handleBlurFillPxChange}
												disabled={!sourceReady || !blurFillBackground}
												style={{ flex: 1 }}
											/>
											<div style={{ minWidth: "48px", textAlign: "right", color: "#475569" }}>
												{blurFillPx.toFixed(1)}px
											</div>
										</div>
									</div>
									<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "160px minmax(0, 1fr)", gap: "10px", alignItems: "end" }}>
										<div>
											<label style={{ color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Padding mode</label>
											<select className="form-control" value={paddingModeValue} disabled={!sourceReady || padToTargetSize} onChange={this.handlePaddingModeChange}>
												<option value="uniform">Uniform</option>
												<option value="custom">Custom sides</option>
											</select>
										</div>
										<div style={{ color: "#666666", fontSize: "12px", lineHeight: 1.5 }}>
											Padding adds transparent frame space around the image. Enable blur-fill to bake photo color into that frame.
										</div>
									</div>
									{paddingModeValue === "uniform" ? (
										<div>
											<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
												<div style={{ minWidth: "88px", color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
													Padding
												</div>
												<input
													type="range"
													min={0}
													max={Math.max(MAX_PADDING_SLIDER_PX, paddingPx)}
													step={1}
													value={paddingPx}
													onInput={this.handlePaddingChange}
													onChange={this.handlePaddingChange}
													disabled={!sourceReady || padToTargetSize}
													style={{ flex: 1 }}
												/>
											</div>
											<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 1fr) 124px", gap: "10px", alignItems: "end", marginTop: "8px" }}>
												<div style={{ color: "#666666", fontSize: "12px", lineHeight: 1.5 }}>
													Use the slider for quick adjustment, or type a larger padding value directly.
												</div>
												<div>
													<label style={{ color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Padding px</label>
													<input
														className="form-control"
														type="number"
														min="0"
														max={MAX_PADDING_INPUT_PX}
														value={paddingPx}
														disabled={!sourceReady || padToTargetSize}
														onChange={this.handlePaddingChange}
													/>
												</div>
											</div>
										</div>
									) : (
										<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr) minmax(0, 1fr)" : "repeat(4, minmax(0, 1fr))", gap: "10px" }}>
											{[
												{ key: "Top", value: paddingTopPx },
												{ key: "Right", value: paddingRightPx },
												{ key: "Bottom", value: paddingBottomPx },
												{ key: "Left", value: paddingLeftPx }
											].map((side) => (
												<div key={side.key}>
													<label style={{ color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{side.key}</label>
													<input
														className="form-control"
														type="number"
														min="0"
														max={MAX_PADDING_INPUT_PX}
														value={side.value}
														disabled={!sourceReady || padToTargetSize}
														onChange={(event) => this.handleSidePaddingChange(side.key, event)}
													/>
												</div>
											))}
										</div>
									)}
									<div style={{ ...toolSectionStyle, paddingTop: "14px", borderTop: "1px solid #e5e7eb" }}>
										<label style={{ display: "flex", alignItems: "center", gap: "10px", margin: 0, fontWeight: 600 }}>
											<input type="checkbox" checked={padToTargetSize} onChange={this.handleTogglePadToTargetSize} />
											<span>Pad to final size</span>
										</label>
										<div style={{ color: "#666666", fontSize: "12px", lineHeight: 1.5 }}>
											Enter an exact final size or choose an aspect ratio. The current image is centered inside that frame.
										</div>
										<div>
											<label style={{ color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Frame aspect</label>
											<select className="form-control" value={padAspectRatioValue} disabled={!padToTargetSize} onChange={this.handlePadAspectRatioChange}>
												{FINAL_FRAME_ASPECT_RATIO_OPTIONS.map((option) => (
													<option key={option.value} value={option.value}>{option.label}</option>
												))}
											</select>
										</div>
										<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: "10px" }}>
											<div>
												<label style={{ color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Final width</label>
												<input className="form-control" type="number" min="1" max={MAX_FINAL_OUTPUT_DIMENSION_PX} value={displayedPadTargetWidth} disabled={!padToTargetSize || !padAspectRatioIsCustom} onChange={this.handlePadTargetWidthChange} />
											</div>
											<div>
												<label style={{ color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Final height</label>
												<input className="form-control" type="number" min="1" max={MAX_FINAL_OUTPUT_DIMENSION_PX} value={displayedPadTargetHeight} disabled={!padToTargetSize || !padAspectRatioIsCustom} onChange={this.handlePadTargetHeightChange} />
											</div>
										</div>
									</div>
									<div style={{ color: "#666666", fontSize: "12px", lineHeight: 1.5 }}>
										Output size: <b>{outputFrameWidth || 0} x {outputFrameHeight || 0}</b>
										{outputFrameDescription}
										{blurFillBackground ? " with experimental photo blur-fill baked into the background" : ""}
									</div>
								</div>
							</div>
						</div>
						{error && (
							<div style={{ color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", padding: "12px 14px" }}>
								{error}
							</div>
						)}
					</div>
					<div style={footerStyle}>
						<Button onClick={onClose} disabled={saving}>
							<FontAwesomeIcon icon="ban" /> Cancel
						</Button>
						<Button bsStyle="primary" onClick={this.handleSaveImage} disabled={!sourceReady || saving || previewImageLoading || !previewExportSafe}>
							<FontAwesomeIcon icon={saving ? "spinner" : "save"} spin={saving} /> {saving ? "Saving..." : "Save Image"}
						</Button>
					</div>
					{saving && (
						<div style={savingOverlayStyle}>
							<div style={savingPanelStyle}>
								<FontAwesomeIcon icon="spinner" spin />
								<span>Saving image...</span>
							</div>
						</div>
					)}
					<canvas ref={this.canvasRef} style={{ display: "none" }} />
				</div>
			</div>
		);
	}
}
